import { useState, useMemo, useRef, useEffect, useCallback, memo, forwardRef, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import theme from "../data/theme";
import { TEAM_META, TEAM_HISTORY as PLAY_HISTORY } from "../data/projectionData";
import { EditorHeader, StepIndicator } from "./EditorChrome";
import { COACHING_HISTORY } from "../data/coachingHistory";
import { PLAYER_HISTORY } from "../data/playerHistory";
import { computeTeamDepthShares, computeLeagueDepthShares } from "../utils/depthHistory";
import { ResultsView } from "./ProjectionResults";
import { getStarters, writeSlotOverride, getPlayerEdit, setPlayerEdit, loadPlayerEdits, clearAllPlayerEdits } from "../utils/depthOverrides";
import { safeReadProjlab, PROJLAB_SCHEMA_VERSION } from "../utils/projlabStorage";
import {
  SLOTS, resolvedStintsFor, resolveStintIdentity, slotDisplayName,
  buildPlayerHistForSlot, findPlayerHist,
  allocTotals, isAllocOk,
  buildInitAllocs, buildInitRates, buildInitVolume, calcPPR,
  computeVolume, computeDerivedPass, projectTeamPlayers,
  FULL_PPR_LEGACY, scoreFromStats, pprLabel, GP_ROLE_MEANS,
} from "../utils/projlabProjection";
import { computeCompetitionHints } from "../utils/competitionHints";
import { computeInjuryHints } from "../utils/injuryHints";
import { computeSeedQualityHints } from "../utils/seedQualityHints";
import { resolvePoolEntry, TEAM_PPR_2025 } from "./ProjectionResults";
import { captureProjlabTeamCompleted } from "../utils/projlabAnalytics";
import { usePlayerCard } from "../context/PlayerCardContext";
import { computeGroupEnvelopeFlags, computeStatLineFlags } from "../utils/realismGuards";
import { buildHouseAllocs, buildHouseRates, houseSeedFlags } from "../utils/houseSeed";

const { colors, fonts, fontSize, fontWeight } = theme;

const COACH_PALETTE = [
  "#22d3ee", "#a78bfa", "#fbbf24", "#f472b6", "#34d399", "#fb923c", "#9ca3af",
];

const LEAGUE_YEARS = [2021, 2022, 2023, 2024, 2025];

const PRIMARY_STAT = { QB: "rush", RB: "rush", WR: "target", TE: "target" };

// Short tooltip labels for competitionHints.js kinds (badge title attr).
const HINT_KIND_LABELS = {
  "r1-wr": "R1 rookie WR",
  "wr-null": "WR arrival",
  "r1-rb-viability": "R1 rookie RB",
  "rb-touches": "RB touches",
  "rb-vet-null": "Vet RB arrival — no discount",
};

// Detail-panel copy for a competitionHints.js hint. Advisory phrasing only -
// nothing here ever touches allocs. rb-touches covers both a Day-2 rookie
// (has a pick) and an arriving vet (no pick) with parallel phrasing.
function hintCopy(hint) {
  const { kind, rival, suggest, pick } = hint;
  if (kind === "r1-wr") {
    return `R1 rookie WR added (${rival}, pick ${pick}). Incumbent WR1s historically lose ~3-4pp target share (2018-25, survivor-conditional). Suggested: trim ~3-4pp — not auto-applied.`;
  }
  if (kind === "r1-rb-viability") {
    return `R1 rookie RB added (${rival}, pick ${pick}). Historically a roster-viability event for incumbents (67% off the team next year) — rethink the role, not just the share.`;
  }
  if (kind === "rb-touches") {
    if (hint.vetTier === "bellcow-paid") {
      return `Veteran RB arrival (${rival}, $${hint.apyM}M/yr) behind a 45%+ bellcow. Paid vets are the one bellcow cell with any signal, but evidence is thin at this tier (n=3). Suggested: ${suggest} — not auto-applied.`;
    }
    if (hint.vetTier === "committee-paid") {
      return `Veteran RB arrival (${rival}, $${hint.apyM}M/yr) into a committee backfield (incumbent under 45% of rushes) — the significant cell: committee × paid vet cost incumbents −16.6pp rush share, CI[−27.6, −7.0] (2018-25). Suggested: ${suggest} — not auto-applied.`;
    }
    const intro = pick != null ? `Day 2 rookie RB added (${rival}, pick ${pick}).` : `Veteran RB arrival added (${rival}).`;
    return `${intro} Backfield touches often get redistributed early. Suggested: ${suggest} — not auto-applied.`;
  }
  if (kind === "rb-vet-null") {
    return `Veteran RB arrival (${rival}, $${hint.apyM}M/yr). Historically a cheap vet behind a 45%+ bellcow is NOT a share event (−4pp, CI through zero, 2018-25) — no discount supported. Depth/committee backs are who pay.`;
  }
  if (kind === "wr-null") {
    return `Competition added (${rival}), but Day-2/3 rookie WRs and vet WR arrivals are historically a non-event for established incumbents' share — no discount supported.`;
  }
  return null;
}

// Short tooltip labels for injuryHints.js kinds (badge title attr).
const INJURY_KIND_LABELS = {
  "rb-vacated": "RB games below role mean",
  "wr-vacated": "WR games below role mean",
  "te-vacated": "TE games below role mean",
};

// Detail-panel copy for an injuryHints.js hint. Advisory phrasing only -
// nothing here ever touches allocs.
function injuryHintCopy(hint, player, roleMean) {
  const { kind, gapGames, suggest } = hint;
  if (kind === "rb-vacated") {
    return `${player} set ${gapGames} games below the ${roleMean}-game role mean. Historically ~90% of a vacated backfield stays in the RB room (other RB ≈53%, depth ≈37%). Suggested: ${suggest} — not auto-applied. Same split applies to his target share if meaningful.`;
  }
  if (kind === "wr-vacated") {
    return `${player} set ${gapGames} games below role mean. Vacated WR targets historically flow to DEPTH, not the next starter: WR4+ recovered ≈75%; the WR2 bump (+4.7%) was statistically zero. Suggested: ${suggest} — don't bump WR2/WR3 on injury alone.`;
  }
  if (kind === "te-vacated") {
    return `${player} set ${gapGames} games below role mean. Only ≈52% of vacated TE volume returns to the TE room — the rest disperses to WR/RB. Suggested: ${suggest}; don't reallocate the full share.`;
  }
  return null;
}

// Badge tooltip for seedQualityHints.js — one title for all kinds.
const SEED_QUALITY_TITLE = "Injury-shortened 2025 — seed may misstate role";

// Detail-panel copy for a seedQualityHints.js hint. Advisory phrasing only -
// nothing here ever touches allocs. whenActive is the ×17/g proxy and is
// always presented as approximate ("~") — see the overstatement caveat in
// src/utils/seedQualityHints.js.
function seedQualityHintCopy(hint, player) {
  const { kind, gms, statCount, teamTotal, stat, rawShare, whenActive, priorShare, priorTeam, reference, movedFrom, pos, age } = hint;
  const counts = `${statCount}/${teamTotal} ${stat === "tgt" ? "tgt" : "att"}`;
  if (kind === "short-season-seed") {
    const prior = priorShare != null ? `; 2024 share: ${priorShare}%${priorTeam ? ` on ${priorTeam}` : ""}` : "";
    return `${player}'s 2025 was injury-shortened (${gms} games), and the seed uses his raw season share (${rawShare}%) — which understates his ROLE. When-active pace: ~${whenActive}% (${counts}; approximate)${prior}. Per-game roles historically persist through injury years — same-team starters returned within ~2-4pp of their role — the risk is GAMES: players off ≤10-game seasons miss 4+ again 47% of the time (vs 29% baseline), averaging ~12.7 games. Consider raising his share toward ~${reference}% and lowering his games, rather than leaving both at the seed. Not auto-applied.`;
  }
  if (kind === "short-season-moved") {
    const isRB = pos === "RB";
    const cell = isRB
      ? { word: "RBs 27+", kept: "1/5", mean: "−18pp" }
      : { word: "WRs", kept: "1/6", mean: "−9pp" };
    const ageUnknown = isRB && age == null
      ? " For RBs this cuts by age — sub-27 movers historically landed in BIGGER roles; 27+ movers were discards."
      : "";
    return `${player}'s 2025 was injury-shortened (${gms} games on ${movedFrom}) AND he changed teams — the one profile that historically loses its role. In the 2021-24 backtest, injured ${cell.word} who moved kept ≥80% of their role only ${cell.kept} of the time (mean ${cell.mean}). Treat his when-active pace (~${whenActive}%) as a CEILING, not an anchor; the low seed may be right. Not auto-applied.${ageUnknown}`;
  }
  if (kind === "short-season-moved-young-rb") {
    return `${player}'s 2025 was injury-shortened (${gms} games on ${movedFrom}) and he changed teams — but for sub-27 RBs a post-injury move was historically an OPPORTUNITY signal (teams paid for the role: +6.7pp mean, 5/6 kept ≥80%). His when-active pace was ~${whenActive}%. The games risk still applies (47% miss 4+ again). Not auto-applied.`;
  }
  return null;
}

// ── Seed-provenance captions (E1 explain collector, display-only) ────────────
// Labels for buildInitRates family codes. Keep in sync with the builders'
// explain vocabulary (SYNC PACT in projlabProjection.js).
const RATE_SEED_TAGS = {
  blend6040: "60/40 you/pos",
  posmean: "pos mean",
  "qb-rush-8020": "80/20 (QB rush)",
  // Source-agnostic on purpose: recent1yr may come from a hydrated override
  // slot's stats rather than the player's actual '25 line.
  recent1yr: "recent rate",
  fallback: "no-history default",
  depth: "depth const",
  // SYNC PACT: mirrors HOUSE_ALLOC_CODE / HOUSE_RATE_CODE in houseSeed.js.
  house: "Mock1 house",
};

function seedTagLine(famRec) {
  if (!famRec) return null;
  const tags = [];
  for (const code of Object.values(famRec)) {
    const t = RATE_SEED_TAGS[code];
    if (t && !tags.includes(t)) tags.push(t);
  }
  return tags.length ? `seeds: ${tags.join(" · ")}` : null;
}

// Caption for one ALLOC slider from an explain.slots[slotKey] field record.
function allocSeedCaption(rec, field, slotKey) {
  if (!rec) return null;
  let base;
  if (rec.code === "own25") {
    base = field === "rush"
      ? `seed: own '25 share (${rec.rAtt}/${rec.teamRushAtt} att)`
      : `seed: own '25 share (${rec.tgts}/${rec.teamPassAtt} tgt)`;
  } else if (rec.code === "house") base = "seeded from Mock1 house projections";
  else if (rec.code === "capital") base = `seed: rookie pick #${rec.pick} capital curve`;
  else if (rec.code === "slotavg") base = `seed: league ${slotKey} avg`;
  else if (rec.code === "remainder") base = "seed: league depth avg";
  else return null;
  return rec.scaled != null ? `${base} · rescaled ×${Math.round(rec.scaled * 100) / 100}` : base;
}

// ── Δ VS DEFAULTS (E3) — advisory-only compare of a resumed save against
// today's fresh seeds. Chips and summaries never write values.
const DELTA_THRESHOLDS = {
  rushPct: 0.5, tgtPct: 0.5, games: 1,
  ypc: 0.2, ypr: 0.2, rushYPC: 0.2, ypa: 0.2,
  catchPct: 1.0, compPct: 1.0,
  tdPct: 0.3, intPct: 0.3, rushTDPct: 0.3, recTDPct: 0.3,
};
const DELTA_FIELD_LABELS = {
  rushPct: "rush share", tgtPct: "target share", games: "games",
  ypc: "YPC", ypr: "Y/Rec", rushYPC: "rush YPC", ypa: "Y/A",
  catchPct: "catch%", compPct: "comp%",
  tdPct: "TD%", intPct: "INT%", rushTDPct: "rush TD%", recTDPct: "rec TD%",
};
const fmtDelta = (d) => `${d > 0 ? "+" : ""}${Math.round(d * 10) / 10}`;

function DefChip({ user, def, threshold }) {
  if (user == null || def == null) return null;
  const d = user - def;
  if (Math.abs(d) < threshold) return null;
  return (
    <span title={`today's default ${def} · yours ${fmtDelta(d)}`} style={{ fontSize: fontSize.micro, fontFamily: fonts.display, fontWeight: fontWeight.bold, color: d > 0 ? colors.status.success : colors.status.danger, background: "rgba(255,255,255,0.06)", border: `1px solid ${colors.border.subtle}`, borderRadius: 4, padding: "1px 5px", whiteSpace: "nowrap", flexShrink: 0 }}>
      def {def}
    </span>
  );
}

// Small muted two-click-confirm button (arm → confirm, auto-disarm after 4s).
// Shared by the clear-player-edits and reset-rates affordances.
function TwoClickButton({ idleLabel, armedLabel, onConfirm, title }) {
  const [armed, setArmed] = useState(false);
  const tRef = useRef(null);
  useEffect(() => () => clearTimeout(tRef.current), []);
  const onClick = () => {
    clearTimeout(tRef.current);
    if (!armed) {
      setArmed(true);
      tRef.current = setTimeout(() => setArmed(false), 4000);
      return;
    }
    setArmed(false);
    onConfirm();
  };
  return (
    <button onClick={onClick} title={title} style={{
      padding: "4px 10px", borderRadius: 6, cursor: "pointer", flexShrink: 0,
      fontSize: fontSize.micro, fontWeight: fontWeight.heavy, fontFamily: fonts.display, letterSpacing: "1px",
      background: armed ? "rgba(239,68,68,0.12)" : "rgba(255,255,255,0.04)",
      border: `1px solid ${armed ? "rgba(239,68,68,0.4)" : colors.border.default}`,
      color: armed ? "#ef4444" : colors.text.muted,
    }}>{armed ? armedLabel : idleLabel}</button>
  );
}

function DeltaToggle({ on, onToggle }) {
  return (
    <button onClick={onToggle} title="Compare against today's default seeds (advisory only)" style={{
      padding: "4px 10px", borderRadius: 6, cursor: "pointer", flexShrink: 0,
      fontSize: fontSize.micro, fontWeight: fontWeight.heavy, fontFamily: fonts.display, letterSpacing: "1px",
      background: on ? "rgba(255,107,0,0.12)" : "rgba(255,255,255,0.04)",
      border: `1px solid ${on ? "rgba(255,107,0,0.4)" : colors.border.default}`,
      color: on ? colors.brand.accent : colors.text.muted,
    }}>Δ VS DEFAULTS</button>
  );
}

function DeltaSummaryLine({ deltas }) {
  if (!deltas) return null;
  const n = deltas.length;
  const biggest = n ? deltas.reduce((a, b) => (b.score > a.score ? b : a)) : null;
  return (
    <span style={{ fontSize: fontSize.caption, color: colors.text.faint }}>
      {n} field{n === 1 ? "" : "s"} differ from today's defaults{biggest ? ` · biggest: ${biggest.label} ${biggest.field} ${fmtDelta(biggest.delta)}` : ""}
    </span>
  );
}

/** "Derrick Henry" → "D. Henry", single names pass through */
const shortName = (name) => {
  if (!name) return "—";
  const parts = name.trim().split(" ");
  if (parts.length < 2) return name;
  return `${parts[0][0]}. ${parts.slice(1).join(" ")}`;
};


// ── DEV-ONLY: render-loop guard for the FN2 intermittent freeze ──────────────
// Converts a silent synchronous hang into a thrown error with a stack trace.
// Counts renders per real-world second (reset via setTimeout, so it survives
// StrictMode's harmless double-render) and throws past `limit`. Dev builds
// only - never runs in production. Remove once the freeze is caught & fixed.
function useRenderLoopGuard(name, limit = 2000) {
  const state = useRef({ count: 0, resetScheduled: false });
  if (process.env.NODE_ENV !== "production") {
    state.current.count++;
    if (!state.current.resetScheduled) {
      state.current.resetScheduled = true;
      setTimeout(() => { state.current.count = 0; state.current.resetScheduled = false; }, 1000);
    }
    if (state.current.count > limit) {
      throw new Error(`[render-loop-guard] ${name} rendered >${limit}x in <1s - likely infinite render loop`);
    }
  }
}

// ── Module-level league stats (total, pass, rush) from real play data ─────────

const LEAGUE_STATS      = {};
const LEAGUE_PASS_STATS = {};
const LEAGUE_RUSH_STATS = {};

LEAGUE_YEARS.forEach(yr => {
  const rows = Object.values(PLAY_HISTORY).map(h => h.find(r => r.year === yr)).filter(Boolean);
  if (!rows.length) return;
  LEAGUE_STATS[yr] = {
    min: Math.min(...rows.map(r => r.totalPlays)),
    max: Math.max(...rows.map(r => r.totalPlays)),
    avg: Math.round(rows.reduce((s, r) => s + r.totalPlays, 0) / rows.length),
  };
  LEAGUE_PASS_STATS[yr] = {
    min: Math.min(...rows.map(r => r.passAtt)),
    max: Math.max(...rows.map(r => r.passAtt)),
    avg: Math.round(rows.reduce((s, r) => s + r.passAtt, 0) / rows.length),
  };
  LEAGUE_RUSH_STATS[yr] = {
    min: Math.min(...rows.map(r => r.rushAtt)),
    max: Math.max(...rows.map(r => r.rushAtt)),
    avg: Math.round(rows.reduce((s, r) => s + r.rushAtt, 0) / rows.length),
  };
});

// ── Helpers ───────────────────────────────────────────────────────────────────

function avgArr(arr) {
  const vals = arr.filter(v => v != null && v > 0);
  return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
}

function getCoachColorMap(history, field) {
  const names = [...new Set(history.map(h => h[field]).filter(n => n && n !== "—"))];
  const map = {};
  names.forEach((name, i) => { map[name] = COACH_PALETTE[i % COACH_PALETTE.length]; });
  return map;
}

function buildMergedHistory(abbrev) {
  const playData  = PLAY_HISTORY[abbrev] || [];
  const coachData = COACHING_HISTORY[abbrev] || [];
  return LEAGUE_YEARS.map(yr => {
    const play  = playData.find(r => r.year === yr);
    if (!play) return null;
    const coach = coachData.find(r => r.year === yr);
    return {
      year:  yr,
      pa:    play.passAtt,
      ra:    play.rushAtt,
      total: play.totalPlays,
      hc:    coach?.hc    ?? "—",
      oc:    coach?.oc    ?? "—",
      note:  coach?.notes ?? "",
    };
  }).filter(Boolean);
}

function buildCareerMergedHistory(careerData) {
  return careerData.filter(h => h.year !== 2026).map(h => {
    const coach = (COACHING_HISTORY[h.team] || []).find(c => c.year === h.year);
    return {
      year:  h.year,
      total: h.totalPlays,
      pa:    h.passAtt,
      ra:    h.rushAtt,
      team:  h.team,
      hc:    coach?.hc ?? "—",
      oc:    coach?.oc ?? "—",
    };
  });
}

function getCoachFullCareer(name) {
  if (!name || name === "—") return [];
  const result = [];
  Object.entries(COACHING_HISTORY).forEach(([abbrev, seasons]) => {
    (seasons || []).forEach(s => {
      let role = null;
      if (s.hc === name) role = "HC";
      else if (s.oc === name) role = "OC";
      if (!role) return;
      const play = (PLAY_HISTORY[abbrev] || []).find(r => r.year === s.year);
      result.push({
        year: s.year, team: abbrev, role,
        hc: role === "OC" ? s.hc : null,
        totalPlays: play?.totalPlays ?? null,
        passAtt: play?.passAtt ?? null,
        rushAtt: play?.rushAtt ?? null,
      });
    });
  });
  return result.sort((a, b) => a.year - b.year);
}

function getOCCareerHistory(ocName) {
  if (!ocName || ocName === "—") return [];
  const result = [];
  Object.entries(COACHING_HISTORY).forEach(([abbrev, seasons]) => {
    (seasons || []).forEach(s => {
      if (s.oc !== ocName) return;
      const play = (PLAY_HISTORY[abbrev] || []).find(r => r.year === s.year);
      result.push({
        year: s.year, team: abbrev,
        totalPlays: play?.totalPlays ?? null,
        passAtt: play?.passAtt ?? null,
        rushAtt: play?.rushAtt ?? null,
      });
    });
  });
  return result.sort((a, b) => a.year - b.year);
}

function getHCCareerHistory(hcName) {
  if (!hcName || hcName === "—") return [];
  const result = [];
  Object.entries(COACHING_HISTORY).forEach(([abbrev, seasons]) => {
    (seasons || []).forEach(s => {
      if (s.hc !== hcName) return;
      const play = (PLAY_HISTORY[abbrev] || []).find(r => r.year === s.year);
      result.push({
        year: s.year, team: abbrev,
        totalPlays: play?.totalPlays ?? null,
        passAtt: play?.passAtt ?? null,
        rushAtt: play?.rushAtt ?? null,
      });
    });
  });
  return result.sort((a, b) => a.year - b.year);
}

// Find where a head coach was HC before joining a new team
// Returns { fromTeam, history[] } or null if no prior HC stint found
function getHCPriorTeamHistory(hcName, currentAbbrev) {
  if (!hcName || hcName === "—") return null;
  // Find all teams where this person was HC (excluding currentAbbrev)
  const stints = []; // { abbrev, years[] }
  Object.entries(COACHING_HISTORY).forEach(([abbrev, seasons]) => {
    if (abbrev === currentAbbrev) return;
    const years = (seasons || []).filter(s => s.hc === hcName).map(s => s.year);
    if (years.length) stints.push({ abbrev, years });
  });
  if (!stints.length) return null;
  // Pick the stint with the most recent year (that's where he came from)
  const best = stints.reduce((a, b) => Math.max(...b.years) > Math.max(...a.years) ? b : a);
  const history = buildMergedHistory(best.abbrev);
  // Only include years the HC was actually there
  const filtered = history.filter(h => best.years.includes(h.year));
  return filtered.length ? { fromTeam: best.abbrev, history: filtered } : null;
}

// Find where an OC was OC before joining a new team
function getOCPriorTeamHistory(ocName, currentAbbrev) {
  if (!ocName || ocName === "—") return null;
  const stints = [];
  Object.entries(COACHING_HISTORY).forEach(([abbrev, seasons]) => {
    if (abbrev === currentAbbrev) return;
    const years = (seasons || []).filter(s => s.oc === ocName).map(s => s.year);
    if (years.length) stints.push({ abbrev, years });
  });
  if (!stints.length) return null;
  const best = stints.reduce((a, b) => Math.max(...b.years) > Math.max(...a.years) ? b : a);
  const history = buildMergedHistory(best.abbrev);
  const filtered = history.filter(h => best.years.includes(h.year));
  return filtered.length ? { fromTeam: best.abbrev, history: filtered } : null;
}

// ── Allocation helpers ─────────────────────────────────────────────────────

function avgArrF(arr) {
  const vals = arr.filter(v => v != null);
  return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length * 10) / 10 : 0;
}

// Slot rank: QB1→0, RB1→0, RB2→1, RB3+→-1 (remainder), WR1→0, etc.
function slotRank(key) {
  if (key.endsWith("+")) return -1; // remainder bucket
  const n = parseInt(key.replace(/\D/g, ""), 10);
  return (n || 1) - 1;
}
function slotPos(key) { return key.replace(/[\d+]/g, ""); }

// Find who held a slot role on a team in a given year by ranking players
// at that position by the primary stat (carries for QB/RB, targets for WR/TE).
// Returns { player, rushAtt, targets, gms } or null.
function findSlotHolder(team, year, slot) {
  const pos = slotPos(slot);
  const rank = slotRank(slot);
  const isRemainder = rank === -1;
  const sortField = pos === "QB" ? "passAtt" : pos === "RB" ? "rushAtt" : "targets";

  // Gather all players at this position on this team in this year
  const candidates = [];
  for (const [name, rows] of Object.entries(PLAYER_HISTORY)) {
    const row = rows.find(r => r.year === year && r.team === team && r.pos === pos);
    if (row) candidates.push({ name, ...row });
  }
  candidates.sort((a, b) => (b[sortField] || 0) - (a[sortField] || 0));

  if (isRemainder) {
    // Sum everyone below the named starter count
    const namedCount = SLOTS.filter(s => !s.isRemainder && slotPos(s.key) === pos).length;
    const rest = candidates.slice(namedCount);
    if (!rest.length) return null;
    return {
      player: `${pos} depth (${rest.length})`,
      rushAtt: rest.reduce((s, r) => s + (r.rushAtt || 0), 0),
      targets: rest.reduce((s, r) => s + (r.targets || 0), 0),
      gms: Math.max(...rest.map(r => r.gms || 0)),
    };
  }
  const pick = candidates[rank];
  if (!pick) return null;
  return { player: pick.name, rushAtt: pick.rushAtt || 0, targets: pick.targets || 0, gms: pick.gms || 0 };
}

function buildAllocRow(team, year, slot) {
  const play = (PLAY_HISTORY[team] || []).find(p => p.year === year);
  if (!play) return null;
  const holder = findSlotHolder(team, year, slot);
  if (!holder) return null;
  const rushPct = play.rushAtt > 0 ? Math.round(holder.rushAtt / play.rushAtt * 1000) / 10 : 0;
  const tgtPct  = play.passAtt > 0 ? Math.round(holder.targets / play.passAtt * 1000) / 10 : 0;
  return {
    year, team, player: holder.player,
    ratt: holder.rushAtt, rushPct,
    trgs: holder.targets, tgtPct,
    teamPA: play.passAtt, teamRA: play.rushAtt, gms: holder.gms,
  };
}

// League-average carries/targets for a slot across all teams for a given year
function leagueAvgForSlot(slot, year) {
  const teams = Object.keys(PLAY_HISTORY);
  const rows = teams.map(t => buildAllocRow(t, year, slot)).filter(Boolean);
  if (!rows.length) return null;
  return {
    ratt: Math.round(rows.reduce((s, r) => s + r.ratt, 0) / rows.length),
    trgs: Math.round(rows.reduce((s, r) => s + r.trgs, 0) / rows.length),
  };
}

// Build a player summary for HC/OC subtitles: "Derrick Henry '24-'25, Gus Edwards '23"
function buildPlayerSummary(history) {
  if (!history.length) return "";
  // Group consecutive years by player
  const groups = [];
  let cur = { player: history[0].player, years: [history[0].year] };
  for (let i = 1; i < history.length; i++) {
    if (history[i].player === cur.player) { cur.years.push(history[i].year); }
    else { groups.push(cur); cur = { player: history[i].player, years: [history[i].year] }; }
  }
  groups.push(cur);
  return groups.map(g => {
    const name = shortName(g.player);
    const yrs = g.years.map(y => `'${String(y).slice(2)}`);
    return yrs.length > 1 ? `${name} ${yrs[0]}-${yrs[yrs.length - 1]}` : `${name} ${yrs[0]}`;
  }).join(", ");
}

function buildAllocHistory(playerName, abbrev, viewMode, slot, hcName, ocName) {
  if (viewMode === "player") {
    // Player career: follow THIS player across all teams
    const rows = findPlayerHist(playerName) || [];
    return rows.map(r => {
      const play = (PLAY_HISTORY[r.team] || []).find(p => p.year === r.year);
      if (!play) return null;
      const rushPct = play.rushAtt > 0 ? Math.round((r.rushAtt || 0) / play.rushAtt * 1000) / 10 : 0;
      const tgtPct  = play.passAtt > 0 ? Math.round((r.targets || 0) / play.passAtt * 1000) / 10 : 0;
      return { year: r.year, team: r.team, player: playerName,
        ratt: r.rushAtt || 0, rushPct, trgs: r.targets || 0, tgtPct,
        teamPA: play.passAtt, teamRA: play.rushAtt, gms: r.gms };
    }).filter(Boolean).sort((a, b) => a.year - b.year);
  }
  if (viewMode === "position") {
    // Position: who held this slot on THIS team each year
    return LEAGUE_YEARS.map(yr => buildAllocRow(abbrev, yr, slot)).filter(Boolean);
  }
  if (viewMode === "hc") {
    // HC: who held this slot under this HC (at all his teams)
    const results = [];
    Object.entries(COACHING_HISTORY).forEach(([team, seasons]) => {
      (seasons || []).filter(c => c.hc === hcName).forEach(c => {
        const row = buildAllocRow(team, c.year, slot);
        if (row) results.push(row);
      });
    });
    return results.sort((a, b) => a.year - b.year);
  }
  // OC: who held this slot under this OC (at all his teams)
  const results = [];
  Object.entries(COACHING_HISTORY).forEach(([team, seasons]) => {
    (seasons || []).filter(c => c.oc === ocName).forEach(c => {
      const row = buildAllocRow(team, c.year, slot);
      if (row) results.push(row);
    });
  });
  return results.sort((a, b) => a.year - b.year);
}

// ── StartersOverride hydration ─────────────────────────────────────────────────
// Maps a raw buildLabStarters() override object (no stats) into the same shape
// as STARTERS_2025 slots, pulling counting + efficiency metrics from PLAYER_HISTORY.

/**
 * Enriches each slot in a startersOverride object with a `stats` sub-object
 * and `games` field sourced from PLAYER_HISTORY (most recent season, 2025 first).
 * Slots with no history entry (rookies, deep depth) are returned unchanged —
 * buildInitAllocs / buildInitRates already have 0 / default fallbacks.
 */
function hydrateOverrideStarters(override) {
  const result = {};
  for (const [slotKey, slot] of Object.entries(override)) {
    if (!slot?.player) { result[slotKey] = slot; continue; }
    const rows = findPlayerHist(slot.player, slot.sleeperId);
    if (!rows || !rows.length) { result[slotKey] = slot; continue; }
    // Prefer 2025 season; fall back to most recent available
    const entry = rows.find(r => r.year === 2025)
      ?? rows.reduce((a, b) => (b.year > a.year ? b : a));
    result[slotKey] = {
      ...slot,
      games: entry.gms ?? null,
      stats: {
        // Counting stats — STARTERS_2025-compatible field names
        pAtt:   entry.passAtt ?? 0,
        pYds:   entry.passYds ?? 0,
        pTD:    entry.passTD  ?? 0,
        int:    entry.int     ?? 0,
        rAtt:   entry.rushAtt ?? 0,
        rYds:   entry.rushYds ?? 0,
        rTD:    entry.rushTD  ?? 0,
        tgts:   entry.targets ?? 0,
        rec:    entry.rec     ?? 0,
        recYds: entry.recYds  ?? 0,
        recTD:  entry.recTD   ?? 0,
        // Efficiency metrics — for buildInitRates (same names as PLAYER_HISTORY)
        compPct:   entry.compPct   ?? null,
        ypa:       entry.ypa       ?? null,
        tdPct:     entry.tdPct     ?? null,
        intPct:    entry.intPct    ?? null,
        ypc:       entry.ypc       ?? null,
        rushTDPct: entry.rushTDPct ?? null,
        catchPct:  entry.catchPct  ?? null,
        ypr:       entry.ypr       ?? null,
        recTDPct:  entry.recTDPct  ?? null,
      },
    };
  }
  return result;
}


function build1ANarrative(abbrev, history, { isNewHC, isNewOC, hcName, ocName, hcPrior, ocPrior }) {
  if (!history.length) return "No historical data available for this team.";
  const fullName  = TEAM_META[abbrev]?.fullName ?? abbrev;
  const teamShort = fullName.split(" ").pop();
  const teamAvg   = avgArr(history.map(h => h.total));
  const lgAvg     = avgArr(LEAGUE_YEARS.filter(y => LEAGUE_STATS[y]).map(y => LEAGUE_STATS[y].avg));
  const last      = history[history.length - 1];

  // New HC with prior team data
  if (isNewHC && hcPrior) {
    const priorAvg  = avgArr(hcPrior.history.map(h => h.total));
    const fromFull  = TEAM_META[hcPrior.fromTeam]?.fullName ?? hcPrior.fromTeam;
    return (
      `<strong style="color:${colors.brand.accent}">${hcName}</strong> averaged ` +
      `<strong>${priorAvg} plays</strong> at ${fromFull}. ` +
      `The ${teamShort} averaged <strong>${teamAvg} plays</strong> under their previous staff. ` +
      `Where do you project ${abbrev} in year 1 under ${hcName.split(" ").pop()}?`
    );
  }

  // New OC (same HC) with prior team data
  if (isNewOC && ocPrior) {
    const priorAvg  = avgArr(ocPrior.history.map(h => h.total));
    const fromFull  = TEAM_META[ocPrior.fromTeam]?.fullName ?? ocPrior.fromTeam;
    return (
      `New OC <strong style="color:${colors.brand.accent}">${ocName}</strong> comes from ${fromFull}, ` +
      `where their offense averaged <strong>${priorAvg} plays</strong>. ` +
      `The ${teamShort} have averaged <strong>${teamAvg} plays</strong> recently. ` +
      `How will ${ocName.split(" ").pop()}'s scheme change ${abbrev}'s tempo in 2026?`
    );
  }

  // Default: stable staff
  const vsLg = teamAvg - lgAvg;
  const dir  = vsLg > 0 ? "above" : "below";
  return (
    `${fullName} has averaged <strong>${teamAvg} plays</strong> over the past 5 seasons — ` +
    `<strong style="color:${vsLg >= 0 ? "#4ade80" : "#f87171"}">${Math.abs(vsLg)} plays ${dir} league average</strong>. ` +
    `Last year's <span style="color:#A0AEC0">${last.total}</span> plays ` +
    `${last.total >= teamAvg ? "matched or exceeded" : `fell ${teamAvg - last.total} short of`} their 5-year average. ` +
    `Will the ${teamShort} maintain this volume in 2026?`
  );
}

function build1BNarrative(abbrev, history, { isNewHC, isNewOC, hcName, ocName, hcPrior, ocPrior }) {
  if (!history.length) return "No historical split data available.";
  const fullName   = TEAM_META[abbrev]?.fullName ?? abbrev;
  const teamShort  = fullName.split(" ").pop();
  const teamAvgPct = avgArr(history.map(h => Math.round(h.pa / h.total * 100)));
  const last       = history[history.length - 1];
  const lastPct    = Math.round(last.pa / last.total * 100);
  const passColor  = colors.pos.QB;
  const rushColor  = colors.pos.RB;

  // New HC with prior team data
  if (isNewHC && hcPrior) {
    const priorAvgPct = avgArr(hcPrior.history.map(h => Math.round(h.pa / h.total * 100)));
    const fromFull    = TEAM_META[hcPrior.fromTeam]?.fullName ?? hcPrior.fromTeam;
    const dir = priorAvgPct > teamAvgPct ? "more pass-heavy" : "more run-heavy";
    return (
      `${hcName}'s offense at ${fromFull} averaged ` +
      `<strong style="color:${passColor}">${priorAvgPct}% passing</strong> — ` +
      `${dir} than ${abbrev}'s <strong>${teamAvgPct}%</strong> under their old staff. ` +
      `Will ${hcName.split(" ").pop()} bring his scheme's split to ${abbrev}, or adapt to the roster?`
    );
  }

  // New OC with prior team data
  if (isNewOC && ocPrior) {
    const priorAvgPct = avgArr(ocPrior.history.map(h => Math.round(h.pa / h.total * 100)));
    const fromFull    = TEAM_META[ocPrior.fromTeam]?.fullName ?? ocPrior.fromTeam;
    return (
      `New OC ${ocName} ran a <strong style="color:${passColor}">${priorAvgPct}% pass</strong> offense at ${fromFull}. ` +
      `${abbrev} has been at <strong>${teamAvgPct}%</strong> recently. ` +
      `How much will ${ocName.split(" ").pop()}'s tendencies shift the balance?`
    );
  }

  // Default: stable staff — trend analysis
  const half     = Math.floor(history.length / 2);
  const earlyPct = avgArr(history.slice(0, half).map(h => Math.round(h.pa / h.total * 100)));
  const latePct  = avgArr(history.slice(half).map(h => Math.round(h.pa / h.total * 100)));
  const trend    = latePct > earlyPct + 3 ? "trending more pass-heavy" : latePct < earlyPct - 3 ? "trending more run-heavy" : "holding a consistent pass/run balance";

  return (
    `${fullName} has averaged <strong style="color:${passColor}">${teamAvgPct}% passing</strong> over 5 seasons — ` +
    `${trend}. Last year: ` +
    `<strong style="color:${lastPct > teamAvgPct ? passColor : rushColor}">${lastPct}% pass / ${100 - lastPct}% rush</strong>. ` +
    `Where do you project the ${teamShort}'s 2026 split?`
  );
}

// ── SliderInput (1A) ──────────────────────────────────────────────────────────

function SliderInput({ label, value, onChange, min, max, color, helper }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <div>
          <span style={{ fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC" }}>{label}</span>
          {helper && <span style={{ fontSize: fontSize.small, color: "#A0AEC0", marginLeft: 8 }}>{helper}</span>}
        </div>
        <input
          type="number" value={value} step={1}
          onChange={e => onChange(Math.max(min, Math.min(max, Number(e.target.value) || 0)))}
          style={{ width: 72, background: colors.bg.elevated, border: `1px solid ${colors.border.default}`, borderRadius: 6, color, fontSize: fontSize.base, fontWeight: fontWeight.heavy, fontFamily: fonts.display, textAlign: "center", padding: "6px", outline: "none" }}
        />
      </div>
      <input type="range" min={min} max={max} step={1} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: color, height: 6, cursor: "pointer" }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <span style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>{min}</span>
        <span style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>{max}</span>
      </div>
    </div>
  );
}

// ── RatioSlider (1B) ──────────────────────────────────────────────────────────

function RatioSlider({ passPct, onChange }) {
  const rushPct = 100 - passPct;
  return (
    <div style={{ margin: "14px 0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: colors.pos.QB, letterSpacing: "0.5px" }}>PASS</span>
          <span style={{ fontSize: fontSize.title, fontWeight: fontWeight.black, color: colors.pos.QB, fontFamily: fonts.display }}>{Math.round(passPct)}%</span>
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
          <span style={{ fontSize: fontSize.title, fontWeight: fontWeight.black, color: colors.pos.RB, fontFamily: fonts.display }}>{Math.round(rushPct)}%</span>
          <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: colors.pos.RB, letterSpacing: "0.5px" }}>RUSH</span>
        </div>
      </div>
      <div style={{ height: 10, display: "flex", borderRadius: 5, overflow: "hidden", marginBottom: 6 }}>
        <div style={{ width: `${passPct}%`, background: `${colors.pos.QB}80`, transition: "width 0.1s" }} />
        <div style={{ width: `${rushPct}%`, background: `${colors.pos.RB}80`, transition: "width 0.1s" }} />
      </div>
      <input type="range" min={33} max={78} step={0.5} value={passPct}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: colors.brand.accent, height: 6, cursor: "pointer" }}
      />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <span style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>33% pass (ultra run-heavy)</span>
        <span style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>78% pass (air raid)</span>
      </div>
    </div>
  );
}

// ── TotalPlaysChart (1A) ──────────────────────────────────────────────────────

function TotalPlaysChart({ mergedHistory, projValue, teamColor, onProjChange, abbrev, title, showProjection = true }) {
  useRenderLoopGuard("TotalPlaysChart");
  const teamByYear = {};
  mergedHistory.forEach(h => { teamByYear[h.year] = h; });
  const barFill = teamColor || "#22d3ee";

  const years = showProjection ? LEAGUE_YEARS : mergedHistory.map(h => h.year);
  const H = 380;
  const pad = { top: 22, bottom: mergedHistory[0]?.team ? 46 : 34, left: 48, right: showProjection ? 72 : 16 };
  const W = showProjection ? 960 : Math.max(480, years.length * 80 + pad.left + pad.right);
  const cW = W - pad.left - pad.right;
  const cH = H - pad.top - pad.bottom;
  const n = showProjection ? years.length + 1 : years.length;
  const xSt = cW / n;
  const xFor = i => pad.left + i * xSt + xSt / 2;
  const pX = showProjection ? pad.left + years.length * xSt + xSt / 2 : 0;

  const allVals = [
    ...years.flatMap(y => { const lg = LEAGUE_STATS[y]; return lg ? [lg.max] : []; }),
    ...years.map(y => teamByYear[y]?.total).filter(Boolean),
    ...(showProjection ? [projValue] : []),
  ];
  const lgMinVals = years.map(y => { const lg = LEAGUE_STATS[y]; return lg ? lg.min : 600; });
  const dataMin = allVals.length ? Math.min(...allVals) : 600;
  const maxV = Math.max(...allVals) * 1.06;
  const minV = Math.min(600, ...lgMinVals, dataMin) * 0.95;
  const rng = maxV - minV;
  const yFor = v => pad.top + cH - ((v - minV) / rng) * cH;
  const valFor = y => Math.round(minV + ((pad.top + cH - y) / cH) * rng);

  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const screenToSvgY = (clientY) => {
    const svg = svgRef.current;
    if (!svg) return 0;
    const rect = svg.getBoundingClientRect();
    return ((clientY - rect.top) / rect.height) * H;
  };
  const handleDragStart = (e) => {
    e.preventDefault();
    dragRef.current = true;
    const move = (ev) => {
      if (!dragRef.current) return;
      const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const svgY = screenToSvgY(clientY);
      const v = Math.max(500, Math.min(1550, valFor(svgY)));
      onProjChange?.(v);
    };
    const up = () => {
      dragRef.current = false;
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", up);
  };

  const gridVals = [700, 800, 900, 1000, 1100].filter(v => v > minV && v < maxV);

  const barW = xSt * 0.38;

  return (
    <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: 10, border: `1px solid ${colors.border.subtle}`, padding: "8px 4px 6px" }}>
      <div style={{ fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC", letterSpacing: "1.5px", padding: "0 8px", marginBottom: 6 }}>
        {title || "TOTAL OFFENSIVE PLAYS — HISTORICAL vs. LEAGUE"}
      </div>
      <svg ref={svgRef} width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", margin: "0 auto", maxWidth: "100%" }}>
        {gridVals.map(v => (
          <g key={v}>
            <line x1={pad.left} x2={W - pad.right} y1={yFor(v)} y2={yFor(v)} stroke="rgba(255,255,255,0.05)" strokeWidth={1} />
            <text x={pad.left - 6} y={yFor(v) + 3} textAnchor="end" fill="#A0AEC0" fontSize={fontSize.small} fontFamily={fonts.display}>{v}</text>
          </g>
        ))}
        {/* Projection dashed line */}
        {showProjection && (
          <line x1={pad.left} x2={W - pad.right + 32} y1={yFor(projValue)} y2={yFor(projValue)}
            stroke={colors.brand.orange} strokeWidth={1.5} strokeDasharray="6,4" opacity={0.5} />
        )}
        {/* League range bands */}
        {years.map((y, i) => {
          const lg = LEAGUE_STATS[y];
          if (!lg) return null;
          const bW = xSt * 0.78;
          return <rect key={`band-${y}`} x={xFor(i) - bW/2} y={yFor(lg.max)} width={bW} height={yFor(lg.min) - yFor(lg.max)} fill="rgba(255,255,255,0.04)" rx={3} />;
        })}
        {/* League avg dashed polyline */}
        {years.length >= 2 && (
          <polyline
            points={years.map((y, i) => { const lg = LEAGUE_STATS[y]; return lg ? `${xFor(i)},${yFor(lg.avg)}` : null; }).filter(Boolean).join(" ")}
            fill="none" stroke="#A0AEC0" strokeWidth={1.5} strokeDasharray="4,3"
          />
        )}
        {LEAGUE_STATS[2025] && (
          <text x={xFor(years.length - 1) + 22} y={yFor(LEAGUE_STATS[2025].avg) + 3} fill="#A0AEC0" fontSize={fontSize.small} fontFamily={fonts.display} fontWeight={fontWeight.medium}>LG AVG</text>
        )}
        {/* Data bars per year */}
        {years.map((y, yi) => {
          const h = teamByYear[y];
          if (!h) return null;
          return (
            <g key={`bar-${y}`}>
              <rect x={xFor(yi) - barW / 2} y={yFor(h.total)} width={barW} height={yFor(minV) - yFor(h.total)} fill={barFill} opacity={0.85} rx={3} />
              <text x={xFor(yi)} y={yFor(h.total) - 6} textAnchor="middle" fill={barFill} fontSize={fontSize.body} fontWeight={fontWeight.bold} fontFamily={fonts.display}>{h.total}</text>
              {h.team ? (
                <>
                  <text x={xFor(yi)} y={H - pad.bottom + 14} textAnchor="middle" fill="#F7FAFC" fontSize={fontSize.caption} fontWeight={fontWeight.bold} fontFamily={fonts.display}>{h.team}</text>
                  <text x={xFor(yi)} y={H - pad.bottom + 26} textAnchor="middle" fill="#A0AEC0" fontSize={fontSize.micro}>'{String(y).slice(2)}</text>
                </>
              ) : (
                <text x={xFor(yi)} y={H - 8} textAnchor="middle" fill="#A0AEC0" fontSize={fontSize.body} fontWeight={fontWeight.medium} fontFamily={fonts.display}>'{String(y).slice(2)}</text>
              )}
            </g>
          );
        })}
        {/* Projection bar */}
        {showProjection && (<>
          <defs>
            <linearGradient id="te-projGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.brand.orange} />
              <stop offset="100%" stopColor={colors.brand.deep} />
            </linearGradient>
          </defs>
          <g onMouseDown={handleDragStart} onTouchStart={handleDragStart} style={{ cursor: "ns-resize" }}>
            <rect x={pX - 14} y={yFor(projValue)} width={28} height={yFor(minV) - yFor(projValue)}
              fill="url(#te-projGrad)" rx={3} style={{ filter: "drop-shadow(0 0 8px rgba(255,107,0,0.4))" }} />
            <rect x={pX - 20} y={yFor(projValue) - 8} width={40} height={16} fill="transparent" />
            <line x1={pX - 6} x2={pX + 6} y1={yFor(projValue) + 4} y2={yFor(projValue) + 4} stroke="rgba(255,255,255,0.5)" strokeWidth={1} />
            <line x1={pX - 6} x2={pX + 6} y1={yFor(projValue) + 7} y2={yFor(projValue) + 7} stroke="rgba(255,255,255,0.5)" strokeWidth={1} />
            <text x={pX} y={yFor(projValue) - 6} textAnchor="middle" fill={colors.brand.accent} fontSize={fontSize.sub} fontWeight={fontWeight.black} fontFamily={fonts.display}>{projValue}</text>
          </g>
          <text x={pX} y={H - 8} textAnchor="middle" fill={colors.brand.accent} fontSize={fontSize.body} fontWeight={fontWeight.heavy} fontFamily={fonts.display}>'26</text>
          {(() => {
            const lg = LEAGUE_STATS[years[0]];
            if (!lg) return null;
            const lx = xFor(0) + xSt * 0.35;
            return (
              <g>
                <text x={lx} y={yFor(lg.max) + 3} fill="#A0AEC0" fontSize={fontSize.small}>max</text>
                <text x={lx} y={yFor(lg.min) - 2} fill="#A0AEC0" fontSize={fontSize.small}>min</text>
              </g>
            );
          })()}
        </>)}
      </svg>
      {/* Legend */}
      <div style={{ display: "flex", justifyContent: "center", gap: 12, paddingBottom: 4, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: fontSize.small, color: barFill }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: barFill, opacity: 0.85 }} />{abbrev}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: fontSize.small, color: "#A0AEC0" }}>
          <div style={{ width: 14, height: 8, borderRadius: 2, background: "rgba(255,255,255,0.06)" }} />League Range
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: fontSize.small, color: "#A0AEC0" }}>
          <div style={{ width: 14, height: 0, borderTop: "2px dashed #A0AEC0" }} />League Avg
        </div>
        {showProjection && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: fontSize.small, color: colors.brand.accent }}>
            <div style={{ width: 14, height: 0, borderTop: "2px dashed rgba(255,107,0,0.5)" }} />Your Projection
          </div>
        )}
      </div>
    </div>
  );
}

// ── SplitChart (1B) — grouped pass%/rush% bar chart ──────────────────────────

function SplitChart({ mergedHistory, projPassAtt, projRushAtt, onSplitChange, title, showProjection = true }) {
  useRenderLoopGuard("SplitChart");
  const H = 340;
  const pad = { top: 20, bottom: mergedHistory[0]?.team ? 46 : 34, left: 48, right: showProjection ? 72 : 16 };
  const W = showProjection ? 960 : Math.max(480, mergedHistory.length * 80 + pad.left + pad.right);
  const cW = W - pad.left - pad.right;
  const cH = H - pad.top - pad.bottom;
  const n  = showProjection ? mergedHistory.length + 1 : mergedHistory.length;
  const xSt = cW / n;
  const xFor = i => pad.left + i * xSt + xSt / 2;
  const pX   = showProjection ? pad.left + mergedHistory.length * xSt + xSt / 2 : 0;
  const projTotal = (projPassAtt || 0) + (projRushAtt || 0);
  const projPassPct = projTotal > 0 ? (projPassAtt / projTotal) * 100 : 50;
  const projRushPct = 100 - projPassPct;

  const svgRef = useRef(null);
  const dragRef = useRef(null);

  const yMin = 22, yMax = 78, yRng = yMax - yMin;
  const yFor = v => pad.top + cH - ((v - yMin) / yRng) * cH;
  const valFor = y => yMin + ((pad.top + cH - y) / cH) * yRng;
  const gridVals = [25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75];

  const bw = xSt * 0.22;
  const gap = 2;
  const passColor = "#60a5fa";
  const rushColor = "#34d399";

  // League avg pass%
  const lgPassPcts = mergedHistory.map(h => {
    const ls = LEAGUE_STATS[h.year];
    const lp = LEAGUE_PASS_STATS[h.year];
    return (ls && lp) ? (lp.avg / ls.avg) * 100 : null;
  }).filter(Boolean);
  const lgPassPct = lgPassPcts.length ? lgPassPcts.reduce((a, b) => a + b, 0) / lgPassPcts.length : null;
  const lgRushPct = lgPassPct != null ? 100 - lgPassPct : null;

  const screenToSvgY = (clientY) => {
    const svg = svgRef.current;
    if (!svg) return 0;
    const rect = svg.getBoundingClientRect();
    return ((clientY - rect.top) / rect.height) * H;
  };
  const handleDragStart = (which) => (e) => {
    e.preventDefault();
    dragRef.current = true;
    const move = (ev) => {
      if (!dragRef.current) return;
      const clientY = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const svgY = screenToSvgY(clientY);
      const raw = Math.max(33, Math.min(67, valFor(svgY)));
      const passPct = which === "pass" ? raw : 100 - raw;
      onSplitChange?.(passPct);
    };
    const up = () => {
      dragRef.current = false;
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("touchend", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("touchend", up);
  };

  return (
    <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: 10, border: `1px solid ${colors.border.subtle}`, padding: "8px 4px 6px" }}>
      <div style={{ fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC", letterSpacing: "1.5px", padding: "0 8px", marginBottom: 6 }}>
        {title || "PASS % / RUSH % — HISTORICAL TREND"}
      </div>
      <svg ref={svgRef} width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: "block", margin: "0 auto", maxWidth: "100%" }}>
        {gridVals.map(v => (
          <g key={v}>
            <line x1={pad.left} x2={W - pad.right} y1={yFor(v)} y2={yFor(v)} stroke="rgba(255,255,255,0.05)" strokeWidth={1} />
            <text x={pad.left - 6} y={yFor(v) + 3} textAnchor="end" fill="#A0AEC0" fontSize={fontSize.caption} fontFamily={fonts.display}>{v}%</text>
          </g>
        ))}
        {lgPassPct != null && (
          <>
            <line x1={pad.left} x2={W - pad.right + 20} y1={yFor(lgPassPct)} y2={yFor(lgPassPct)}
              stroke={passColor} strokeWidth={1.2} strokeDasharray="6,4" opacity={0.4} />
            <text x={W - pad.right + 24} y={yFor(lgPassPct) + 4} fill={passColor} fontSize={fontSize.micro} fontFamily={fonts.display} opacity={0.7}>LG {lgPassPct.toFixed(0)}%</text>
          </>
        )}
        {lgRushPct != null && (
          <>
            <line x1={pad.left} x2={W - pad.right + 20} y1={yFor(lgRushPct)} y2={yFor(lgRushPct)}
              stroke={rushColor} strokeWidth={1.2} strokeDasharray="6,4" opacity={0.4} />
            <text x={W - pad.right + 24} y={yFor(lgRushPct) + 4} fill={rushColor} fontSize={fontSize.micro} fontFamily={fonts.display} opacity={0.7}>LG {lgRushPct.toFixed(0)}%</text>
          </>
        )}
        {mergedHistory.map((h, i) => {
          const pp = h.total > 0 ? (h.pa / h.total) * 100 : 50;
          const rp = 100 - pp;
          const cx = xFor(i);
          const baseY = yFor(yMin);
          return (
            <g key={`bar-${h.year}`}>
              <rect x={cx - bw - gap/2} y={yFor(pp)} width={bw} height={baseY - yFor(pp)}
                fill={passColor} opacity={0.65} rx={2} />
              <text x={cx - bw/2 - gap/2} y={yFor(pp) - 5} textAnchor="middle" fill={passColor} fontSize={fontSize.caption} fontWeight={fontWeight.bold} fontFamily={fonts.display} opacity={0.85}>{pp.toFixed(0)}</text>
              <rect x={cx + gap/2} y={yFor(rp)} width={bw} height={baseY - yFor(rp)}
                fill={rushColor} opacity={0.65} rx={2} />
              <text x={cx + bw/2 + gap/2} y={yFor(rp) - 5} textAnchor="middle" fill={rushColor} fontSize={fontSize.caption} fontWeight={fontWeight.bold} fontFamily={fonts.display} opacity={0.85}>{rp.toFixed(0)}</text>
              {h.team ? (
                <>
                  <text x={cx} y={H - pad.bottom + 14} textAnchor="middle" fill="#F7FAFC" fontSize={fontSize.caption} fontWeight={fontWeight.bold} fontFamily={fonts.display}>{h.team}</text>
                  <text x={cx} y={H - pad.bottom + 26} textAnchor="middle" fill="#A0AEC0" fontSize={fontSize.micro}>'{String(h.year).slice(2)}</text>
                </>
              ) : (
                <text x={cx} y={H - 8} textAnchor="middle" fill="#A0AEC0" fontSize={fontSize.small} fontWeight={fontWeight.medium} fontFamily={fonts.display}>'{String(h.year).slice(2)}</text>
              )}
            </g>
          );
        })}
        {showProjection && (() => {
          const cx = pX;
          const baseY = yFor(yMin);
          return (
            <g>
              <g onMouseDown={handleDragStart("pass")} onTouchStart={handleDragStart("pass")} style={{ cursor: "ns-resize" }}>
                <rect x={cx - bw - gap/2} y={yFor(projPassPct)} width={bw} height={baseY - yFor(projPassPct)}
                  fill={passColor} opacity={0.9} rx={2} />
                <rect x={cx - bw - gap/2} y={yFor(projPassPct)} width={bw} height={baseY - yFor(projPassPct)}
                  fill="none" stroke={colors.brand.accent} strokeWidth={1.5} rx={2}
                  style={{ filter: "drop-shadow(0 0 4px rgba(255,107,0,0.3))" }} />
                <rect x={cx - bw - gap/2 - 4} y={yFor(projPassPct) - 6} width={bw + 8} height={12} fill="transparent" />
                <line x1={cx - bw - gap/2 + 3} x2={cx - gap/2 - 3} y1={yFor(projPassPct) + 3} y2={yFor(projPassPct) + 3} stroke="rgba(255,255,255,0.5)" strokeWidth={1} />
                <line x1={cx - bw - gap/2 + 3} x2={cx - gap/2 - 3} y1={yFor(projPassPct) + 6} y2={yFor(projPassPct) + 6} stroke="rgba(255,255,255,0.5)" strokeWidth={1} />
              </g>
              <text x={cx - bw/2 - gap/2} y={yFor(projPassPct) - 5} textAnchor="middle" fill={colors.brand.accent} fontSize={fontSize.small} fontWeight={fontWeight.black} fontFamily={fonts.display}>{projPassPct.toFixed(0)}</text>
              <g onMouseDown={handleDragStart("rush")} onTouchStart={handleDragStart("rush")} style={{ cursor: "ns-resize" }}>
                <rect x={cx + gap/2} y={yFor(projRushPct)} width={bw} height={baseY - yFor(projRushPct)}
                  fill={rushColor} opacity={0.9} rx={2} />
                <rect x={cx + gap/2} y={yFor(projRushPct)} width={bw} height={baseY - yFor(projRushPct)}
                  fill="none" stroke={colors.brand.accent} strokeWidth={1.5} rx={2}
                  style={{ filter: "drop-shadow(0 0 4px rgba(255,107,0,0.3))" }} />
                <rect x={cx + gap/2 - 4} y={yFor(projRushPct) - 6} width={bw + 8} height={12} fill="transparent" />
                <line x1={cx + gap/2 + 3} x2={cx + gap/2 + bw - 3} y1={yFor(projRushPct) + 3} y2={yFor(projRushPct) + 3} stroke="rgba(255,255,255,0.5)" strokeWidth={1} />
                <line x1={cx + gap/2 + 3} x2={cx + gap/2 + bw - 3} y1={yFor(projRushPct) + 6} y2={yFor(projRushPct) + 6} stroke="rgba(255,255,255,0.5)" strokeWidth={1} />
              </g>
              <text x={cx + bw/2 + gap/2} y={yFor(projRushPct) - 5} textAnchor="middle" fill={colors.brand.accent} fontSize={fontSize.small} fontWeight={fontWeight.black} fontFamily={fonts.display}>{projRushPct.toFixed(0)}</text>
              <text x={cx} y={H - 8} textAnchor="middle" fill={colors.brand.accent} fontSize={fontSize.small} fontWeight={fontWeight.heavy} fontFamily={fonts.display}>'26</text>
            </g>
          );
        })()}
      </svg>
      <div style={{ display: "flex", justifyContent: "center", gap: 14, paddingBottom: 4, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: fontSize.small, color: passColor }}>
          <div style={{ width: 12, height: 8, borderRadius: 2, background: passColor, opacity: 0.7 }} />Pass %
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: fontSize.small, color: rushColor }}>
          <div style={{ width: 12, height: 8, borderRadius: 2, background: rushColor, opacity: 0.7 }} />Rush %
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: fontSize.small, color: passColor }}>
          <div style={{ width: 12, height: 0, borderTop: "1.5px dashed", borderColor: passColor, opacity: 0.5 }} />Lg Avg Pass%
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: fontSize.small, color: rushColor }}>
          <div style={{ width: 12, height: 0, borderTop: "1.5px dashed", borderColor: rushColor, opacity: 0.5 }} />Lg Avg Rush%
        </div>
        {showProjection && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: fontSize.small, color: colors.brand.accent }}>
            <div style={{ width: 12, height: 8, borderRadius: 2, border: `1.5px solid ${colors.brand.accent}`, background: "transparent" }} />'26 (drag to adjust)
          </div>
        )}
      </div>
    </div>
  );
}

// ── Shared: locked strip ──────────────────────────────────────────────────────

function LockedStrip({ label, children, onClick, hoverable }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => hoverable && setHov(true)}
      onMouseLeave={() => hoverable && setHov(false)}
      style={{
        background: hov ? colors.bg.elevated : colors.bg.raised,
        borderRadius: 8,
        border: "1px solid rgba(34,197,94,0.15)",
        padding: "8px 14px",
        marginBottom: 8,
        display: "flex",
        alignItems: "center",
        gap: 8,
        cursor: hoverable ? "pointer" : "default",
        transition: "background 0.15s",
      }}
    >
      <span style={{ color: "#4ade80", fontSize: fontSize.small }}>✓</span>
      <span style={{ fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#A0AEC0", fontFamily: fonts.display, letterSpacing: "1px" }}>{label}</span>
      {children}
      {hoverable && <span style={{ marginLeft: "auto", fontSize: fontSize.small, color: "#A0AEC0", fontStyle: "italic" }}>click to edit</span>}
    </div>
  );
}

// ── Shared: coaching side panels ──────────────────────────────────────────────

function CoachingPanels({ abbrev, teamColor, hcName, ocName, hcColors, ocColors, coachData, ocCareer, isNewHC, isNewOC }) {
  const hasChange = isNewHC || isNewOC;
  if (!hasChange) return null;

  const ocColor = ocColors[ocName] ?? "#9ca3af";
  // OC history panel always shows the OC — ocCareer tracks the OC's stops
  const panelName  = ocName;
  const panelColor = ocColor;
  const changedRole = isNewHC ? "Head Coach" : "Off. Coordinator";

  return (
    <div style={{ marginTop: 14 }}>
      {/* Coaching staff summary */}
      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        <div style={{ flex: 1, padding: "10px 14px", background: `${teamColor}0D`, border: `1px solid ${teamColor}20`, borderRadius: 10 }}>
          <div style={{ fontSize: fontSize.caption, fontWeight: fontWeight.bold, color: teamColor, letterSpacing: "1.5px", marginBottom: 6, fontFamily: fonts.display }}>2026 COACHING STAFF</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: fontSize.body, color: "#E2E8F0" }}>Head Coach</span>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 7, height: 7, borderRadius: 2, background: hcColors[hcName] ?? "#E2E8F0", flexShrink: 0 }} />
                <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.bold, color: hcColors[hcName] ?? "#E2E8F0" }}>{hcName}</span>
                {isNewHC && <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: colors.brand.accent, background: "rgba(255,107,0,0.12)", padding: "1px 6px", borderRadius: 4 }}>NEW</span>}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: fontSize.body, color: "#E2E8F0" }}>Off. Coordinator</span>
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ width: 7, height: 7, borderRadius: 2, background: ocColor, flexShrink: 0 }} />
                <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.bold, color: ocColor }}>{ocName}</span>
                {isNewOC && <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: colors.brand.accent, background: "rgba(255,107,0,0.12)", padding: "1px 6px", borderRadius: 4 }}>NEW</span>}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Prominent OC history panel */}
      <div style={{ padding: "14px 18px", background: `${panelColor}08`, border: `1px solid ${panelColor}20`, borderRadius: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ width: 4, height: 22, borderRadius: 2, background: panelColor }} />
          <div style={{ fontSize: fontSize.sub, fontWeight: fontWeight.heavy, color: panelColor, fontFamily: fonts.display, letterSpacing: "0.5px" }}>
            {panelName.toUpperCase()} — COACHING HISTORY
          </div>
          <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: colors.brand.accent, background: "rgba(255,107,0,0.12)", padding: "2px 8px", borderRadius: 4, marginLeft: "auto" }}>
            NEW {changedRole.toUpperCase()}
          </span>
        </div>
        {ocCareer.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {ocCareer.map((h, i) => {
              const teamMeta = TEAM_META[h.team];
              const rowColor = teamMeta?.primaryColor ?? "#A0AEC0";
              const isCurrent = h.team === abbrev;
              const isHC = h.role === "HC";
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "7px 10px", borderRadius: 6,
                  background: isCurrent ? "rgba(255,107,0,0.06)" : (i % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent"),
                  borderLeft: isCurrent ? `3px solid ${colors.brand.accent}` : `3px solid transparent`,
                }}>
                  <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.bold, color: colors.text.primary, fontFamily: fonts.display, width: 36 }}>{h.year}</span>
                  <span style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 80 }}>
                    <span style={{ width: 6, height: 6, borderRadius: 2, background: rowColor, flexShrink: 0 }} />
                    <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.medium, color: rowColor }}>{teamMeta?.fullName?.split(" ").pop() ?? h.team}</span>
                  </span>
                  <span style={{
                    fontSize: fontSize.nano, fontWeight: fontWeight.heavy, letterSpacing: "0.5px", padding: "2px 6px", borderRadius: 4, flexShrink: 0,
                    background: isHC ? "rgba(251,191,36,0.15)" : "rgba(96,165,250,0.15)",
                    color: isHC ? "#fbbf24" : "#60a5fa",
                  }}>
                    {h.role}
                  </span>
                  {h.role === "OC" && h.hc ? (
                    <span style={{ fontSize: fontSize.caption, color: "#A0AEC0", fontStyle: "italic", whiteSpace: "nowrap" }}>
                      under {h.hc}
                    </span>
                  ) : null}
                  <span style={{
                    fontSize: fontSize.sub, fontWeight: h.totalPlays ? fontWeight.heavy : fontWeight.regular, fontFamily: fonts.display,
                    color: h.totalPlays ? colors.text.primary : "#A0AEC0", marginLeft: "auto", whiteSpace: "nowrap",
                  }}>
                    {h.totalPlays ? `${h.totalPlays} plays` : "—"}
                  </span>
                  {h.totalPlays && i > 0 && ocCareer[i-1].totalPlays ? (
                    <span style={{ fontSize: fontSize.small, fontWeight: fontWeight.medium, color: h.totalPlays >= ocCareer[i-1].totalPlays ? "#4ade80" : "#f87171", whiteSpace: "nowrap" }}>
                      {h.totalPlays >= ocCareer[i-1].totalPlays ? "+" : ""}{h.totalPlays - ocCareer[i-1].totalPlays}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ fontSize: fontSize.body, color: "#CBD5E0" }}>No prior seasons found in dataset.</div>
        )}
      </div>
    </div>
  );
}

// ── LegendDot ─────────────────────────────────────────────────────────────────

function LegendDot({ color, op = 1, glow, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: fontSize.small }}>
      <div style={{ width: 10, height: 10, borderRadius: 2, background: color, opacity: op, boxShadow: glow ? `0 0 6px ${color}80` : "none" }} />
      <span style={{ color: glow ? color : "#A0AEC0", fontWeight: glow ? fontWeight.bold : fontWeight.regular }}>{label}</span>
    </div>
  );
}

// ── AllocationChart ────────────────────────────────────────────────────────────

const AllocationChart = memo(function AllocationChart({ title, subtitle, history, projRush, projTrg, viewMode, lgAvg, showPace }) {
  const showLgAvg = lgAvg && history.length <= 1;
  const h = 360, w = 900;
  const pad = { top: 22, bottom: 44, left: 50, right: 70 };
  const chartW = w - pad.left - pad.right;
  const chartH = h - pad.top - pad.bottom;
  const paceExtras = showPace ? history.filter(d => d.gms >= 6 && d.gms < 17).flatMap(d => [Math.round(d.ratt * 17 / d.gms), Math.round(d.trgs * 17 / d.gms)]) : [];
  const allVals = [...history.map(d => Math.max(d.ratt, d.trgs)), projRush, projTrg, ...(showLgAvg ? [lgAvg.ratt, lgAvg.trgs] : []), ...paceExtras];
  const maxVal = Math.max(...allVals, 10) * 1.18;
  const nBars = history.length + 1 + (showLgAvg ? 1 : 0);
  const groupW = chartW / nBars;
  const barW = groupW * 0.32;
  const gap = groupW * 0.08;
  const yFor = v => pad.top + chartH - (v / maxVal) * chartH;
  const xFor = i => pad.left + i * groupW + groupW / 2;
  const gridVals = [50, 100, 150, 200, 250, 300].filter(v => v < maxVal);
  const projCarryC = "#22d3ee", projTargetC = "#fb923c";
  const histCarryC = "#60a5fa", histTargetC = colors.brand.accent;

  return (
    <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: 10, border: `1px solid ${colors.border.subtle}`, padding: "10px 8px 6px" }}>
      <div style={{ textAlign: "center", marginBottom: 4 }}>
        <div style={{ fontSize: fontSize.sub, fontWeight: fontWeight.black, color: colors.text.primary, fontFamily: fonts.display }}>{title}</div>
        {subtitle && <div style={{ fontSize: fontSize.body, color: "#A0AEC0", marginTop: 1 }}>{subtitle}</div>}
      </div>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block", maxWidth: "100%" }}>
        <defs>
          <filter id="ac-gC" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <filter id="ac-gT" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="4" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <filter id="ac-gL" x="-10%" y="-50%" width="120%" height="200%"><feGaussianBlur stdDeviation="2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        </defs>
        {gridVals.map(v => (
          <g key={v}>
            <line x1={pad.left} x2={w - pad.right} y1={yFor(v)} y2={yFor(v)} stroke="rgba(255,255,255,0.05)" />
            <text x={pad.left - 4} y={yFor(v) + 3} textAnchor="end" fill="#A0AEC0" fontSize={fontSize.body} fontFamily={fonts.display}>{v}</text>
          </g>
        ))}
        {projRush > 0 && <line x1={pad.left} x2={w - pad.right} y1={yFor(projRush)} y2={yFor(projRush)} stroke={projCarryC} strokeWidth={1.5} strokeDasharray="6,4" opacity={0.4} filter="url(#ac-gL)" />}
        {projTrg > 0 && <line x1={pad.left} x2={w - pad.right} y1={yFor(projTrg)} y2={yFor(projTrg)} stroke={projTargetC} strokeWidth={1.5} strokeDasharray="6,4" opacity={0.4} filter="url(#ac-gL)" />}
        {history.map((d, i) => {
          const cx = xFor(i);
          const ghost = showPace && d.gms >= 6 && d.gms < 17;
          const paceRatt = ghost ? Math.round(d.ratt * 17 / d.gms) : d.ratt;
          const paceTrgs = ghost ? Math.round(d.trgs * 17 / d.gms) : d.trgs;
          return (
            <g key={i}>
              {d.ratt > 0 && <g>
                {ghost && paceRatt > d.ratt && <g>
                  <rect x={cx - barW - gap / 2} y={yFor(paceRatt)} width={barW} height={yFor(d.ratt) - yFor(paceRatt)} fill={histCarryC} opacity={0.15} rx={2} />
                  <line x1={cx - barW - gap / 2} x2={cx - gap / 2} y1={yFor(paceRatt)} y2={yFor(paceRatt)} stroke={histCarryC} strokeWidth={1.5} strokeDasharray="3,2" opacity={0.5} />
                  <text x={cx - barW / 2 - gap / 2} y={yFor(paceRatt) - 4} textAnchor="middle" fill={histCarryC} fontSize={fontSize.caption} fontWeight={fontWeight.bold} fontFamily={fonts.display} opacity={0.4}>{paceRatt}</text>
                </g>}
                <rect x={cx - barW - gap / 2} y={yFor(d.ratt)} width={barW} height={yFor(0) - yFor(d.ratt)} fill={histCarryC} opacity={0.45} rx={2} />
                <text x={cx - barW / 2 - gap / 2} y={ghost && paceRatt > d.ratt ? yFor(paceRatt) - 16 : yFor(d.ratt) - 4} textAnchor="middle" fill={histCarryC} fontSize={fontSize.body} fontWeight={fontWeight.bold} fontFamily={fonts.display} opacity={0.65}>{d.ratt}</text>
              </g>}
              {d.trgs > 0 && <g>
                {ghost && paceTrgs > d.trgs && <g>
                  <rect x={cx + gap / 2} y={yFor(paceTrgs)} width={barW} height={yFor(d.trgs) - yFor(paceTrgs)} fill={histTargetC} opacity={0.15} rx={2} />
                  <line x1={cx + gap / 2} x2={cx + barW + gap / 2} y1={yFor(paceTrgs)} y2={yFor(paceTrgs)} stroke={histTargetC} strokeWidth={1.5} strokeDasharray="3,2" opacity={0.5} />
                  <text x={cx + barW / 2 + gap / 2} y={yFor(paceTrgs) - 4} textAnchor="middle" fill={histTargetC} fontSize={fontSize.caption} fontWeight={fontWeight.bold} fontFamily={fonts.display} opacity={0.4}>{paceTrgs}</text>
                </g>}
                <rect x={cx + gap / 2} y={yFor(d.trgs)} width={barW} height={yFor(0) - yFor(d.trgs)} fill={histTargetC} opacity={0.45} rx={2} />
                <text x={cx + barW / 2 + gap / 2} y={ghost && paceTrgs > d.trgs ? yFor(paceTrgs) - 16 : yFor(d.trgs) - 4} textAnchor="middle" fill={histTargetC} fontSize={fontSize.body} fontWeight={fontWeight.bold} fontFamily={fonts.display} opacity={0.65}>{d.trgs}</text>
              </g>}
              {/* Player name label (all tabs) */}
              {d.player && (
                <text x={cx} y={h - 30} textAnchor="middle" fill="#A0AEC0" fontSize={fontSize.caption} fontFamily={fonts.body}>{shortName(d.player)}</text>
              )}
              {/* Year / team label */}
              <text x={cx} y={h - 16} textAnchor="middle" fill="#A0AEC0" fontSize={fontSize.small} fontFamily={fonts.display}>
                {viewMode === "position" ? `'${String(d.year).slice(2)}` : `${d.team} '${String(d.year).slice(2)}`}
              </text>
            </g>
          );
        })}
        {/* League avg context bars (shown for 1-season players) */}
        {showLgAvg && (() => {
          const cx = xFor(history.length);
          const lgC = "#9ca3af";
          return (
            <g>
              {lgAvg.ratt > 0 && <g>
                <rect x={cx - barW - gap / 2} y={yFor(lgAvg.ratt)} width={barW} height={yFor(0) - yFor(lgAvg.ratt)} fill={lgC} opacity={0.25} rx={2} />
                <text x={cx - barW / 2 - gap / 2} y={yFor(lgAvg.ratt) - 4} textAnchor="middle" fill={lgC} fontSize={fontSize.small} fontWeight={fontWeight.bold} fontFamily={fonts.display} opacity={0.5}>{lgAvg.ratt}</text>
              </g>}
              {lgAvg.trgs > 0 && <g>
                <rect x={cx + gap / 2} y={yFor(lgAvg.trgs)} width={barW} height={yFor(0) - yFor(lgAvg.trgs)} fill={lgC} opacity={0.25} rx={2} />
                <text x={cx + barW / 2 + gap / 2} y={yFor(lgAvg.trgs) - 4} textAnchor="middle" fill={lgC} fontSize={fontSize.small} fontWeight={fontWeight.bold} fontFamily={fonts.display} opacity={0.5}>{lgAvg.trgs}</text>
              </g>}
              <text x={cx} y={h - 16} textAnchor="middle" fill={lgC} fontSize={fontSize.small} fontWeight={fontWeight.bold} fontFamily={fonts.display}>Lg Avg</text>
            </g>
          );
        })()}
        {/* Projection bars */}
        {(() => {
          const cx = xFor(history.length + (showLgAvg ? 1 : 0));
          return (
            <g>
              {projRush > 0 && <g filter="url(#ac-gC)">
                <rect x={cx - 26 - gap / 2} y={yFor(projRush)} width={26} height={yFor(0) - yFor(projRush)} fill={projCarryC} rx={2} />
                <text x={cx - 13 - gap / 2} y={yFor(projRush) - 5} textAnchor="middle" fill={projCarryC} fontSize={fontSize.base} fontWeight={fontWeight.black} fontFamily={fonts.display}>{projRush}</text>
              </g>}
              {projTrg > 0 && <g filter="url(#ac-gT)">
                <rect x={cx + gap / 2} y={yFor(projTrg)} width={26} height={yFor(0) - yFor(projTrg)} fill={projTargetC} rx={2} />
                <text x={cx + 13 + gap / 2} y={yFor(projTrg) - 5} textAnchor="middle" fill={projTargetC} fontSize={fontSize.base} fontWeight={fontWeight.black} fontFamily={fonts.display}>{projTrg}</text>
              </g>}
              <text x={cx} y={h - 16} textAnchor="middle" fill={colors.brand.accent} fontSize={fontSize.sub} fontWeight={fontWeight.black} fontFamily={fonts.display}>'26</text>
            </g>
          );
        })()}
      </svg>
      <div style={{ display: "flex", justifyContent: "center", gap: 12, paddingBottom: 2, paddingTop: 2 }}>
        <LegendDot color={histCarryC} op={0.45} label="Carries" />
        <LegendDot color={histTargetC} op={0.45} label="Targets" />
        {showLgAvg && <LegendDot color="#9ca3af" op={0.25} label="Lg Avg" />}
        <LegendDot color={projCarryC} glow label="Proj Carries" />
        <LegendDot color={projTargetC} glow label="Proj Targets" />
        {showPace && history.some(d => d.gms >= 6 && d.gms < 17) && (
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: fontSize.small }}>
            <div style={{ width: 14, height: 10, borderRadius: 2, background: "rgba(255,255,255,0.08)", borderTop: "2px dashed rgba(255,255,255,0.4)" }} />
            <span style={{ color: "#A0AEC0" }}>17g pace</span>
          </div>
        )}
      </div>
    </div>
  );
});

// ── AllocationEditor ────────────────────────────────────────────────────────

// Find the primary (most recent) prior team for a coach in a given role
function findCoachPriorTeam(name, currentAbbrev, role) {
  if (!name || name === "—") return null;
  let bestTeam = null, bestYear = -1;
  Object.entries(COACHING_HISTORY).forEach(([team, seasons]) => {
    if (team === currentAbbrev) return;
    (seasons || []).forEach(s => {
      if (s[role] === name && s.year > bestYear) { bestYear = s.year; bestTeam = team; }
    });
  });
  return bestTeam;
}

// Seed choosers. A team with NO user save opens on the house projection so the
// wizard starts where the rest of the app already shows that team; a saved team
// keeps the legacy buildInit* path exactly as before. buildInitAllocs /
// buildInitRates themselves are NOT modified — projectAllDefaults and the projlab
// golden test read them.
const seedAllocsFor = (houseSeed, abbrev, teamStarters, passAtt, rushAtt, explain) =>
  houseSeed
    ? buildHouseAllocs(abbrev, teamStarters, { passAtt, rushAtt }, explain)
    : buildInitAllocs(abbrev, teamStarters, explain);

const seedRatesFor = (houseSeed, abbrev, allocs, teamStarters, explain) =>
  houseSeed
    ? buildHouseRates(abbrev, allocs, teamStarters, explain)
    : buildInitRates(abbrev, allocs, teamStarters, explain);

const AllocationEditor = forwardRef(function AllocationEditor({ abbrev, teamColor, passAtt, rushAtt, hcName, ocName, onConfirm, teamStarters, initialAllocs, logChange, showPace, setShowPace, onDepthChange, houseSeed = false }, ref) {
  useRenderLoopGuard("AllocationEditor");
  const { openPlayerCard } = usePlayerCard();
  const meta = TEAM_META[abbrev];
  const teamShort = meta.fullName.split(" ").pop();
  const tc = teamColor || meta.primaryColor;
  const hcPriorTeam = useMemo(() => findCoachPriorTeam(hcName, abbrev, "hc"), [hcName, abbrev]);
  const ocPriorTeam = useMemo(() => findCoachPriorTeam(ocName, abbrev, "oc"), [ocName, abbrev]);
  const hcLastName  = shortName(hcName);
  const ocLastName  = shortName(ocName);
  const depthHist = useMemo(() => computeTeamDepthShares(abbrev), [abbrev]);
  const depthLg   = useMemo(() => computeLeagueDepthShares(), []);
  // Advisory only - never mutates allocs. See src/utils/competitionHints.js.
  const competitionHints = useMemo(() => computeCompetitionHints(abbrev, teamStarters), [abbrev, teamStarters]);
  // Advisory only - never mutates allocs. See src/utils/seedQualityHints.js.
  const seedQualityHints = useMemo(() => computeSeedQualityHints(abbrev, teamStarters), [abbrev, teamStarters]);

  const [allocs, setAllocs]     = useState(() => initialAllocs || seedAllocsFor(houseSeed, abbrev, teamStarters, passAtt, rushAtt));
  // Advisory only - never mutates allocs. See src/utils/injuryHints.js.
  const injuryHints = useMemo(() => computeInjuryHints(allocs), [allocs]);
  // Display-only seed provenance (E1 explain collector), computed on the
  // DEFAULT seeds - never passed in a scoring/engine path.
  const seedSlots = useMemo(() => { const ex = {}; seedAllocsFor(houseSeed, abbrev, teamStarters, passAtt, rushAtt, ex); return ex.slots; }, [houseSeed, abbrev, teamStarters, passAtt, rushAtt]);
  // Δ VS DEFAULTS (E3): today's fresh alloc seeds - display-only, never
  // merged, never persisted, never fed to scoring.
  const freshAllocs = useMemo(() => seedAllocsFor(houseSeed, abbrev, teamStarters, passAtt, rushAtt), [houseSeed, abbrev, teamStarters, passAtt, rushAtt]);
  const allocResumed = initialAllocs != null;
  const [showDeltas, setShowDeltas] = useState(false);
  const allocDeltas = useMemo(() => {
    if (!allocResumed) return null;
    const out = [];
    SLOTS.forEach(s => {
      const cur = allocs[s.key], def = freshAllocs[s.key];
      if (!cur || !def) return;
      const label = slotDisplayName(s.key, allocs, teamStarters);
      for (const f of ["rushPct", "tgtPct"]) {
        const d = (cur[f] ?? 0) - (def[f] ?? 0);
        if (Math.abs(d) >= DELTA_THRESHOLDS[f]) out.push({ label, field: DELTA_FIELD_LABELS[f], delta: d, score: Math.abs(d) / DELTA_THRESHOLDS[f] });
      }
      const gd = (cur.stints?.[0]?.games ?? 17) - (def.stints?.[0]?.games ?? 17);
      if (Math.abs(gd) >= DELTA_THRESHOLDS.games) out.push({ label, field: "games", delta: gd, score: Math.abs(gd) / DELTA_THRESHOLDS.games });
    });
    return out;
  }, [allocResumed, allocs, freshAllocs, teamStarters]);
  const [selected, setSelected] = useState("QB1");
  const [viewModeRaw, setViewMode] = useState("player");
  const [lockedSlots, setLockedSlots] = useState(() => {
    // Auto-lock slots with non-default allocations (resuming)
    if (!initialAllocs) return {};
    const defs = seedAllocsFor(houseSeed, abbrev, teamStarters, passAtt, rushAtt);
    const init = {};
    SLOTS.forEach(s => {
      const a = initialAllocs[s.key]; const d = defs[s.key];
      if (a && d && (Math.abs((a.rushPct || 0) - (d.rushPct || 0)) > 0.01 || Math.abs((a.tgtPct || 0) - (d.tgtPct || 0)) > 0.01)) init[s.key] = true;
    });
    return init;
  });
  // Remainder slots don't have a Player tab — fall back to position
  const slotInfoForMode = SLOTS.find(s => s.key === selected);
  const viewMode = (slotInfoForMode?.isRemainder && viewModeRaw === "player") ? "position" : viewModeRaw;

  // ── Depth-chart pill dropdown ──
  const [dropdownSlot, setDropdownSlot] = useState(null);
  const [dropdownCoords, setDropdownCoords] = useState(null);
  const activePillRef = useRef(null);
  const dropdownRef = useRef(null);

  // ── Split-starter (2nd stint) picker ──
  const [splitPickerOpen, setSplitPickerOpen] = useState(false);
  useEffect(() => setSplitPickerOpen(false), [selected]);

  useEffect(() => {
    if (!dropdownSlot) return;
    const onMouseDown = (e) => {
      if (
        activePillRef.current && !activePillRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setDropdownSlot(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setDropdownSlot(null); };
    const close = () => setDropdownSlot(null);
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [dropdownSlot]);

  const dropdownOptions = (() => {
    if (!dropdownSlot) return [];
    const slotPos = dropdownSlot.match(/^(QB|RB|WR|TE)/)?.[1];
    if (!slotPos) return [];
    const team = getStarters(abbrev);
    const opts = [];
    for (const k of Object.keys(team)) {
      const m = k.match(/^(QB|RB|WR|TE)(\d+)$/);
      if (!m || m[1] !== slotPos) continue;
      const entry = team[k];
      if (!entry?.player) continue;
      opts.push({ slotKey: k, idx: parseInt(m[2], 10), player: entry.player });
    }
    opts.sort((a, b) => a.idx - b.idx);
    return opts.slice(0, 12);
  })();

  // logChange (a TeamEditor setState) must be called from the event handler
  // body, NEVER inside a setAllocs updater — React runs updaters during the
  // render phase (twice in StrictMode), which both fired the "Cannot update
  // TeamEditor while rendering AllocationEditor" warning and could double-log.
  // Handlers read `allocs` (the latest committed state) for old values.
  const stintsOf = (key) => allocs[key]?.stints?.length ? allocs[key].stints : [{ id: `${key}#0`, player: null, games: 17 }];

  const updateAlloc = (key, field, val) => {
    const clamped = Math.max(0, Math.min(100, Math.round(val * 10) / 10));
    const old = allocs[key]?.[field] ?? 0;
    if (old !== clamped) logChange?.(key, field === "rushPct" ? "Rush %" : "Tgt %", old, clamped);
    setAllocs(prev => ({ ...prev, [key]: { ...prev[key], [field]: clamped } }));
  };

  const updateStintGames = (key, idx, val) => {
    const clamped = Math.max(1, Math.min(17, Math.round(val)));
    const old = stintsOf(key)[idx]?.games ?? 17;
    if (old !== clamped) logChange?.(key, idx === 0 ? "Games" : `Games (${idx + 1})`, old, clamped);
    setAllocs(prev => {
      const prevStints = prev[key]?.stints?.length ? prev[key].stints : [{ id: `${key}#0`, player: null, games: 17 }];
      const newStints = prevStints.map((st, i) => i === idx ? { ...st, games: clamped } : st);
      return { ...prev, [key]: { ...prev[key], stints: newStints } };
    });
  };

  const updateStintRush = (key, idx, val) => {
    const clamped = Math.max(0, Math.min(90, Math.round(val * 10) / 10));
    const old = stintsOf(key)[idx]?.rushPct ?? allocs[key]?.rushPct ?? 0;
    if (old !== clamped) logChange?.(key, `Rush % (${idx + 1})`, old, clamped);
    setAllocs(prev => {
      const prevStints = prev[key]?.stints?.length ? prev[key].stints : [{ id: `${key}#0`, player: null, games: 17 }];
      const newStints = prevStints.map((st, i) => i === idx ? { ...st, rushPct: clamped } : st);
      return { ...prev, [key]: { ...prev[key], stints: newStints } };
    });
  };

  const addStint = (key, player, sleeperId) => {
    logChange?.(key, "Add stint", "—", player);
    setAllocs(prev => {
      const prevStints = prev[key]?.stints?.length ? prev[key].stints : [{ id: `${key}#0`, player: null, games: 17 }];
      const sumExisting = prevStints.reduce((s, st) => s + (st.games ?? 17), 0);
      const newStint = { id: `${key}#${prevStints.length}`, player, sleeperId, games: Math.max(1, 17 - sumExisting) };
      return { ...prev, [key]: { ...prev[key], stints: [...prevStints, newStint] } };
    });
  };

  const removeStint = (key, idx) => {
    if (idx === 0) return;
    const removed = stintsOf(key)[idx];
    if (!removed) return;
    logChange?.(key, "Remove stint", removed.player ?? "—", "—");
    setAllocs(prev => {
      if (idx === 0) return prev;
      const prevStints = prev[key]?.stints?.length ? prev[key].stints : [{ id: `${key}#0`, player: null, games: 17 }];
      if (!prevStints[idx]) return prev;
      return { ...prev, [key]: { ...prev[key], stints: prevStints.filter((_, i) => i !== idx) } };
    });
  };

  // Volume allocated / team volume — see allocTotals in projlabProjection.js for
  // why the raw 17g-pace sum was the wrong basis and why this is display/gate only.
  // Summing raw made 13 of 32 teams unable to clear the CONFIRM gate at all.
  const { totalRush, totalTgt } = allocTotals(allocs, teamStarters);

  // ONE definition, single-sourced with the band. It was duplicated at the
  // snapshot guard and the CONFIRM button — a snapshot that persists what CONFIRM
  // would refuse is exactly the bug that duplication invites.
  const allocOk = isAllocOk({ totalRush, totalTgt });
  // Advisory only - never blocks lock/confirm. See realismGuards.js.
  const groupEnvelopeFlags = computeGroupEnvelopeFlags(allocs).filter(f => f.flag);

  // Tab-away auto-commit (FN2): only hands back a snapshot when the alloc
  // totals are within the same valid band the CONFIRM button requires -
  // never persists an invalid (way over/under 100%) allocation.
  // Snapshot is a deep clone, NOT the live `allocs` state object. Handing
  // back the same reference means the parent's allocSummary.allocs and this
  // component's own allocs state are literally one shared mutable object
  // straddling both a state slot that's about to survive an unmount and one
  // that's about to be torn down - a known source of pathological React
  // reconciliation/DevTools behavior even without an explicit mutation bug.
  useImperativeHandle(ref, () => ({
    getSnapshot: () => {
      return allocOk ? { allocs: JSON.parse(JSON.stringify(allocs)), totalRush, totalTgt } : null;
    },
  }), [allocs, totalRush, totalTgt, allocOk]);

  const selAlloc  = allocs[selected] ?? { rushPct: 0, tgtPct: 0 };
  const slotInfo  = SLOTS.find(s => s.key === selected);
  const posColor  = colors.pos[slotInfo?.pos || "WR"];
  const currentPlayer = slotDisplayName(selected, allocs, teamStarters);
  const primaryStat   = PRIMARY_STAT[slotInfo?.pos] || "target";
  const projRush = Math.round(rushAtt * selAlloc.rushPct / 100);
  const projTrg  = Math.round(passAtt * selAlloc.tgtPct / 100);

  const displayHistory = useMemo(() =>
    buildAllocHistory(currentPlayer, abbrev, viewMode, selected, hcName, ocName),
    [currentPlayer, abbrev, viewMode, selected, hcName, ocName]);

  const posHistForAvg = useMemo(() =>
    buildAllocHistory(currentPlayer, abbrev, "position", selected, hcName, ocName),
    [currentPlayer, abbrev, selected, hcName, ocName]);
  const fiveYrRushAvg = avgArrF(posHistForAvg.map(h => h.rushPct));
  const fiveYrTgtAvg  = avgArrF(posHistForAvg.map(h => h.tgtPct));

  // Pre-compute HC/OC histories for subtitle player summaries
  const hcHistory = useMemo(() =>
    buildAllocHistory(currentPlayer, abbrev, "hc", selected, hcName, ocName),
    [currentPlayer, abbrev, selected, hcName, ocName]);
  const ocHistory = useMemo(() =>
    buildAllocHistory(currentPlayer, abbrev, "oc", selected, hcName, ocName),
    [currentPlayer, abbrev, selected, hcName, ocName]);
  const hcPlayerSummary = useMemo(() => buildPlayerSummary(hcHistory), [hcHistory]);
  const ocPlayerSummary = useMemo(() => buildPlayerSummary(ocHistory), [ocHistory]);

  // League average for this slot (most recent year with data)
  const lgAvg = useMemo(() => {
    for (let y = 2025; y >= 2021; y--) {
      const avg = leagueAvgForSlot(selected, y);
      if (avg) return avg;
    }
    return null;
  }, [selected]);

  const hcTeamLabel = hcPriorTeam ? ` at ${hcPriorTeam}` : "";
  const ocTeamLabel = ocPriorTeam ? ` at ${ocPriorTeam}` : "";
  const hcSuffix = hcPlayerSummary ? ` (${hcPlayerSummary})` : "";
  const ocSuffix = ocPlayerSummary ? ` (${ocPlayerSummary})` : "";
  const views = [
    { key: "player",   icon: "🏈", label: "PLAYER",      desc: `${currentPlayer} career` },
    { key: "position", icon: "📋", label: "POSITION",    desc: `${abbrev}'s ${selected} history` },
    { key: "hc",       icon: "🎯", label: "HEAD COACH",  desc: `${hcLastName}'s ${selected}${hcTeamLabel}${hcSuffix}` },
    { key: "oc",       icon: "🎓", label: "OFF. COORD.", desc: `${ocLastName}'s ${selected}${ocTeamLabel}${ocSuffix}` },
  ];

  const chartTitle = viewMode === "position" ? `${abbrev}'s ${selected} — Carries & Targets`
    : viewMode === "player" ? `${currentPlayer} — Career Carries & Targets`
    : viewMode === "hc"     ? `${hcLastName}'s ${selected}${hcTeamLabel} — Carries & Targets`
    : `${ocLastName}'s ${selected}${ocTeamLabel} — Carries & Targets`;
  const chartSubtitle = viewMode === "position" ? `Whoever held the ${selected} role at ${teamShort}`
    : viewMode === "player" ? "Follows the player across all teams"
    : viewMode === "hc"     ? hcPlayerSummary || `${hcName}'s ${selected} usage`
    : ocPlayerSummary || `${ocName}'s ${selected} usage`;

  const rushGlow = primaryStat === "rush";
  const tgtGlow  = primaryStat === "target";

  // Shared by the sticky budget meters and the per-slot games/split-starter total.
  const meterColor = (pct) => pct >= 95 && pct <= 105 ? "#22c55e" : (pct >= 80 && pct < 95) || (pct > 105 && pct <= 115) ? "#fbbf24" : "#ef4444";

  const thS = { padding: "7px 6px", textAlign: "center", fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC" };
  const tdS = { padding: "9px 6px", textAlign: "center", fontSize: fontSize.sub };
  const tdD = { padding: "6px 4px", textAlign: "center" };

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: fontSize.body, fontWeight: fontWeight.bold, color: colors.brand.accent, letterSpacing: "2px", fontFamily: fonts.display, marginBottom: 4 }}>
        STEP 3 — CARRY & TARGET ALLOCATION
      </div>
      <h2 style={{ fontFamily: fonts.display, fontSize: fontSize.title, fontWeight: fontWeight.heavy, margin: "0 0 4px", color: colors.text.primary }}>
        How will {meta.fullName} distribute {passAtt} targets and {rushAtt} carries?
      </h2>
      <p style={{ fontSize: fontSize.body, color: "#A0AEC0", margin: "0 0 14px" }}>
        Set each share for a full 17-game season. Adjust games played per player below — shares stay at 17-game pace.
      </p>
      {allocResumed && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <DeltaToggle on={showDeltas} onToggle={() => setShowDeltas(v => !v)} />
          {showDeltas && <DeltaSummaryLine deltas={allocDeltas} />}
        </div>
      )}

      {/* Sticky budget meters */}
      {(() => {
        const rushC = meterColor(totalRush);
        const tgtC  = meterColor(totalTgt);
        return (
          <div style={{ position: "sticky", top: 0, zIndex: 20, background: `${colors.bg.app}f0`, backdropFilter: "blur(10px)", padding: "8px 0 10px", marginBottom: 10, borderBottom: `1px solid ${colors.border.subtle}` }}>
            <div style={{ display: "flex", gap: 16 }}>
              {[
                { label: "Rush Allocation", pct: totalRush, color: rushC, accent: "#60a5fa" },
                { label: "Target Allocation", pct: totalTgt, color: tgtC, accent: colors.brand.accent },
              ].map(m => (
                <div key={m.label} style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
                    <span style={{ fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#E2E8F0", fontFamily: fonts.display }}>{m.label}</span>
                    <span style={{ fontSize: fontSize.base, fontWeight: fontWeight.black, color: m.color, fontFamily: fonts.display }}>{Math.round(m.pct)}%</span>
                  </div>
                  <div style={{ height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${Math.min(m.pct, 120) / 1.2}%`, background: m.color, borderRadius: 4, transition: "width 0.15s, background 0.15s" }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Position-group envelope guards — advisory only, never blocks lock/confirm */}
      {groupEnvelopeFlags.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {groupEnvelopeFlags.map(f => (
            <div key={`${f.side}-${f.group}`} style={{ fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#fbbf24", fontFamily: fonts.display, marginBottom: 2 }}>
              ⚠ {f.group} group {f.sharePct}% of {f.side} — outside 90% of NFL team-seasons since 2018 ({Math.round(f.lo)}-{Math.round(f.hi)}%)
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 14, alignItems: "start" }}>
        {/* LEFT: Budget table + confirm */}
        <div style={{ background: colors.bg.raised, borderRadius: 12, border: `1px solid ${colors.border.subtle}`, overflow: "hidden", position: "sticky", top: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px 8px", borderBottom: `1px solid ${colors.border.default}` }}>
            <span style={{ fontSize: fontSize.base, fontWeight: fontWeight.black, color: colors.text.primary, fontFamily: fonts.display }}>Allocation Budget</span>
            {(() => {
              const setCount = SLOTS.filter(s => lockedSlots[s.key]).length;
              const allSet = setCount === SLOTS.length;
              return <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: allSet ? "#22c55e" : colors.text.faint, fontFamily: fonts.display }}>{allSet ? "✓ all set" : `${setCount}/${SLOTS.length} set`}</span>;
            })()}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${colors.border.default}` }}>
                <th style={{ ...thS, textAlign: "left", width: 118 }}>Player</th>
                <th style={{ ...thS, color: "#60a5fa" }}>Rush%</th>
                <th style={{ ...thS, color: "#60a5fa" }}>Att</th>
                <th style={{ ...thS, color: colors.brand.accent }}>Tgt%</th>
                <th style={{ ...thS, color: colors.brand.accent }}>Tgts</th>
              </tr>
            </thead>
            <tbody>
              {SLOTS.map(s => {
                const a   = allocs[s.key] ?? { rushPct: 0, tgtPct: 0 };
                const att = Math.round(rushAtt * a.rushPct / 100);
                const tgt = Math.round(passAtt * a.tgtPct / 100);
                const isSel = selected === s.key;
                const pc = colors.pos[s.pos];
                const onSlotClick = () => {
                  setSelected(s.key);
                  if (lockedSlots[s.key]) setLockedSlots(prev => { const next = { ...prev }; delete next[s.key]; return next; });
                };
                const rowStyle = {
                  cursor: "pointer",
                  background: isSel ? `${pc}12` : "transparent",
                  borderLeft: isSel ? `3px solid ${pc}` : "3px solid transparent",
                  transition: "all 0.12s",
                  opacity: s.isRemainder ? 0.7 : 1,
                };
                return (
                  <tr key={s.key} onClick={onSlotClick} style={{ ...rowStyle, borderBottom: `1px solid ${colors.border.subtle}`, verticalAlign: "middle" }}>
                    <td style={{ width: 118, textAlign: "left", padding: "6px 8px" }}>
                      <div style={{ fontSize: fontSize.micro, color: pc, letterSpacing: "1px", fontWeight: fontWeight.normal, marginBottom: 2, fontFamily: fonts.display, fontStyle: s.isRemainder ? "italic" : "normal", display: "flex", alignItems: "center", gap: 4 }}>
                        {s.key}
                        {!s.isRemainder && competitionHints[s.key] && (
                          <span
                            title={HINT_KIND_LABELS[competitionHints[s.key].kind] || competitionHints[s.key].kind}
                            style={{ fontSize: fontSize.micro, lineHeight: 1, color: competitionHints[s.key].level === "warn" ? "#fbbf24" : colors.text.faint, cursor: "help" }}
                          >
                            {competitionHints[s.key].level === "warn" ? "⚠" : "ⓘ"}
                          </span>
                        )}
                        {!s.isRemainder && injuryHints[s.key] && (
                          <span
                            title={INJURY_KIND_LABELS[injuryHints[s.key].kind] || injuryHints[s.key].kind}
                            style={{ fontSize: fontSize.micro, lineHeight: 1, color: injuryHints[s.key].level === "warn" ? "#f87171" : colors.text.faint, cursor: "help" }}
                          >
                            ⚕
                          </span>
                        )}
                        {!s.isRemainder && seedQualityHints[s.key] && (
                          <span
                            title={SEED_QUALITY_TITLE}
                            style={{ fontSize: fontSize.micro, lineHeight: 1, color: seedQualityHints[s.key].level === "warn" ? "#fbbf24" : colors.text.faint, cursor: "help" }}
                          >
                            ⏱
                          </span>
                        )}
                      </div>
                      {['QB1','RB1','RB2','WR1','WR2','WR3','TE1','TE2'].includes(s.key) ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (dropdownSlot === s.key) {
                              setDropdownSlot(null);
                              return;
                            }
                            const rect = e.currentTarget.getBoundingClientRect();
                            setDropdownCoords({ top: rect.bottom + 4, left: rect.left });
                            activePillRef.current = e.currentTarget;
                            setDropdownSlot(s.key);
                          }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            marginTop: 2, padding: '3px 8px',
                            border: `0.5px solid ${colors.border.default}`,
                            borderRadius: 6,
                            background: 'rgba(255,255,255,0.04)',
                            fontSize: fontSize.nano,
                            color: colors.text.secondary,
                            fontFamily: fonts.body,
                            cursor: 'pointer',
                          }}
                        >
                          <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.normal }}>{shortName(slotDisplayName(s.key, allocs, teamStarters))}</span>
                          <span style={{ fontSize: fontSize.micro }}>▾</span>
                        </button>
                      ) : (
                        <div style={{ fontSize: fontSize.micro, color: colors.text.faint }}>
                          {shortName(slotDisplayName(s.key, allocs, teamStarters))}
                        </div>
                      )}
                      {(() => {
                        const g = allocs[s.key]?.stints?.[0]?.games;
                        return g != null && g < 17 ? (
                          <div style={{ fontSize: fontSize.nano, color: colors.status.warning, fontWeight: fontWeight.bold, marginTop: 1 }}>
                            {g}g
                          </div>
                        ) : null;
                      })()}
                      {s.isRemainder && (
                        <div style={{ fontSize: fontSize.micro, color: colors.text.faint, fontStyle: "italic" }}>
                          {s.key === "WR4+" ? "all WRs beyond WR1-3, combined" : "all RBs beyond RB1-2, combined"}
                        </div>
                      )}
                      {s.isRemainder && s.key === "WR4+" && depthHist?.wr4PlusTgt != null && (
                        <div style={{ fontSize: fontSize.micro, color: colors.text.faint }}>
                          hist {depthHist.wr4PlusTgt}% tgt (lg {depthLg.wr4PlusTgt}%)
                        </div>
                      )}
                      {s.isRemainder && s.key === "RB3+" && depthHist?.rb3PlusRush != null && (
                        <div style={{ fontSize: fontSize.micro, color: colors.text.faint }}>
                          hist {depthHist.rb3PlusRush}% rush / {depthHist.rb3PlusTgt}% tgt (lg {depthLg.rb3PlusRush}% / {depthLg.rb3PlusTgt}%)
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdS, padding: "4px 6px 6px", fontWeight: fontWeight.bold, color: a.rushPct > 0 ? "#60a5fa" : "#A0AEC0", fontFamily: fonts.display }}>{a.rushPct > 0 ? `${a.rushPct}%` : "—"}</td>
                    <td style={{ ...tdS, padding: "4px 6px 6px", fontWeight: fontWeight.medium, color: att > 0 ? "#E2E8F0" : "#A0AEC0", fontFamily: fonts.display }}>{att || "—"}</td>
                    <td style={{ ...tdS, padding: "4px 6px 6px", fontWeight: fontWeight.bold, color: s.pos === "QB" ? "#A0AEC0" : a.tgtPct > 0 ? colors.brand.accent : "#A0AEC0", fontFamily: fonts.display }}>{s.pos === "QB" ? "—" : a.tgtPct > 0 ? `${a.tgtPct}%` : "—"}</td>
                    <td style={{ ...tdS, padding: "4px 6px 6px", fontWeight: fontWeight.medium, color: s.pos === "QB" ? "#A0AEC0" : tgt > 0 ? "#E2E8F0" : "#A0AEC0", fontFamily: fonts.display }}>{s.pos === "QB" ? "—" : tgt || "—"}</td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: `2px solid ${colors.border.default}`, background: "rgba(255,255,255,0.03)" }}>
                <td style={{ padding: "8px 8px", fontWeight: fontWeight.black, color: colors.text.primary, fontFamily: fonts.display, fontSize: fontSize.base }}>TOTAL</td>
                <td style={{ ...tdS, fontSize: fontSize.base, fontWeight: fontWeight.black, fontFamily: fonts.display, color: totalRush > 100 ? "#ef4444" : totalRush > 90 ? "#22c55e" : "#60a5fa" }}>{Math.round(totalRush)}%</td>
                <td style={{ ...tdS, fontSize: fontSize.base, fontWeight: fontWeight.heavy, color: colors.text.primary, fontFamily: fonts.display }}>{Math.round(rushAtt * totalRush / 100)}</td>
                <td style={{ ...tdS, fontSize: fontSize.base, fontWeight: fontWeight.black, fontFamily: fonts.display, color: totalTgt > 100 ? "#ef4444" : totalTgt > 90 ? "#22c55e" : colors.brand.accent }}>{Math.round(totalTgt)}%</td>
                <td style={{ ...tdS, fontSize: fontSize.base, fontWeight: fontWeight.heavy, color: colors.text.primary, fontFamily: fonts.display }}>{Math.round(passAtt * totalTgt / 100)}</td>
              </tr>
            </tbody>
          </table>
          <div style={{ padding: "6px 12px", borderTop: `1px solid ${colors.border.subtle}`, fontSize: fontSize.small, color: "#A0AEC0" }}>
            Unallocated → depth, backups, throwaways
          </div>
          <div style={{ padding: "8px 12px" }}>
            {(() => {
              const allSet = SLOTS.every(s => lockedSlots[s.key]);
              const ready = allocOk && allSet;
              return (
                <>
                  <button
                    onClick={allocOk ? () => onConfirm({ allocs, totalRush, totalTgt }) : undefined}
                    disabled={!allocOk}
                    style={{ width: "100%", padding: "11px 0", background: ready ? "linear-gradient(135deg, #FF6B00, #E55A00)" : allocOk ? "rgba(255,107,0,0.15)" : colors.bg.elevated, border: ready ? "none" : allocOk ? "1px solid rgba(255,107,0,0.3)" : `1px solid ${colors.border.default}`, borderRadius: 10, color: ready ? "#fff" : allocOk ? "#FF6B00" : colors.text.faint, fontFamily: fonts.display, fontSize: fontSize.base, fontWeight: fontWeight.heavy, letterSpacing: "1.5px", cursor: allocOk ? "pointer" : "not-allowed", boxShadow: ready ? "0 4px 12px rgba(255,107,0,0.3)" : "none", transition: "all 0.2s" }}>
                    CONFIRM → PLAYER EFFICIENCY
                  </button>
                  {!allocOk && (
                    <div style={{ fontSize: fontSize.caption, color: colors.status.warning, textAlign: "center", marginTop: 6 }}>
                      Allocate closer to 100% before continuing
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>

        {/* RIGHT: Detail panel */}
        <div style={{ background: colors.bg.raised, borderRadius: 12, border: `1px solid ${colors.border.subtle}`, padding: 20 }}>
          {/* Slot header */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: fontSize.title, fontWeight: fontWeight.black, color: posColor, fontFamily: fonts.display, background: `${posColor}15`, borderRadius: 6, padding: "4px 14px" }}>{selected}</span>
            <div>
              <div style={{ fontSize: fontSize.heading, fontWeight: fontWeight.heavy, color: colors.text.primary, display: "flex", alignItems: "center", gap: 6 }}>
                {slotInfo?.isRemainder ? `${slotInfo.pos} Depth` : currentPlayer}
                {/* Named starters only — remainder buckets have no single player */}
                {!slotInfo?.isRemainder && (() => {
                  // resolvePoolEntry matches sleeper_id BEFORE name, so the id has to
                  // come from the stint too — a house name paired with the depth
                  // chart's id still resolves to the depth chart's player.
                  const ident = resolveStintIdentity(allocs?.[selected]?.stints?.[0], selected, teamStarters);
                  const poolEntry = resolvePoolEntry({ player: ident.player, pos: slotInfo?.pos, sleeperId: ident.sleeperId });
                  if (!poolEntry) return null;
                  return (
                    <span
                      title="Game logs"
                      onClick={e => { e.stopPropagation(); openPlayerCard(poolEntry.name, {}, "team_editor"); }}
                      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 16, height: 16, borderRadius: 4, background: "rgba(255,255,255,0.08)", color: "#64748b", fontSize: 10, fontWeight: 700, lineHeight: 1, cursor: "pointer", transition: "background 0.15s ease, color 0.15s ease" }}
                      onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,107,0,0.2)"; e.currentTarget.style.color = "#FF6B00"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "#64748b"; }}
                    >≡</span>
                  );
                })()}
              </div>
              <div style={{ fontSize: fontSize.body, color: "#A0AEC0" }}>{slotInfo?.isRemainder ? `Backups, depth, throwaways · ${meta.fullName}` : `2025 starter · ${meta.fullName}`}</div>
            </div>
          </div>

          {/* 4-view toggle */}
          {(() => {
            // Remainder slots: skip Player tab (no single player), show Position/HC/OC
            const availViews = slotInfo?.isRemainder ? views.filter(v => v.key !== "player") : views;
            return (
              <>
                <div style={{ display: "flex", gap: 3, marginBottom: 6, padding: "3px", background: "rgba(255,255,255,0.03)", borderRadius: 8, border: `1px solid ${colors.border.subtle}` }}>
                  {availViews.map(v => (
                    <button key={v.key} onClick={() => setViewMode(v.key)} style={{
                      flex: 1, padding: "8px 6px", borderRadius: 6, cursor: "pointer",
                      fontSize: fontSize.small, fontWeight: fontWeight.heavy, fontFamily: fonts.display, letterSpacing: "0.3px",
                      background: viewMode === v.key ? "rgba(255,107,0,0.12)" : "transparent",
                      border: viewMode === v.key ? "1px solid rgba(255,107,0,0.3)" : "1px solid transparent",
                      color: viewMode === v.key ? colors.brand.accent : "#A0AEC0",
                    }}>{v.icon} {v.label}</button>
                  ))}
                </div>
                <div style={{ fontSize: fontSize.body, color: "#A0AEC0", marginBottom: 12, fontStyle: "italic" }}>
                  {views.find(v => v.key === viewMode)?.desc}
                </div>
              </>
            );
          })()}

          {/* Competition hint — advisory only, never auto-applied */}
          {(() => {
            const hint = !slotInfo?.isRemainder ? competitionHints[selected] : null;
            if (!hint) return null;
            const isWarn = hint.level === "warn";
            return (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 14, padding: "10px 14px", borderRadius: 8,
                background: isWarn ? "rgba(251,191,36,0.06)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${isWarn ? "rgba(251,191,36,0.3)" : colors.border.subtle}`,
                borderLeft: `3px solid ${isWarn ? "#fbbf24" : colors.text.faint}`,
              }}>
                <span style={{ fontSize: fontSize.body, color: isWarn ? "#fbbf24" : colors.text.faint, flexShrink: 0, lineHeight: 1.3 }}>
                  {isWarn ? "⚠" : "ⓘ"}
                </span>
                <span style={{ fontSize: fontSize.small, color: isWarn ? "#fde68a" : colors.text.faint, lineHeight: 1.4 }}>
                  {hintCopy(hint)}
                </span>
              </div>
            );
          })()}

          {/* Injury redistribution hint — advisory only, never auto-applied */}
          {(() => {
            const hint = !slotInfo?.isRemainder ? injuryHints[selected] : null;
            if (!hint) return null;
            const isWarn = hint.level === "warn";
            const accent = isWarn ? "#f87171" : colors.text.faint;
            return (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 14, padding: "10px 14px", borderRadius: 8,
                background: isWarn ? "rgba(248,113,113,0.06)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${isWarn ? "rgba(248,113,113,0.3)" : colors.border.subtle}`,
                borderLeft: `3px solid ${accent}`,
              }}>
                <span style={{ fontSize: fontSize.body, color: accent, flexShrink: 0, lineHeight: 1.3 }}>⚕</span>
                <span style={{ fontSize: fontSize.small, color: isWarn ? "#fecaca" : colors.text.faint, lineHeight: 1.4 }}>
                  <span style={{ display: "block", fontWeight: fontWeight.bold, letterSpacing: "0.5px", marginBottom: 2, color: accent }}>
                    INJURY REDISTRIBUTION — advisory only
                  </span>
                  {injuryHintCopy(hint, selAlloc?.stints?.[0]?.player, GP_ROLE_MEANS[selected] ?? 17)}
                </span>
              </div>
            );
          })()}

          {/* Short-season seed hint — advisory only, never auto-applied */}
          {(() => {
            const hint = !slotInfo?.isRemainder ? seedQualityHints[selected] : null;
            if (!hint) return null;
            const isWarn = hint.level === "warn";
            const accent = isWarn ? "#fbbf24" : colors.text.faint;
            const player = selAlloc?.stints?.[0]?.player ?? teamStarters?.[selected]?.player;
            return (
              <div style={{
                display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 14, padding: "10px 14px", borderRadius: 8,
                background: isWarn ? "rgba(251,191,36,0.06)" : "rgba(255,255,255,0.03)",
                border: `1px solid ${isWarn ? "rgba(251,191,36,0.3)" : colors.border.subtle}`,
                borderLeft: `3px solid ${accent}`,
              }}>
                <span style={{ fontSize: fontSize.body, color: accent, flexShrink: 0, lineHeight: 1.3 }}>⏱</span>
                <span style={{ fontSize: fontSize.small, color: isWarn ? "#fde68a" : colors.text.faint, lineHeight: 1.4 }}>
                  <span style={{ display: "block", fontWeight: fontWeight.bold, letterSpacing: "0.5px", marginBottom: 2, color: accent }}>
                    SHORT-SEASON SEED — advisory only
                  </span>
                  {seedQualityHintCopy(hint, player)}
                </span>
              </div>
            );
          })()}

          {/* Sliders */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            {/* Rush share */}
            <div style={{
              padding: "10px 14px", borderRadius: 10, transition: "all 0.2s",
              background: rushGlow ? "rgba(34,211,238,0.08)" : "rgba(96,165,250,0.04)",
              border: rushGlow ? "1.5px solid rgba(34,211,238,0.35)" : "1px solid rgba(96,165,250,0.12)",
              boxShadow: rushGlow ? "0 0 16px rgba(34,211,238,0.15), inset 0 0 12px rgba(34,211,238,0.06)" : "none",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.bold, color: rushGlow ? "#22d3ee" : "#60a5fa", letterSpacing: "0.5px" }}>
                  RUSH SHARE {rushGlow && <span style={{ fontSize: fontSize.micro, color: "#22d3ee", opacity: 0.7 }}>● PRIMARY</span>}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {showDeltas && <DefChip user={selAlloc.rushPct} def={freshAllocs[selected]?.rushPct} threshold={DELTA_THRESHOLDS.rushPct} />}
                  <input type="number" value={selAlloc.rushPct} step={0.5}
                    onChange={e => updateAlloc(selected, "rushPct", Number(e.target.value) || 0)}
                    style={{ width: 62, background: colors.bg.elevated, border: `1px solid ${rushGlow ? "rgba(34,211,238,0.4)" : "rgba(96,165,250,0.3)"}`, borderRadius: 5, color: rushGlow ? "#22d3ee" : "#60a5fa", fontSize: fontSize.heading, fontWeight: fontWeight.heavy, textAlign: "center", padding: "4px", fontFamily: fonts.display, outline: "none" }}
                  />
                </span>
              </div>
              <input type="range" min={0} max={90} step={0.5} value={selAlloc.rushPct}
                onChange={e => updateAlloc(selected, "rushPct", Number(e.target.value))}
                style={{ width: "100%", accentColor: rushGlow ? "#22d3ee" : "#60a5fa", height: 5, cursor: "pointer" }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: fontSize.body, color: "#A0AEC0", marginTop: 3 }}>
                <span>= <strong style={{ color: "#22d3ee" }}>{projRush}</strong> carries</span>
                {!slotInfo?.isRemainder && <span>5yr avg: {fiveYrRushAvg}%</span>}
              </div>
              {(() => { const c = allocSeedCaption(seedSlots?.[selected]?.rush, "rush", selected); const sq = seedQualityHints[selected]; const suffix = c && sq?.stat === "rush" ? ` · '25 injury-shortened (${sq.gms} g)` : ""; return c ? <div style={{ fontSize: fontSize.caption, color: colors.text.faint, marginTop: 2 }}>{c}{suffix}</div> : null; })()}
            </div>

            {/* Target share — hidden for QB */}
            {slotInfo?.pos !== "QB" && (
            <div style={{
              padding: "10px 14px", borderRadius: 10, transition: "all 0.2s",
              background: tgtGlow ? "rgba(251,146,60,0.08)" : "rgba(255,149,0,0.04)",
              border: tgtGlow ? "1.5px solid rgba(251,146,60,0.35)" : "1px solid rgba(255,149,0,0.12)",
              boxShadow: tgtGlow ? "0 0 16px rgba(251,146,60,0.15), inset 0 0 12px rgba(251,146,60,0.06)" : "none",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.bold, color: tgtGlow ? "#fb923c" : colors.brand.accent, letterSpacing: "0.5px" }}>
                  TARGET SHARE {tgtGlow && <span style={{ fontSize: fontSize.micro, color: "#fb923c", opacity: 0.7 }}>● PRIMARY</span>}
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {showDeltas && <DefChip user={selAlloc.tgtPct} def={freshAllocs[selected]?.tgtPct} threshold={DELTA_THRESHOLDS.tgtPct} />}
                  <input type="number" value={selAlloc.tgtPct} step={0.5}
                    onChange={e => updateAlloc(selected, "tgtPct", Number(e.target.value) || 0)}
                    style={{ width: 62, background: colors.bg.elevated, border: `1px solid ${tgtGlow ? "rgba(251,146,60,0.4)" : "rgba(255,149,0,0.3)"}`, borderRadius: 5, color: tgtGlow ? "#fb923c" : colors.brand.accent, fontSize: fontSize.heading, fontWeight: fontWeight.heavy, textAlign: "center", padding: "4px", fontFamily: fonts.display, outline: "none" }}
                  />
                </span>
              </div>
              <input type="range" min={0} max={55} step={0.5} value={selAlloc.tgtPct}
                onChange={e => updateAlloc(selected, "tgtPct", Number(e.target.value))}
                style={{ width: "100%", accentColor: tgtGlow ? "#fb923c" : colors.brand.accent, height: 5, cursor: "pointer" }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: fontSize.body, color: "#A0AEC0", marginTop: 3 }}>
                <span>= <strong style={{ color: "#fb923c" }}>{projTrg}</strong> targets</span>
                {!slotInfo?.isRemainder && <span>5yr avg: {fiveYrTgtAvg}%</span>}
              </div>
              {(() => { const c = allocSeedCaption(seedSlots?.[selected]?.tgt, "tgt", selected); const sq = seedQualityHints[selected]; const suffix = c && sq?.stat === "tgt" ? ` · '25 injury-shortened (${sq.gms} g)` : ""; return c ? <div style={{ fontSize: fontSize.caption, color: colors.text.faint, marginTop: 2 }}>{c}{suffix}</div> : null; })()}
            </div>
            )}
          </div>

          {/* Games played / split-starter (stint) controls */}
          {(() => {
            const stints = selAlloc.stints?.length ? selAlloc.stints : [{ id: `${selected}#0`, player: null, games: 17 }];
            const totalGames = stints.reduce((s, st) => s + (st.games ?? 17), 0);
            const totalColor = meterColor(totalGames / 17 * 100);
            const slotPos = selected.match(/^(QB|RB|WR|TE)/)?.[1];
            const splitOptions = (() => {
              if (!slotPos) return [];
              const team = getStarters(abbrev);
              const usedNames = new Set(
                stints.map((st, i) => (i === 0 ? (st.player ?? teamStarters?.[selected]?.player) : st.player)).filter(Boolean)
              );
              const opts = [];
              for (const k of Object.keys(team)) {
                const m = k.match(/^(QB|RB|WR|TE)(\d+)$/);
                if (!m || m[1] !== slotPos) continue;
                const entry = team[k];
                if (!entry?.player || usedNames.has(entry.player)) continue;
                opts.push({ slotKey: k, idx: parseInt(m[2], 10), player: entry.player, sleeperId: entry.sleeper_id });
              }
              opts.sort((a, b) => a.idx - b.idx);
              return opts.slice(0, 12);
            })();
            return (
              <div style={{ padding: "8px 14px", borderRadius: 10, background: "rgba(255,255,255,0.03)", border: `1px solid ${colors.border.subtle}`, marginBottom: 14 }}>
                <div style={{ fontSize: fontSize.body, fontWeight: fontWeight.bold, color: "#A0AEC0", letterSpacing: "0.5px", marginBottom: 8 }}>GAMES / SPLIT STARTERS</div>
                {seedSlots?.[selected]?.games?.code === "rolemean" && (
                  <div style={{ fontSize: fontSize.caption, color: colors.text.faint, marginTop: -6, marginBottom: 8 }}>
                    default {seedSlots[selected].games.mean} = role-mean games (2018-25)
                  </div>
                )}
                {stints.map((st, idx) => {
                  const g = st.games ?? 17;
                  const label = idx === 0 ? (st.player ?? teamStarters?.[selected]?.player ?? selected) : (st.player ?? "?");
                  return (
                    <div key={st.id ?? idx} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: idx < stints.length - 1 ? 6 : 0 }}>
                      <span style={{ flex: 1, fontSize: fontSize.body, color: colors.text.primary, fontFamily: fonts.body, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
                      <button
                        onClick={() => updateStintGames(selected, idx, g - 1)}
                        disabled={g <= 1}
                        style={{ width: 26, height: 26, borderRadius: 6, border: `1px solid ${colors.border.default}`, background: colors.bg.elevated, color: colors.text.primary, fontSize: fontSize.body, fontWeight: fontWeight.bold, cursor: g <= 1 ? "not-allowed" : "pointer", opacity: g <= 1 ? 0.4 : 1 }}
                      >−</button>
                      <input type="number" min={1} max={17} value={g}
                        onChange={e => updateStintGames(selected, idx, Number(e.target.value) || 17)}
                        style={{ width: 46, background: colors.bg.elevated, border: `1px solid ${colors.border.default}`, borderRadius: 5, color: colors.text.primary, fontSize: fontSize.body, fontWeight: fontWeight.heavy, textAlign: "center", padding: "3px", fontFamily: fonts.display, outline: "none" }}
                      />
                      {showDeltas && idx === 0 && <DefChip user={g} def={freshAllocs[selected]?.stints?.[0]?.games ?? 17} threshold={DELTA_THRESHOLDS.games} />}
                      <button
                        onClick={() => updateStintGames(selected, idx, g + 1)}
                        disabled={g >= 17}
                        style={{ width: 26, height: 26, borderRadius: 6, border: `1px solid ${colors.border.default}`, background: colors.bg.elevated, color: colors.text.primary, fontSize: fontSize.body, fontWeight: fontWeight.bold, cursor: g >= 17 ? "not-allowed" : "pointer", opacity: g >= 17 ? 0.4 : 1 }}
                      >+</button>
                      {idx > 0 && (
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <span style={{ fontSize: fontSize.caption, color: "#60a5fa", fontFamily: fonts.display, fontWeight: fontWeight.bold }}>Rush %</span>
                          <input type="number" min={0} max={90} step={0.5}
                            value={st.rushPct ?? selAlloc.rushPct ?? 0}
                            onChange={e => updateStintRush(selected, idx, Number(e.target.value) || 0)}
                            style={{ width: 50, background: colors.bg.elevated, border: `1px solid ${colors.border.default}`, borderRadius: 5, color: "#60a5fa", fontSize: fontSize.body, fontWeight: fontWeight.heavy, textAlign: "center", padding: "3px", fontFamily: fonts.display, outline: "none" }}
                          />
                        </div>
                      )}
                      {idx > 0 && (
                        <button onClick={() => removeStint(selected, idx)}
                          style={{ padding: "3px 8px", borderRadius: 6, border: `1px solid ${colors.border.default}`, background: "transparent", color: "#ef4444", fontSize: fontSize.caption, fontWeight: fontWeight.bold, cursor: "pointer", fontFamily: fonts.display }}
                        >✕ Remove</button>
                      )}
                    </div>
                  );
                })}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                  <span style={{ fontSize: fontSize.caption, fontWeight: fontWeight.bold, color: totalColor, fontFamily: fonts.display }}>
                    {stints.map(st => st.games ?? 17).join(" + ")} = {totalGames} / 17
                  </span>
                  <div style={{ position: "relative" }}>
                    <button onClick={() => setSplitPickerOpen(p => !p)}
                      style={{ padding: "4px 10px", borderRadius: 6, border: `1px solid ${colors.border.default}`, background: "rgba(255,255,255,0.04)", color: colors.brand.accent, fontSize: fontSize.caption, fontWeight: fontWeight.bold, cursor: "pointer", fontFamily: fonts.display }}
                    >+ Split season / add starter</button>
                    {splitPickerOpen && (
                      <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, minWidth: 200, maxHeight: 240, overflowY: "auto", background: colors.bg.raised, border: `0.5px solid ${colors.border.default}`, borderRadius: 6, boxShadow: "0 8px 24px rgba(0,0,0,0.5)", zIndex: 50, padding: 4 }}>
                        {splitOptions.length === 0 ? (
                          <div style={{ padding: "6px 10px", fontSize: fontSize.small, color: colors.text.faint }}>No eligible players</div>
                        ) : splitOptions.map(opt => (
                          <button key={opt.slotKey}
                            onClick={() => { addStint(selected, opt.player, opt.sleeperId); setSplitPickerOpen(false); }}
                            style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", borderRadius: 4, border: "none", cursor: "pointer", background: "transparent", fontSize: fontSize.body, color: colors.text.primary, fontFamily: fonts.body }}
                          >
                            {opt.player}
                            <span style={{ color: colors.text.faint, fontSize: fontSize.caption, marginLeft: 6 }}>({opt.slotKey})</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* 17G Pace toggle + Chart */}
          {displayHistory.length >= 1 && (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: fontSize.caption, color: showPace ? "#22c55e" : "#A0AEC0", fontFamily: fonts.display, fontWeight: fontWeight.bold }}>
                <span>17G Pace</span>
                <div onClick={() => setShowPace(p => !p)} style={{ width: 32, height: 16, borderRadius: 8, background: showPace ? "#22c55e" : "rgba(255,255,255,0.1)", border: `1px solid ${showPace ? "#22c55e" : "rgba(255,255,255,0.15)"}`, position: "relative", cursor: "pointer", transition: "all 0.2s" }}>
                  <div style={{ width: 12, height: 12, borderRadius: 6, background: showPace ? "#fff" : "#718096", position: "absolute", top: 1, left: showPace ? 17 : 1, transition: "all 0.2s" }} />
                </div>
              </label>
            </div>
          )}
          {displayHistory.length >= 1 ? (
            <AllocationChart title={chartTitle} subtitle={chartSubtitle} history={displayHistory} projRush={projRush} projTrg={projTrg} viewMode={viewMode} lgAvg={lgAvg} showPace={showPace} />
          ) : (
            <div style={{ padding: 24, textAlign: "center", background: "rgba(255,255,255,0.02)", borderRadius: 10, border: `1px solid ${colors.border.subtle}` }}>
              <div style={{ fontSize: fontSize.body, color: "#A0AEC0" }}>
                {viewMode === "player"
                  ? <><strong style={{ color: colors.text.primary }}>{currentPlayer}</strong> — Rookie, no NFL history. Use <strong>Position</strong>, <strong>HC</strong>, and <strong>OC</strong> tabs for context.</>
                  : "No history available for this view."}
              </div>
            </div>
          )}

          {/* Historical table */}
          {displayHistory.length >= 1 && (
            <div style={{ marginTop: 12, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fontSize.body }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${colors.border.default}` }}>
                    <th style={thS}>Year</th>
                    {viewMode !== "position" && <th style={thS}>Team</th>}
                    <th style={thS}>G</th>
                    <th style={thS}>Team Rush</th>
                    <th style={{ ...thS, color: "#60a5fa" }}>Carries</th>
                    <th style={{ ...thS, color: "#60a5fa" }}>Rush%</th>
                    <th style={thS}>Team Pass</th>
                    <th style={{ ...thS, color: colors.brand.accent }}>Targets</th>
                    <th style={{ ...thS, color: colors.brand.accent }}>Tgt%</th>
                    <th style={thS}>Player</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: "rgba(255,107,0,0.05)", borderLeft: `3px solid ${colors.brand.accent}`, borderBottom: "2px dashed rgba(255,107,0,0.25)" }}>
                    <td style={tdD}>
                      <span style={{ fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display }}>2026</span>
                      <span style={{ fontSize: fontSize.nano, fontWeight: fontWeight.heavy, color: colors.brand.accent, background: "rgba(255,107,0,0.12)", padding: "1px 4px", borderRadius: 3, marginLeft: 4, letterSpacing: "0.5px", verticalAlign: "middle" }}>PROJ</span>
                    </td>
                    {viewMode !== "position" && <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: tc, fontSize: fontSize.small }}>{abbrev}</span></td>}
                    <td style={tdD}><span style={{ color: colors.brand.accent }}>17</span></td>
                    <td style={tdD}><span style={{ color: "#E2E8F0" }}>{rushAtt}</span></td>
                    <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: "#22d3ee", fontFamily: fonts.display }}>{projRush}</span></td>
                    <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: "#22d3ee" }}>{selAlloc.rushPct}%</span></td>
                    <td style={tdD}><span style={{ color: "#E2E8F0" }}>{passAtt}</span></td>
                    <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: "#fb923c", fontFamily: fonts.display }}>{projTrg}</span></td>
                    <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: "#fb923c" }}>{selAlloc.tgtPct}%</span></td>
                    <td style={{ ...tdD, textAlign: "left" }}><span style={{ color: "#E2E8F0", fontSize: fontSize.small }}>{currentPlayer}</span></td>
                  </tr>
                  {displayHistory.slice().reverse().map((h, i) => {
                    const paced = showPace && h.gms >= 6 && h.gms < 17;
                    const pf = paced ? 17 / h.gms : 1;
                    const pRatt = paced ? Math.round(h.ratt * pf) : h.ratt;
                    const pTrgs = paced ? Math.round(h.trgs * pf) : h.trgs;
                    const pRushPct = paced ? Math.round(h.rushPct * pf * 10) / 10 : h.rushPct;
                    const pTgtPct  = paced ? Math.round(h.tgtPct  * pf * 10) / 10 : h.tgtPct;
                    const pC = paced ? "#a3e635" : undefined;
                    return (
                    <tr key={i} style={{ borderBottom: `1px solid ${colors.border.subtle}` }}>
                      <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: colors.text.primary, fontFamily: fonts.display }}>{h.year}</span></td>
                      {viewMode !== "position" && <td style={tdD}><span style={{ fontWeight: fontWeight.medium, color: h.team === abbrev ? tc : "#E2E8F0", fontSize: fontSize.small }}>{h.team}</span></td>}
                      <td style={tdD}><span style={{ color: h.gms < 15 ? "#fbbf24" : "#E2E8F0" }}>{h.gms}{paced && "*"}</span></td>
                      <td style={tdD}><span style={{ color: "#E2E8F0" }}>{h.teamRA}</span></td>
                      <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: paced ? pC : "#60a5fa", fontFamily: fonts.display }}>{pRatt}</span></td>
                      <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: paced ? pC : "#60a5fa" }}>{pRushPct}%</span></td>
                      <td style={tdD}><span style={{ color: "#E2E8F0" }}>{h.teamPA}</span></td>
                      <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: paced ? pC : colors.brand.accent, fontFamily: fonts.display }}>{pTrgs}</span></td>
                      <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: paced ? pC : colors.brand.accent }}>{pTgtPct}%</span></td>
                      <td style={{ ...tdD, textAlign: "left" }}><span style={{ color: "#E2E8F0", fontSize: fontSize.small }}>{h.player}</span></td>
                    </tr>
                    );
                  })}
                  <tr style={{ background: "rgba(255,107,0,0.06)", borderTop: `2px solid ${colors.border.default}` }}>
                    <td style={tdD}><span style={{ fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display }}>AVG</span></td>
                    {viewMode !== "position" && <td style={tdD} />}
                    <td style={tdD}><span style={{ color: "#A0AEC0" }}>{avgArrF(displayHistory.map(h => h.gms))}</span></td>
                    <td style={tdD}><span style={{ color: "#E2E8F0" }}>{avgArr(displayHistory.map(h => h.teamRA))}</span></td>
                    <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: "#60a5fa", fontFamily: fonts.display }}>{Math.round(avgArr(displayHistory.map(h => h.ratt)))}</span></td>
                    <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: "#60a5fa" }}>{avgArrF(displayHistory.map(h => h.rushPct))}%</span></td>
                    <td style={tdD}><span style={{ color: "#E2E8F0" }}>{avgArr(displayHistory.map(h => h.teamPA))}</span></td>
                    <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: colors.brand.accent, fontFamily: fonts.display }}>{Math.round(avgArr(displayHistory.map(h => h.trgs)))}</span></td>
                    <td style={tdD}><span style={{ fontWeight: fontWeight.bold, color: colors.brand.accent }}>{avgArrF(displayHistory.map(h => h.tgtPct))}%</span></td>
                    <td style={tdD} />
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* SET PROJECTION button + Next link */}
          {(() => {
            const curIdx = SLOTS.findIndex(s => s.key === selected);
            const nextSlot = curIdx < SLOTS.length - 1 ? SLOTS[curIdx + 1] : null;
            const nextPlayer = nextSlot ? slotDisplayName(nextSlot.key, allocs, teamStarters) : null;
            const isLocked = !!lockedSlots[selected];
            return (
              <div style={{ textAlign: "center", padding: "14px 0 2px" }}>
                {nextSlot && !isLocked && (
                  <div style={{ fontSize: fontSize.caption, color: "#A0AEC0", marginBottom: 6, cursor: "pointer" }}
                    onClick={() => { setLockedSlots(prev => ({ ...prev, [selected]: true })); setSelected(nextSlot.key); }}>
                    Next: {shortName(nextPlayer)} ({nextSlot.key}) ↓
                  </div>
                )}
                <button
                  onClick={() => {
                    setLockedSlots(prev => ({ ...prev, [selected]: true }));
                    if (nextSlot) setSelected(nextSlot.key);
                  }}
                  style={{ padding: "9px 28px", background: isLocked ? "rgba(34,197,94,0.15)" : "linear-gradient(135deg, #FF6B00, #E55A00)", border: isLocked ? "1px solid rgba(34,197,94,0.3)" : "none", borderRadius: 8, color: isLocked ? "#22c55e" : "#fff", fontFamily: fonts.display, fontSize: fontSize.body, fontWeight: fontWeight.heavy, letterSpacing: "1px", cursor: "pointer", transition: "all 0.2s" }}>
                  {isLocked ? "✓ SET" : "SET PROJECTION ✓"}
                </button>
              </div>
            );
          })()}
        </div>
      </div>
      {dropdownSlot && createPortal(
        <div ref={dropdownRef} onClick={e => e.stopPropagation()}
          style={{
            position: "fixed",
            top: dropdownCoords?.top ?? 0,
            left: dropdownCoords?.left ?? 0,
            minWidth: 220,
            maxWidth: 280,
            maxHeight: 280,
            overflowY: "auto",
            background: colors.bg.raised,
            border: `0.5px solid ${colors.border.default}`,
            borderRadius: 6,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
            zIndex: 1000,
            padding: 4,
            fontFamily: fonts.body,
          }}>
          {dropdownOptions.length === 0 ? (
            <div style={{ padding: "6px 10px", fontSize: fontSize.small, color: colors.text.faint }}>No players</div>
          ) : dropdownOptions.map((opt) => {
            const isCurrent = opt.slotKey === dropdownSlot;
            return (
              <button key={opt.slotKey}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isCurrent) { setDropdownSlot(null); return; }
                  const slotPos = dropdownSlot.match(/^(QB|RB|WR|TE)/)?.[1];
                  const ok = writeSlotOverride(abbrev, slotPos, dropdownSlot, opt.player);
                  if (ok) {
                    onDepthChange?.();
                  } else {
                    console.warn(`[DepthOverride] Failed to write: ${opt.player} → ${dropdownSlot} on ${abbrev}`);
                  }
                  setDropdownSlot(null);
                }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "6px 10px", borderRadius: 4,
                  border: "none",
                  cursor: isCurrent ? "default" : "pointer",
                  opacity: isCurrent ? 0.4 : 1,
                  background: "transparent",
                  fontSize: fontSize.body,
                  color: colors.text.primary,
                  fontFamily: fonts.body,
                }}>
                <span style={{ fontWeight: fontWeight.normal }}>{opt.player}</span>
                <span style={{ color: colors.text.faint, fontSize: fontSize.caption, marginLeft: 6 }}>(currently {opt.slotKey})</span>
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
});

// ── Efficiency helpers ────────────────────────────────────────────────────────

const LEAGUE_COMPS = {
  "QB-tdPct":    [{ player:"Lamar Jackson",val:7.2},{player:"Josh Allen",val:6.8},{player:"Joe Burrow",val:6.5},{player:"Patrick Mahomes",val:5.9},{player:"Jayden Daniels",val:5.4},{player:"Baker Mayfield",val:5.2}],
  "QB-intPct":   [{ player:"Jayden Daniels",val:0.8},{player:"Patrick Mahomes",val:1.0},{player:"Joe Burrow",val:1.1},{player:"Lamar Jackson",val:1.4},{player:"Josh Allen",val:1.8},{player:"Baker Mayfield",val:2.0},{player:"Dak Prescott",val:2.3}],
  "QB-ypa":      [{ player:"Lamar Jackson",val:8.9},{player:"Joe Burrow",val:8.2},{player:"Jayden Daniels",val:7.8},{player:"Josh Allen",val:7.6},{player:"Patrick Mahomes",val:7.4},{player:"Baker Mayfield",val:7.1},{player:"Dak Prescott",val:6.8}],
  "QB-compPct":  [{ player:"Joe Burrow",val:70.2},{player:"Jayden Daniels",val:69.1},{player:"Baker Mayfield",val:68.5},{player:"Patrick Mahomes",val:67.2},{player:"Lamar Jackson",val:66.7},{player:"Josh Allen",val:63.8},{player:"C.J. Stroud",val:62.4}],
  "QB-rushYPC":  [{ player:"Lamar Jackson",val:5.6},{player:"Josh Allen",val:4.8},{player:"Jayden Daniels",val:4.5},{player:"Patrick Mahomes",val:4.1},{player:"Baker Mayfield",val:3.8},{player:"Joe Burrow",val:3.2},{player:"Dak Prescott",val:2.8}],
  "QB-rushTDPct":[{ player:"Lamar Jackson",val:8.2},{player:"Josh Allen",val:7.1},{player:"Jayden Daniels",val:5.8},{player:"Patrick Mahomes",val:3.5},{player:"Baker Mayfield",val:3.0},{player:"Joe Burrow",val:1.8}],
  "RB-ypc":      [{ player:"Saquon Barkley",val:5.8},{player:"De'Von Achane",val:5.4},{player:"Bijan Robinson",val:5.1},{player:"Derrick Henry",val:4.9},{player:"Josh Jacobs",val:4.6},{player:"Breece Hall",val:4.3},{player:"Kyren Williams",val:4.2}],
  "RB-rushTDPct":[{ player:"Derrick Henry",val:7.1},{player:"Saquon Barkley",val:6.8},{player:"Bijan Robinson",val:5.9},{player:"Josh Jacobs",val:5.1},{player:"De'Von Achane",val:4.2},{player:"Breece Hall",val:3.8}],
  "RB-catchPct": [{ player:"De'Von Achane",val:82.1},{player:"Bijan Robinson",val:72.0},{player:"Saquon Barkley",val:70.8},{player:"Breece Hall",val:67.3},{player:"Josh Jacobs",val:65.4},{player:"Derrick Henry",val:58.2}],
  "RB-ypr":      [{ player:"De'Von Achane",val:9.2},{player:"Saquon Barkley",val:8.1},{player:"Bijan Robinson",val:7.4},{player:"Breece Hall",val:7.0},{player:"Josh Jacobs",val:5.8},{player:"Derrick Henry",val:5.2}],
  "RB-recTDPct": [{ player:"De'Von Achane",val:3.9},{player:"Saquon Barkley",val:3.5},{player:"Bijan Robinson",val:2.8},{player:"Breece Hall",val:2.1}],
  "WR-catchPct": [{ player:"Puka Nacua",val:72.4},{player:"Ja'Marr Chase",val:71.8},{player:"Nico Collins",val:70.2},{player:"CeeDee Lamb",val:66.2},{player:"A.J. Brown",val:65.1},{player:"Malik Nabers",val:64.8},{player:"Tyreek Hill",val:63.5}],
  "WR-ypr":      [{ player:"Tyreek Hill",val:14.2},{player:"Ja'Marr Chase",val:13.8},{player:"Nico Collins",val:13.1},{player:"A.J. Brown",val:12.8},{player:"CeeDee Lamb",val:12.4},{player:"Puka Nacua",val:11.5}],
  "WR-recTDPct": [{ player:"Ja'Marr Chase",val:11.2},{player:"A.J. Brown",val:10.5},{player:"CeeDee Lamb",val:8.8},{player:"Nico Collins",val:8.1},{player:"Malik Nabers",val:7.5}],
  "TE-catchPct": [{ player:"Travis Kelce",val:78.2},{player:"Trey McBride",val:74.1},{player:"Dallas Goedert",val:72.0},{player:"George Kittle",val:71.3},{player:"T.J. Hockenson",val:69.8},{player:"Mark Andrews",val:67.4},{player:"David Njoku",val:66.5}],
  "TE-ypr":      [{ player:"George Kittle",val:13.1},{player:"Mark Andrews",val:12.8},{player:"David Njoku",val:11.5},{player:"T.J. Hockenson",val:11.2},{player:"Trey McBride",val:10.9},{player:"Travis Kelce",val:10.4},{player:"Dallas Goedert",val:10.1}],
  "TE-recTDPct": [{ player:"Mark Andrews",val:9.8},{player:"George Kittle",val:8.9},{player:"T.J. Hockenson",val:7.5},{player:"Travis Kelce",val:7.2},{player:"Trey McBride",val:6.1}],
};

const TEAM_BENCHMARKS = { leagueAvg: 1580, leagueMax: 2050, leagueMin: 1180, top5Threshold: 1920 };

const VOLUME_CONTEXT_KEYS = {
  compPct:    h => h.comp != null && h.passAtt != null ? `${h.comp}/${h.passAtt}` : null,
  ypa:        h => h.passAtt != null ? `${h.passAtt} att` : null,
  tdPct:      h => h.passTD  != null ? `${h.passTD} TD`  : null,
  intPct:     h => h.int     != null ? `${h.int} INT`     : null,
  rushYPC:    h => h.rushAtt != null ? `${h.rushAtt} att` : null,
  ypc:        h => h.rushAtt != null ? `${h.rushAtt} att` : null,
  rushTDPct:  h => h.rushTD  != null ? `${h.rushTD} TD`  : null,
  catchPct:   h => h.rec != null && h.targets != null ? `${h.rec}/${h.targets}` : null,
  ypr:        h => h.rec     != null ? `${h.rec} rec`     : null,
  recTDPct:   h => h.recTD   != null ? `${h.recTD} TD`   : null,
};

// ── Efficiency chart helpers ──────────────────────────────────────────────────

function LgDot({ color, op = 1, glow, label }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: fontSize.micro }}>
      <div style={{ width: 8, height: 8, borderRadius: 2, background: color, opacity: glow ? 1 : op, boxShadow: glow ? `0 0 5px ${color}80` : "none" }} />
      <span style={{ color: glow ? color : colors.text.faint }}>{label}</span>
    </div>
  );
}

const RATE_METRICS = new Set(["ypc", "ypa", "ypr", "catchPct", "compPct", "intPct", "rushTDPct", "recTDPct", "tdPct", "rushYPC"]);

const MetricHistoryChart = memo(function MetricHistoryChart({ title, history, metricKey, projValue, color, teamAbbrev, projContext, showPace }) {
  const vals = [...history].reverse();
  if (!vals.length) return null;
  const ctxFn = VOLUME_CONTEXT_KEYS[metricKey];
  const isRate = RATE_METRICS.has(metricKey);
  const h = 280; const pad = { top: 16, bottom: 46, left: 36, right: 16 };
  const w = 400; const chartW = w - pad.left - pad.right; const chartH = h - pad.top - pad.bottom;
  // Include pace values in axis range for counting stats
  const paceExtras = showPace && !isRate ? vals.filter(v => v.gms >= 6 && v.gms < 17).map(v => Math.round(+(v[metricKey] || 0) * 17 / v.gms)) : [];
  const allV = [...vals.map(v => +(v[metricKey] || 0)), projValue, ...paceExtras];
  const maxV = Math.max(...allV) * 1.18; const minV = Math.min(...allV) * 0.82;
  const range = maxV - minV || 1;
  const n = vals.length + 1;
  const bw = Math.min(26, (chartW / n) * 0.5);
  const xFor = i => pad.left + (i + 0.5) * (chartW / n);
  const yFor = v => pad.top + chartH - ((v - minV) / range) * chartH;
  const hasGhosts = showPace && !isRate && vals.some(v => v.gms >= 6 && v.gms < 17);

  return (
    <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: 8, border: `1px solid ${colors.border.subtle}`, padding: "8px 6px 4px" }}>
      <div style={{ fontSize: fontSize.small, fontWeight: fontWeight.black, color: colors.text.primary, fontFamily: fonts.display, textAlign: "center", marginBottom: 4 }}>{title}</div>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "block", maxWidth: "100%" }}>
        <defs>
          <filter id="eff-metGlow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
          <filter id="eff-metLine" x="-10%" y="-50%" width="120%" height="200%"><feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        </defs>
        <line x1={pad.left} x2={w - pad.right} y1={yFor(projValue)} y2={yFor(projValue)}
          stroke="#22d3ee" strokeWidth={2} strokeDasharray="6,4" opacity={0.7} filter="url(#eff-metLine)" />
        {vals.map((v, i) => {
          const val = +(v[metricKey] || 0);
          const ctx = ctxFn ? ctxFn(v) : null;
          const partial = showPace && v.gms >= 6 && v.gms < 17;
          const ghost = partial && !isRate;
          const paceVal = ghost ? Math.round(val * 17 / v.gms) : val;
          const pCtx = partial && isRate && ctx ? ctx.replace(/\d+/, m => Math.round(parseInt(m) * 17 / v.gms)) : null;
          return (
            <g key={i}>
              {/* Ghost bar extension for counting stats */}
              {ghost && paceVal > val && <g>
                <rect x={xFor(i) - bw / 2} y={yFor(paceVal)} width={bw} height={yFor(val) - yFor(paceVal)} fill={color} opacity={0.15} rx={2} />
                <line x1={xFor(i) - bw / 2} x2={xFor(i) + bw / 2} y1={yFor(paceVal)} y2={yFor(paceVal)} stroke={color} strokeWidth={1.5} strokeDasharray="3,2" opacity={0.5} />
                <text x={xFor(i)} y={yFor(paceVal) - 4} textAnchor="middle" fill={color} fontSize={fontSize.nano} fontWeight={fontWeight.bold} fontFamily={fonts.display} opacity={0.45}>{paceVal}</text>
              </g>}
              {/* Solid bar for actual value */}
              <rect x={xFor(i) - bw / 2} y={yFor(val)} width={bw} height={yFor(minV) - yFor(val)} fill={color} opacity={0.4} rx={2} />
              {/* Dashed outline for rate metrics in partial seasons */}
              {partial && isRate && <rect x={xFor(i) - bw / 2} y={yFor(val)} width={bw} height={yFor(minV) - yFor(val)} fill="none" stroke={color} strokeWidth={1} strokeDasharray="3,2" opacity={0.6} rx={2} />}
              <text x={xFor(i)} y={ghost && paceVal > val ? yFor(paceVal) - 15 : yFor(val) - 5} textAnchor="middle" fill={color} fontSize={fontSize.caption} fontWeight={fontWeight.bold} fontFamily={fonts.display} opacity={0.85}>{val}</text>
              {partial && isRate && pCtx ? <text x={xFor(i)} y={yFor(val) - 17} textAnchor="middle" fill="#a3e635" fontSize={fontSize.nano} fontFamily={fonts.body}>{pCtx}*</text>
                : ctx && <text x={xFor(i)} y={ghost && paceVal > val ? yFor(paceVal) - 26 : yFor(val) - 17} textAnchor="middle" fill="#A0AEC0" fontSize={fontSize.nano} fontFamily={fonts.body}>{ctx}</text>}
              <text x={xFor(i)} y={h - 22} textAnchor="middle" fill="#A0AEC0" fontSize={fontSize.caption} fontFamily={fonts.display}>
                {v.team && v.team !== teamAbbrev ? v.team : `'${String(v.year).slice(2)}`}
              </text>
              {v.gms < 15 ? <text x={xFor(i)} y={h - 10} textAnchor="middle" fill="#fbbf24" fontSize={fontSize.nano} fontFamily={fonts.display}>{v.gms}g{partial && "*"}</text>
                : <text x={xFor(i)} y={h - 10} textAnchor="middle" fill="#A0AEC0" fontSize={fontSize.nano} fontFamily={fonts.display} opacity={0.7}>{v.gms}g</text>}
            </g>
          );
        })}
        <g filter="url(#eff-metGlow)">
          <rect x={xFor(vals.length) - bw / 2} y={yFor(projValue)} width={bw} height={yFor(minV) - yFor(projValue)} fill="#22d3ee" rx={2} />
          <text x={xFor(vals.length)} y={yFor(projValue) - 5} textAnchor="middle" fill="#22d3ee" fontSize={fontSize.small} fontWeight={fontWeight.black} fontFamily={fonts.display}>{projValue}</text>
          {projContext && <text x={xFor(vals.length)} y={yFor(projValue) - 17} textAnchor="middle" fill="#22d3ee" fontSize={fontSize.nano} fontFamily={fonts.body} opacity={0.8}>{projContext}</text>}
        </g>
        <text x={xFor(vals.length)} y={h - 22} textAnchor="middle" fill={colors.brand.accent} fontSize={fontSize.caption} fontWeight={fontWeight.heavy} fontFamily={fonts.display}>'26</text>
      </svg>
      <div style={{ display: "flex", justifyContent: "center", gap: 8, paddingTop: 2, paddingBottom: 2 }}>
        <LgDot color={color} op={0.4} label="Historical" />
        <LgDot color="#22d3ee" glow label="Projection" />
        {showPace && vals.some(v => v.gms >= 6 && v.gms < 17) && (
          <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: fontSize.micro }}>
            <div style={{ width: 8, height: 8, borderRadius: 2, border: "1.5px dashed rgba(163,230,53,0.6)", background: hasGhosts ? `${color}25` : "transparent" }} />
            <span style={{ color: "#a3e635" }}>17g pace</span>
          </div>
        )}
      </div>
    </div>
  );
});

const LeagueCompChart = memo(function LeagueCompChart({ title, data, projValue, color, expandedPlayerName }) {
  if (!data) return null;
  const allData = [...data];
  if (expandedPlayerName && !allData.find(d => d.self && d.selfName === expandedPlayerName)) {
    allData.push({ player: expandedPlayerName, val: projValue, self: true, selfName: expandedPlayerName });
  }
  const sorted = [...allData].sort((a, b) => b.val - a.val);
  const maxV = Math.max(...sorted.map(d => d.val), projValue) * 1.08;
  return (
    <div style={{ background: "rgba(255,255,255,0.02)", borderRadius: 8, border: `1px solid ${colors.border.subtle}`, padding: "8px 10px" }}>
      <div style={{ fontSize: fontSize.small, fontWeight: fontWeight.heavy, color: colors.text.primary, fontFamily: fonts.display, marginBottom: 6 }}>{title}</div>
      <div style={{ minHeight: 200 }}>
      {sorted.map((d, i) => {
        const isExpanded = d.self && d.selfName === expandedPlayerName;
        const isSelf = d.self;
        const w = (d.val / maxV) * 100;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
            <div style={{ width: 95, fontSize: fontSize.caption, fontWeight: isSelf ? fontWeight.heavy : fontWeight.normal, color: isSelf ? color : "#A0AEC0", textAlign: "right", fontFamily: isSelf ? fonts.display : fonts.body, flexShrink: 0 }}>{d.player}</div>
            <div style={{ flex: 1, height: 18, background: "rgba(255,255,255,0.03)", borderRadius: 3, position: "relative", overflow: "hidden" }}>
              <div style={{ height: "100%", borderRadius: 3, width: `${w}%`, background: isSelf ? color : "rgba(255,255,255,0.12)", opacity: isExpanded ? 0.8 : isSelf ? 0.5 : 0.3, boxShadow: isExpanded ? `0 0 8px ${color}40` : "none" }} />
              {isExpanded && <div style={{ position: "absolute", top: 0, bottom: 0, left: `${(projValue / maxV) * 100}%`, width: 2, background: "#22d3ee", boxShadow: "0 0 6px #22d3ee80" }} />}
            </div>
            <div style={{ width: 34, fontSize: fontSize.caption, fontWeight: isSelf ? fontWeight.heavy : fontWeight.normal, color: isSelf ? color : "#A0AEC0", fontFamily: fonts.display, textAlign: "right" }}>{d.val}</div>
          </div>
        );
      })}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 4, justifyContent: "flex-end" }}>
        <div style={{ width: 2, height: 8, background: "#22d3ee", boxShadow: "0 0 4px #22d3ee80" }} />
        <span style={{ fontSize: fontSize.nano, color: "#22d3ee" }}>Your '26 projection</span>
      </div>
    </div>
  );
});

function BenchMark({ pct: p, label, color: bColor }) {
  return (
    <div style={{ position: "absolute", left: `${p}%`, top: 0, transform: "translateX(-50%)" }}>
      <div style={{ width: 1, height: 24, background: bColor, opacity: 0.35 }} />
      <div style={{ fontSize: fontSize.micro, color: bColor, textAlign: "center", whiteSpace: "nowrap", marginTop: 0 }}>{label}</div>
    </div>
  );
}

function SanityBar({ teamTotal, allPts, scoring = FULL_PPR_LEGACY }) {
  const { leagueMin, leagueAvg, leagueMax, top5Threshold } = TEAM_BENCHMARKS;
  const lo = leagueMin - 100; const hi = leagueMax + 200;
  const pct = v => Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));
  const isHot = teamTotal > top5Threshold; const isInsane = teamTotal > leagueMax;
  const barColor = isInsane ? "#ef4444" : isHot ? "#fbbf24" : "#22c55e";
  const label = isInsane ? "ABOVE ANY 2025 TEAM" : isHot ? "TOP 5 TERRITORY" : "REASONABLE";

  // Position segments for the stacked bar
  const posGroups = [
    { pos: "QB", slots: ["QB1"] },
    { pos: "RB", slots: ["RB1", "RB2", "RB3+"] },
    { pos: "WR", slots: ["WR1", "WR2", "WR3", "WR4+"] },
    { pos: "TE", slots: ["TE1", "TE2"] },
  ];
  const segments = posGroups.map(g => ({
    pos: g.pos,
    color: colors.pos[g.pos],
    pts: g.slots.reduce((sum, k) => sum + (allPts?.[k] || 0), 0),
  })).filter(s => s.pts > 0);
  const segTotal = segments.reduce((s, g) => s + g.pts, 0) || 1;

  return (
    <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: `${colors.bg.app}f0`, borderTop: `1px solid ${colors.border.default}`, backdropFilter: "blur(10px)", zIndex: 100 }}>
      {/* Segmented position bar */}
      {allPts && (
        <div style={{ display: "flex", height: 6 }}>
          {segments.map((s, i) => (
            <div key={i} style={{ width: `${(s.pts / segTotal) * 100}%`, background: s.color, opacity: 0.55, transition: "width 0.2s" }} />
          ))}
        </div>
      )}
      <div style={{ padding: "5px 20px 6px" }}>
        <div style={{ maxWidth: 1400, margin: "0 auto", display: "flex", alignItems: "center", gap: 12 }}>
          {/* Segment labels */}
          {allPts && (
            <div style={{ display: "flex", gap: 10, flexShrink: 0 }}>
              {segments.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: s.color, opacity: 0.7 }} />
                  <span style={{ fontSize: fontSize.caption, fontWeight: fontWeight.bold, color: s.color, fontFamily: fonts.display }}>{s.pts.toFixed(0)}</span>
                  <span style={{ fontSize: fontSize.nano, color: colors.text.faint }}>{s.pos}</span>
                </div>
              ))}
            </div>
          )}
          {/* Benchmark range bar */}
          <div style={{ flex: 1, position: "relative", height: 20 }}>
            <div style={{ position: "absolute", top: 6, left: 0, right: 0, height: 8, background: "rgba(255,255,255,0.06)", borderRadius: 4 }}>
              <div style={{ position: "absolute", left: `${pct(leagueAvg - 150)}%`, width: `${pct(top5Threshold) - pct(leagueAvg - 150)}%`, height: "100%", background: "rgba(34,197,94,0.2)", borderRadius: 4 }} />
              <div style={{ position: "absolute", left: `${pct(top5Threshold)}%`, width: `${pct(leagueMax) - pct(top5Threshold)}%`, height: "100%", background: "rgba(251,191,36,0.2)" }} />
              <div style={{ position: "absolute", left: `${pct(leagueMax)}%`, right: 0, height: "100%", background: "rgba(239,68,68,0.15)", borderRadius: "0 4px 4px 0" }} />
            </div>
            <BenchMark pct={pct(leagueAvg)} label="Lg Avg" color={colors.text.faint} />
            <BenchMark pct={pct(leagueMax)} label="Lg Max" color="#fbbf24" />
            <div style={{ position: "absolute", left: `${pct(teamTotal)}%`, top: 0, width: 3, height: 20, background: barColor, borderRadius: 2, boxShadow: `0 0 8px ${barColor}80`, transform: "translateX(-1.5px)", transition: "left 0.15s" }} />
          </div>
          {/* Total + label */}
          <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: fontSize.nano, fontWeight: fontWeight.bold, color: colors.text.faint, letterSpacing: "1px" }}>TEAM TOTAL</div>
              <div style={{ fontSize: fontSize.heading, fontWeight: fontWeight.black, color: barColor, fontFamily: fonts.display, lineHeight: 1 }}>{teamTotal.toFixed(0)} <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.medium }}>{pprLabel(scoring)}</span></div>
            </div>
            <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: barColor, fontFamily: fonts.display, background: `${barColor}15`, padding: "2px 7px", borderRadius: 3, whiteSpace: "nowrap" }}>{label}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const effThS = { padding: "3px 3px", textAlign: "center", fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC" };
const effThC = { padding: "3px 3px", textAlign: "center", fontSize: fontSize.small, fontWeight: fontWeight.medium, color: "#F7FAFC" };
const effThR = (active, c) => ({ padding: "3px 3px", textAlign: "center", fontSize: fontSize.small, fontWeight: active ? fontWeight.heavy : fontWeight.bold, color: active ? c : "#A0AEC0", background: active ? `${c}08` : "transparent" });
const effTdS = { padding: "3px 3px", textAlign: "center", fontSize: fontSize.small };
const effTdC = { padding: "3px 3px", textAlign: "center", fontSize: fontSize.small, color: "#E2E8F0" };
const effTdR = (active, c) => ({ padding: "3px 3px", textAlign: "center", fontSize: fontSize.small, fontWeight: active ? fontWeight.heavy : fontWeight.medium, color: active ? c : "#E2E8F0", fontFamily: fonts.display, background: active ? `${c}08` : "transparent" });

function RateSlider({ label, value, onChange, min, max, step = 0.1, unit = "", avg5yr, isActive, onClick, color, isTouched, onTouch, defChip = null }) {
  const dragRef = useRef({ startVal: value, moved: false });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 6, cursor: "pointer", background: isActive ? `${color}10` : "transparent", border: isActive ? `1.5px solid ${color}30` : "1px solid transparent", boxShadow: isActive ? `0 0 10px ${color}12` : "none", transition: "all 0.12s" }}>
      <span style={{ fontSize: fontSize.micro, color: isTouched ? "#22c55e" : "#4A5568", lineHeight: 1, flexShrink: 0 }}>{isTouched ? "●" : "○"}</span>
      <div onClick={() => { onTouch?.(); onClick?.(); }} style={{ width: 64, fontSize: fontSize.body, fontWeight: fontWeight.bold, color: isActive ? color : "#E2E8F0", letterSpacing: "0.3px" }}>{label}</div>
      <input type="range" min={min} max={max} step={step} value={value}
        onPointerDown={e => { onClick?.(); onTouch?.(); dragRef.current = { startVal: value, moved: false, x: e.clientX }; }}
        onPointerMove={e => { if (!dragRef.current.moved && Math.abs(e.clientX - dragRef.current.x) > 3) dragRef.current.moved = true; }}
        onChange={e => { if (dragRef.current.moved) onChange(Number(e.target.value)); }}
        onPointerUp={() => { if (!dragRef.current.moved) onClick?.(); }}
        style={{ flex: 1, accentColor: color, height: 4, cursor: "grab" }} />
      <input type="number" value={value} step={step}
        onFocus={() => onTouch?.()}
        onChange={e => { onTouch?.(); onChange(Math.max(min, Math.min(max, Number(e.target.value) || 0))); }}
        style={{ width: 44, background: isActive ? `${color}12` : colors.bg.elevated, border: `1px solid ${isActive ? `${color}40` : colors.border.subtle}`, borderRadius: 4, color: isActive ? color : "#E2E8F0", fontSize: fontSize.body, fontWeight: fontWeight.heavy, textAlign: "center", padding: "2px", fontFamily: fonts.display, outline: "none" }} />
      {defChip}
      <span onClick={() => { onTouch?.(); onClick?.(); }} style={{ fontSize: fontSize.small, color: "#A0AEC0", width: 34, textAlign: "right", cursor: "pointer" }}>avg {avg5yr}</span>
    </div>
  );
}

function EPlayerCard({ slot, vol, rates, defaultRates, seedTags, showDeltas, onRateChange, activeMetric, setActiveMetric, isExpanded, onToggle, posColor, isTE, abbrev, derivedPass, isLocked, onLockIn, showPace, onDepthChange, scoring = FULL_PPR_LEGACY }) {
  const pos = slot.startsWith("QB") ? "QB" : slot.startsWith("RB") ? "RB" : "REC";
  const pts = calcPPR(slot, vol, rates, derivedPass, scoring);
  const theColor = isTE ? colors.pos.TE : posColor;
  const [hovered, setHovered] = useState(false);
  const [touched, setTouched] = useState(() => {
    // Auto-mark sliders as touched if rate differs from default (resuming)
    const init = new Set();
    if (defaultRates) {
      Object.keys(rates).forEach(k => {
        if (parseFloat((rates[k] ?? 0).toFixed(2)) !== parseFloat((defaultRates[k] ?? 0).toFixed(2))) init.add(k);
      });
    }
    return init;
  });
  const markTouched = useCallback((key) => setTouched(prev => prev.has(key) ? prev : new Set(prev).add(key)), []);

  // Detect if any rate differs from default (compare to 2 decimal places)
  const isEdited = defaultRates != null && Object.keys(rates).some(k => {
    const cur = parseFloat((rates[k] ?? 0).toFixed(2));
    const def = parseFloat((defaultRates[k] ?? 0).toFixed(2));
    return cur !== def;
  });
  const metrics = pos === "QB" ? [
    { key: "intPct",    label: "INT%",     min: 0.5, max: 6,    unit: "%" },
    { key: "rushYPC",   label: "RUSH YPC", min: 0,   max: 9.5 },
    { key: "rushTDPct", label: "RUSH TD%", min: 0.5, max: 18,   unit: "%" },
  ] : pos === "RB" ? [
    { key: "ypc",       label: "YPC",      min: 3,   max: 9 },
    { key: "rushTDPct", label: "RUSH TD%", min: 1,   max: 12.5, unit: "%" },
    { key: "catchPct",  label: "CATCH%",   min: 55,  max: 95,   unit: "%" },
    { key: "ypr",       label: "Y/REC",    min: 4,   max: 18 },
    { key: "recTDPct",  label: "REC TD%",  min: 0,   max: 18,   unit: "%" },
  ] : [
    { key: "catchPct", label: "CATCH%",  min: 45, max: 95, unit: "%" },
    { key: "ypr",      label: "Y/REC",   min: 7,  max: 26 },
    { key: "recTDPct", label: "REC TD%", min: 1,  max: 18, unit: "%" },
  ];
  const hist = buildPlayerHistForSlot(vol.player, pos, vol?.sleeperId ?? vol?.sleeper_id);
  const avgH = arr => arr.filter(v => v != null).length ? (arr.filter(v => v != null).reduce((a, b) => a + b, 0) / arr.filter(v => v != null).length).toFixed(1) : "—";

  const statLine = (() => {
    if (pos === "QB") {
      const pa = vol.passAtt || 0;
      const c = derivedPass?.comp ?? 0;
      const y = derivedPass?.passYds ?? 0;
      const td = derivedPass?.passTD ?? 0;
      const i = Math.round(pa * (rates.intPct || 0) / 100);
      const ry = Math.round((vol.rushAtt || 0) * (rates.rushYPC || 0));
      const rtd = Math.round((vol.rushAtt || 0) * (rates.rushTDPct || 0) / 100);
      return `${c}/${pa} · ${y.toLocaleString()} yds · ${td} TD · ${i} INT | ${ry} rush yds · ${rtd} rush TD`;
    }
    if (pos === "RB") {
      const ry = Math.round((vol.rushAtt || 0) * (rates.ypc || 0));
      const rtd = Math.round((vol.rushAtt || 0) * (rates.rushTDPct || 0) / 100);
      const rec = Math.round((vol.targets || 0) * (rates.catchPct || 0) / 100);
      const recy = Math.round(rec * (rates.ypr || 0));
      const rectd = Math.round(rec * (rates.recTDPct || 0) / 100);
      return `${vol.rushAtt} att · ${ry} yds · ${rtd} TD | ${rec} rec · ${recy} yds · ${rectd} TD`;
    }
    const rec = Math.round((vol.targets || 0) * (rates.catchPct || 0) / 100);
    const recy = Math.round(rec * (rates.ypr || 0));
    const rectd = Math.round(rec * (rates.recTDPct || 0) / 100);
    return `${rec}/${vol.targets} · ${recy.toLocaleString()} yds · ${rectd} TD`;
  })();

  return (
    <div style={{ background: colors.bg.raised, borderRadius: 8, border: `1px solid ${isExpanded ? `${theColor}30` : colors.border.subtle}`, overflow: "hidden" }}>
      <div onClick={onToggle} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", cursor: "pointer", background: isExpanded ? `${theColor}06` : hovered ? "rgba(255,255,255,0.04)" : "transparent", transition: "background 0.12s" }}>
        <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.black, color: theColor, fontFamily: fonts.display, background: `${theColor}15`, borderRadius: 4, padding: "2px 7px", minWidth: 28, textAlign: "center" }}>{slot}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ fontSize: fontSize.sub, fontWeight: fontWeight.bold, color: colors.text.primary }}>{vol.player}</div>
            {isLocked ? (
              <span style={{ fontSize: fontSize.micro, color: "#22c55e", fontWeight: fontWeight.black, flexShrink: 0 }} title="Set">✓</span>
            ) : isEdited ? (
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: colors.brand.accent, flexShrink: 0, boxShadow: "0 0 5px rgba(255,107,0,0.6)" }} title="Customized" />
            ) : null}
            {isExpanded && (() => {
              const reviewedCount = metrics.filter(m => touched.has(m.key)).length;
              const totalCount = metrics.length;
              const allDone = reviewedCount === totalCount;
              return (
                <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: allDone ? "#22c55e" : colors.text.faint, fontFamily: fonts.display, flexShrink: 0 }}>
                  {allDone ? "✓ reviewed" : `${reviewedCount}/${totalCount} reviewed`}
                </span>
              );
            })()}
          </div>
          <div style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>{statLine}</div>
          {seedTags && <div style={{ fontSize: fontSize.caption, color: colors.text.faint }}>{seedTags}</div>}
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: fontSize.title, fontWeight: fontWeight.black, color: theColor, fontFamily: fonts.display }}>{pts.toFixed(1)}</div>
          <div style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>PPR</div>
        </div>
        <span style={{ fontSize: fontSize.sub, color: hovered && !isExpanded ? theColor : "#A0AEC0", display: "inline-block", transform: isExpanded ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.15s, color 0.12s" }}>▼</span>
      </div>
      {isExpanded && (
        <div style={{ padding: "0 12px 12px" }}>
          {pos === "QB" && derivedPass && (
            <div style={{ display: "flex", gap: 8, marginBottom: 6, padding: "6px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 6, border: `1px solid ${colors.border.subtle}` }}>
              {[
                { label: "COMP", val: `${derivedPass.comp}/${vol.passAtt || 0}` },
                { label: "PASS YDS", val: derivedPass.passYds.toLocaleString() },
                { label: "PASS TD", val: derivedPass.passTD },
              ].map(d => (
                <div key={d.label} style={{ flex: 1, textAlign: "center" }}>
                  <div style={{ fontSize: fontSize.nano, fontWeight: fontWeight.bold, color: colors.text.faint, letterSpacing: "0.5px", fontFamily: fonts.display }}>{d.label}</div>
                  <div style={{ fontSize: fontSize.base, fontWeight: fontWeight.black, color: theColor, fontFamily: fonts.display }}>{d.val}</div>
                  <div style={{ fontSize: fontSize.micro, color: colors.text.faint }}>from receivers</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {metrics.map(m => (
              <RateSlider key={m.key} label={m.label} value={rates[m.key] ?? 0} unit={m.unit || ""}
                onChange={v => onRateChange(m.key, v)} min={m.min} max={m.max}
                avg5yr={hist ? avgH(hist.map(h => h[m.key])) : "—"}
                isActive={activeMetric === m.key} onClick={() => setActiveMetric(m.key)} color={theColor}
                isTouched={touched.has(m.key)} onTouch={() => markTouched(m.key)}
                defChip={showDeltas ? <DefChip user={rates[m.key] ?? 0} def={defaultRates?.[m.key]} threshold={DELTA_THRESHOLDS[m.key] ?? 0.2} /> : null} />
            ))}
          </div>
          {(() => {
            // Compute 2026 projection row from volume × rates
            const p26 = (() => {
              if (pos === "QB") {
                const pa = vol.passAtt || 0;
                const ra = vol.rushAtt || 0;
                const intV = Math.round(pa * (rates.intPct || 0) / 100);
                const rtd = Math.round(ra * (rates.rushTDPct || 0) / 100);
                return { passAtt: pa, comp: derivedPass?.comp ?? 0, passYds: derivedPass?.passYds ?? 0, passTD: derivedPass?.passTD ?? 0, int: intV, intPct: Math.round(rates.intPct || 0), rushAtt: ra, rushYPC: +(rates.rushYPC || 0).toFixed(1), rushTD: rtd, rushTDPct: Math.round(rates.rushTDPct || 0) };
              }
              if (pos === "RB") {
                const ra = vol.rushAtt || 0;
                const ry = Math.round(ra * (rates.ypc || 0));
                const rtd = Math.round(ra * (rates.rushTDPct || 0) / 100);
                const rec = Math.round((vol.targets || 0) * (rates.catchPct || 0) / 100);
                const recy = Math.round(rec * (rates.ypr || 0));
                const rectd = Math.round(rec * (rates.recTDPct || 0) / 100);
                return { rushAtt: ra, rushYds: ry, ypc: +(rates.ypc || 0).toFixed(1), rushTD: rtd, rushTDPct: Math.round(rates.rushTDPct || 0), targets: vol.targets || 0, rec, catchPct: Math.round(rates.catchPct || 0), recYds: recy, ypr: +(rates.ypr || 0).toFixed(1), recTD: rectd, recTDPct: Math.round(rates.recTDPct || 0) };
              }
              const rec = Math.round((vol.targets || 0) * (rates.catchPct || 0) / 100);
              const recy = Math.round(rec * (rates.ypr || 0));
              const rectd = Math.round(rec * (rates.recTDPct || 0) / 100);
              return { targets: vol.targets || 0, rec, catchPct: Math.round(rates.catchPct || 0), recYds: recy, ypr: +(rates.ypr || 0).toFixed(1), recTD: rectd, recTDPct: Math.round(rates.recTDPct || 0) };
            })();
            const projRowStyle = { borderBottom: "2px dashed rgba(255,107,0,0.25)", borderLeft: `3px solid ${colors.brand.accent}`, background: "rgba(255,107,0,0.05)" };
            const projTdC = { ...effTdC, fontWeight: fontWeight.bold, color: colors.brand.accent };
            const projTdR = (active) => ({ ...effTdC, fontWeight: fontWeight.heavy, color: colors.brand.accent, fontFamily: fonts.display, background: active ? "rgba(255,107,0,0.10)" : "transparent" });
            // Highlight helpers for companion columns of active metric
            const hlTh = (mk) => activeMetric === mk ? { ...effThC, background: "rgba(245,158,11,0.15)", color: "#F59E0B" } : effThC;
            const hlTd = (mk) => activeMetric === mk ? { ...effTdC, background: "rgba(245,158,11,0.15)", color: "#F5F5F5" } : effTdC;
            const hlPTd = (mk) => activeMetric === mk ? { ...projTdC, background: "rgba(245,158,11,0.15)" } : projTdC;
            return (
            <div style={{ marginTop: 8, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fontSize.caption }}>
                <thead><tr style={{ borderBottom: `1px solid ${colors.border.default}` }}>
                  <th style={effThS}>Year</th><th style={effThS}>Tm</th><th style={effThS}>G</th>
                  {pos === "QB" && <>
                    <th style={effThC}>Att</th><th style={effThC}>Cmp</th><th style={effThC}>Yds</th><th style={effThC}>TD</th>
                    <th style={hlTh("intPct")}>INT</th><th style={effThR(activeMetric==="intPct",theColor)}>INT%</th>
                    <th style={hlTh("rushYPC")}>RuAt</th><th style={effThR(activeMetric==="rushYPC",theColor)}>RuYPC</th>
                    <th style={hlTh("rushTDPct")}>RuTD</th><th style={effThR(activeMetric==="rushTDPct",theColor)}>RuTD%</th>
                  </>}
                  {pos === "RB" && <>
                    <th style={effThC}>Att</th><th style={hlTh("ypc")}>Yds</th><th style={effThR(activeMetric==="ypc",theColor)}>YPC</th>
                    <th style={hlTh("rushTDPct")}>TD</th><th style={effThR(activeMetric==="rushTDPct",theColor)}>TD%</th>
                    <th style={{ ...effThC, borderLeft: `1px solid ${colors.border.default}` }}>Tgt</th><th style={hlTh("catchPct")}>Rec</th><th style={effThR(activeMetric==="catchPct",theColor)}>Ct%</th>
                    <th style={hlTh("ypr")}>RcYd</th><th style={effThR(activeMetric==="ypr",theColor)}>Y/R</th>
                    <th style={hlTh("recTDPct")}>RcTD</th><th style={effThR(activeMetric==="recTDPct",theColor)}>RcTD%</th>
                  </>}
                  {pos === "REC" && <>
                    <th style={effThC}>Tgt</th><th style={hlTh("catchPct")}>Rec</th><th style={effThR(activeMetric==="catchPct",theColor)}>Ct%</th>
                    <th style={hlTh("ypr")}>Yds</th><th style={effThR(activeMetric==="ypr",theColor)}>Y/R</th>
                    <th style={hlTh("recTDPct")}>TD</th><th style={effThR(activeMetric==="recTDPct",theColor)}>TD%</th>
                  </>}
                </tr></thead>
                <tbody>
                  {/* 2026 projection row */}
                  <tr style={projRowStyle}>
                    <td style={effTdS}>
                      <span style={{ fontWeight: fontWeight.black, fontFamily: fonts.display, color: colors.brand.accent }}>2026</span>
                      <span style={{ fontSize: fontSize.nano, fontWeight: fontWeight.heavy, color: colors.brand.accent, background: "rgba(255,107,0,0.12)", padding: "1px 3px", borderRadius: 3, marginLeft: 3, letterSpacing: "0.5px", verticalAlign: "middle" }}>PROJ</span>
                    </td>
                    <td style={effTdS}><span style={{ color: colors.brand.accent, fontSize: fontSize.small }}>{abbrev}</span></td>
                    <td style={effTdS}><span style={{ color: colors.brand.accent }}>17</span></td>
                    {pos === "QB" && <>
                      <td style={projTdC}>{p26.passAtt}</td><td style={projTdC}>{p26.comp}</td><td style={projTdC}>{p26.passYds?.toLocaleString()}</td><td style={projTdC}>{p26.passTD}</td>
                      <td style={hlPTd("intPct")}>{p26.int}</td><td style={projTdR(activeMetric==="intPct")}>{p26.intPct}</td>
                      <td style={hlPTd("rushYPC")}>{p26.rushAtt}</td><td style={projTdR(activeMetric==="rushYPC")}>{p26.rushYPC}</td>
                      <td style={hlPTd("rushTDPct")}>{p26.rushTD}</td><td style={projTdR(activeMetric==="rushTDPct")}>{p26.rushTDPct}</td>
                    </>}
                    {pos === "RB" && <>
                      <td style={projTdC}>{p26.rushAtt}</td><td style={hlPTd("ypc")}>{p26.rushYds?.toLocaleString()}</td><td style={projTdR(activeMetric==="ypc")}>{p26.ypc}</td>
                      <td style={hlPTd("rushTDPct")}>{p26.rushTD}</td><td style={projTdR(activeMetric==="rushTDPct")}>{p26.rushTDPct}</td>
                      <td style={{ ...projTdC, borderLeft: `1px solid ${colors.border.default}` }}>{p26.targets}</td><td style={hlPTd("catchPct")}>{p26.rec}</td><td style={projTdR(activeMetric==="catchPct")}>{p26.catchPct}</td>
                      <td style={hlPTd("ypr")}>{p26.recYds}</td><td style={projTdR(activeMetric==="ypr")}>{p26.ypr}</td>
                      <td style={hlPTd("recTDPct")}>{p26.recTD}</td><td style={projTdR(activeMetric==="recTDPct")}>{p26.recTDPct}</td>
                    </>}
                    {pos === "REC" && <>
                      <td style={projTdC}>{p26.targets}</td><td style={hlPTd("catchPct")}>{p26.rec}</td><td style={projTdR(activeMetric==="catchPct")}>{p26.catchPct}</td>
                      <td style={hlPTd("ypr")}>{p26.recYds?.toLocaleString()}</td><td style={projTdR(activeMetric==="ypr")}>{p26.ypr}</td>
                      <td style={hlPTd("recTDPct")}>{p26.recTD}</td><td style={projTdR(activeMetric==="recTDPct")}>{p26.recTDPct}</td>
                    </>}
                  </tr>
                  {(hist || []).map((h, i) => {
                    const paced = showPace && h.gms >= 6 && h.gms < 17;
                    const pf = paced ? 17 / h.gms : 1;
                    const p = (v) => paced ? Math.round((v || 0) * pf) : v;
                    const pC = paced ? "#a3e635" : undefined;
                    const pTd = (mk) => paced ? { ...hlTd(mk), color: pC } : hlTd(mk);
                    const pEff = paced ? { ...effTdC, color: pC } : effTdC;
                    return (
                    <tr key={i} style={{ borderBottom: `1px solid ${colors.border.subtle}` }}>
                      <td style={effTdS}><span style={{ fontWeight: fontWeight.bold, fontFamily: fonts.display, color: colors.text.primary }}>{h.year}</span></td>
                      <td style={effTdS}><span style={{ color: "#E2E8F0", fontSize: fontSize.small }}>{h.team}</span></td>
                      <td style={effTdS}><span style={{ color: h.gms < 15 ? "#fbbf24" : "#E2E8F0" }}>{h.gms}{paced && "*"}</span></td>
                      {pos === "QB" && <>
                        <td style={pEff}>{p(h.passAtt)}</td><td style={pEff}>{p(h.comp)}</td><td style={pEff}>{p(h.passYds)?.toLocaleString()}</td><td style={pEff}>{p(h.passTD)}</td>
                        <td style={pTd("intPct")}>{p(h.int)}</td><td style={effTdR(activeMetric==="intPct",theColor)}>{h.intPct}</td>
                        <td style={pTd("rushYPC")}>{p(h.rushAtt)}</td><td style={effTdR(activeMetric==="rushYPC",theColor)}>{h.rushYPC}</td>
                        <td style={pTd("rushTDPct")}>{p(h.rushTD)}</td><td style={effTdR(activeMetric==="rushTDPct",theColor)}>{h.rushTDPct}</td>
                      </>}
                      {pos === "RB" && <>
                        <td style={pEff}>{p(h.rushAtt)}</td><td style={pTd("ypc")}>{p(h.rushYds)?.toLocaleString()}</td><td style={effTdR(activeMetric==="ypc",theColor)}>{h.ypc}</td>
                        <td style={pTd("rushTDPct")}>{p(h.rushTD)}</td><td style={effTdR(activeMetric==="rushTDPct",theColor)}>{h.rushTDPct}</td>
                        <td style={{ ...(paced ? pEff : effTdC), borderLeft: `1px solid ${colors.border.default}` }}>{p(h.targets)}</td><td style={pTd("catchPct")}>{p(h.rec)}</td><td style={effTdR(activeMetric==="catchPct",theColor)}>{h.catchPct}</td>
                        <td style={pTd("ypr")}>{p(h.recYds)}</td><td style={effTdR(activeMetric==="ypr",theColor)}>{h.ypr}</td>
                        <td style={pTd("recTDPct")}>{p(h.recTD)}</td><td style={effTdR(activeMetric==="recTDPct",theColor)}>{h.recTDPct}</td>
                      </>}
                      {pos === "REC" && <>
                        <td style={pEff}>{p(h.targets)}</td><td style={pTd("catchPct")}>{p(h.rec)}</td><td style={effTdR(activeMetric==="catchPct",theColor)}>{h.catchPct}</td>
                        <td style={pTd("ypr")}>{p(h.recYds)?.toLocaleString()}</td><td style={effTdR(activeMetric==="ypr",theColor)}>{h.ypr}</td>
                        <td style={pTd("recTDPct")}>{p(h.recTD)}</td><td style={effTdR(activeMetric==="recTDPct",theColor)}>{h.recTDPct}</td>
                      </>}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            );
          })()}
          {onLockIn && (
            <div style={{ textAlign: "center", padding: "10px 0 2px" }}>
              <button onClick={(e) => { e.stopPropagation(); onLockIn(); }}
                style={{ padding: "8px 28px", background: "linear-gradient(135deg, #FF6B00, #E55A00)", color: "#fff", border: "none", borderRadius: 8, fontWeight: fontWeight.heavy, fontFamily: fonts.display, fontSize: fontSize.sub, cursor: "pointer", boxShadow: "0 4px 12px rgba(255,107,0,0.3)", letterSpacing: "0.5px" }}>
                SET PROJECTION ✓
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── EfficiencyEditor ──────────────────────────────────────────────────────────

const EfficiencyEditor = forwardRef(function EfficiencyEditor({ abbrev, teamColor, passAtt, rushAtt, totalPlays, passPct, allocs, onConfirm, teamStarters, initialRates, logChange, showPace, setShowPace, onDepthChange, scoring = FULL_PPR_LEGACY, houseSeed = false }, ref) {
  // Rates (and now volume) are keyed by stint id (e.g. "WR1#0") so a future 2nd
  // stint in the same slot can carry its own rate card and volume split. Falls
  // back to `${slotKey}#0` when a slot's stint has no id yet (shouldn't happen
  // post-A1, but keeps this defensive).
  const stintIdFor = (slotKey) => allocs?.[slotKey]?.stints?.[0]?.id ?? `${slotKey}#0`;

  // Every stint for a slot, each carrying a stable id and resolved identity.
  // Single-stint slots (the only case for now) always yield exactly one entry
  // keyed by stintIdFor, equivalent to reading teamStarters[slot] directly.
  const stintsFor = (slot) => resolvedStintsFor(slot, allocs, teamStarters);

  // Build volume (per-stint rushAtt + targets) — memoized so its identity is
  // stable across renders, otherwise the derivedPass useMemo below (which
  // depends on it) recomputes every render instead of only when inputs change.
  const volume = useMemo(() => computeVolume({ allocs, passAtt, rushAtt, teamStarters }), [allocs, teamStarters, passAtt, rushAtt]);

  const [posGroup, setPosGroup]       = useState("QB");
  const [expanded, setExpanded]       = useState({ [stintIdFor("QB1")]: true });
  const [activeMetric, setActiveMetric] = useState("intPct");
  const [rates, setRates]             = useState(() => initialRates || seedRatesFor(houseSeed, abbrev, allocs, teamStarters));
  // Bumped by the clear-all affordance so the rebuild effect below re-merges
  // without the (now removed) player-edit layer.
  const [editsTick, setEditsTick]     = useState(0);
  // Per-team reset-rates flag (C4): when set, the rebuild merge drops the
  // initialRates (save-wins) layer for this team for the rest of the session.
  const [ratesReset, setRatesReset]   = useState(false);

  // Tab-away auto-commit (FN2): builds the exact same payload the Finalize
  // button does (via the single-sourced projectTeamPlayers), so there's no
  // second copy of the finalize math to keep in sync. `rates` is cloned for
  // the same reason as AllocationEditor's allocs clone - never hand back the
  // live state object this component keeps rendering/mutating.
  useImperativeHandle(ref, () => ({
    getSnapshot: () => {
      const { players, teamTotal } = projectTeamPlayers({ abbrev, totalPlays, passPct, allocs, rates, teamStarters, scoring });
      return { teamTotal, players, rates: JSON.parse(JSON.stringify(rates)) };
    },
  }), [abbrev, totalPlays, passPct, allocs, rates, teamStarters, scoring]);

  useEffect(() => {
    const next = {};
    const built = seedRatesFor(houseSeed, abbrev, allocs, teamStarters);
    for (const s of SLOTS) {
      stintsFor(s.key).forEach(st => {
        // Layer saved rates LAST so a confirmed team's stored values win over
        // rebuilt seeds/player-edits on resume — converges the wizard with
        // quick-edit (both read save truth) and closes the FN2 tab-away
        // auto-commit data-loss path. Stint-id-then-bare-slot fallback matches
        // `rates[st.id] ?? rates[s.key]` used everywhere else (legacy saves key
        // rates by the bare slot). Never-confirmed teams (initialRates null)
        // keep fresh seeds + player edits — both spreads collapse to {}.
        next[st.id] = {
          ...built[st.id],
          ...(st.sleeperId ? getPlayerEdit(st.sleeperId) : {}),
          // ratesReset (C4) drops the save-wins layer for this team/session;
          // every other path keeps the default merge order.
          ...(ratesReset ? {} : (initialRates?.[st.id] ?? initialRates?.[s.key] ?? {})),
        };
      });
    }
    setRates(next);
  }, [teamStarters, abbrev, allocs, editsTick, ratesReset, houseSeed]);

  // Also serves as freshDefaults.rates for Δ VS DEFAULTS (E3): these are the
  // PRE-merge `built` values - never merged, never persisted, never fed to scoring.
  const defaultRates = useMemo(() => seedRatesFor(houseSeed, abbrev, allocs, teamStarters), [houseSeed, abbrev, allocs, teamStarters]);
  // Display-only seed provenance (E1 explain collector) - never fed into rates state.
  const seedRates = useMemo(() => { const ex = {}; seedRatesFor(houseSeed, abbrev, allocs, teamStarters, ex); return ex.rates; }, [houseSeed, abbrev, allocs, teamStarters]);
  // Δ VS DEFAULTS (E3): only offered on teams resumed from a save - the same
  // initialRates gate a reset-rates affordance would use.
  const ratesResumed = initialRates != null;
  const [showDeltas, setShowDeltas] = useState(false);
  // Clear-all affordance for the per-player rate edits store
  // (mock1_player_edits_2026). clearedFlash briefly reports the count removed.
  const editCount = useMemo(() => Object.keys(loadPlayerEdits()).length, [editsTick, rates]); // eslint-disable-line react-hooks/exhaustive-deps
  const [clearedFlash, setClearedFlash] = useState(null);
  const clearTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(clearTimerRef.current), []);
  const onClearEdits = () => {
    const n = clearAllPlayerEdits();
    setClearedFlash(n);
    setEditsTick(t => t + 1); // re-merge rates without the edit layer
    clearTimeout(clearTimerRef.current);
    clearTimerRef.current = setTimeout(() => setClearedFlash(null), 4000);
  };
  // Per-team reset-rates escape hatch: saved teams are frozen rate snapshots
  // under save-wins — dropping the initialRates layer for THIS team only, for
  // the rest of the session. The default merge order (...built, ...playerEdit,
  // ...initialRates) is untouched everywhere else; the FN2 tab-away
  // auto-commit then snapshots the fresh rates, and the next save persists
  // them naturally.
  const onResetRates = () => {
    logChange?.("Efficiency", "rates", "saved snapshot", "reset to defaults");
    setRatesReset(true); // rebuild effect above re-merges without initialRates
  };
  const [locked, setLocked]           = useState(() => {
    // If resuming with initialRates, auto-lock all previously edited slots
    if (!initialRates) return {};
    const init = {};
    const defs = seedRatesFor(houseSeed, abbrev, allocs, teamStarters);
    SLOTS.forEach(s => {
      const stintId = stintIdFor(s.key);
      const r = initialRates[stintId] ?? initialRates[s.key]; const d = defs[stintId];
      if (r && d && Object.keys(r).some(k => parseFloat((r[k] ?? 0).toFixed(2)) !== parseFloat((d[k] ?? 0).toFixed(2)))) init[stintId] = true;
    });
    return init;
  });

  const updateRate = (st, key, val) => {
    const old = rates[st.id]?.[key] ?? rates[st.slotKey]?.[key] ?? 0;
    if (old !== val) logChange?.(st.player ?? st.slotKey, key, old, val);
    setRates(prev => ({ ...prev, [st.id]: { ...(prev[st.id] ?? prev[st.slotKey]), [key]: val } }));
    if (st.sleeperId) {
      setPlayerEdit(st.sleeperId, { [key]: val });
    }
  };

  // Advisory-only diff list for the Δ VS DEFAULTS summary line.
  const rateDeltas = useMemo(() => {
    if (!ratesResumed) return null;
    const out = [];
    SLOTS.forEach(s => {
      stintsFor(s.key).forEach(st => {
        const cur = rates[st.id] ?? rates[s.key] ?? {};
        const def = defaultRates[st.id];
        if (!def) return;
        const label = volume[st.id]?.player ?? s.key;
        for (const k of Object.keys(def)) {
          const th = DELTA_THRESHOLDS[k];
          if (th == null || cur[k] == null) continue;
          const d = cur[k] - def[k];
          if (Math.abs(d) >= th) out.push({ label, field: DELTA_FIELD_LABELS[k] ?? k, delta: d, score: Math.abs(d) / th });
        }
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratesResumed, rates, defaultRates, volume, allocs, teamStarters]);

  // Derive QB passing stats from receiver projections
  const derivedPass = useMemo(() => computeDerivedPass(volume, rates), [volume, rates]);

  // Splits the team's derived passing line across QB stints by games share.
  // Rounded per-field (same pattern as derivedPass itself) so a multi-stint
  // QB slot doesn't carry fractional comp/yds/TD into displayed stats.
  const splitPass = (share) => ({
    comp:     Math.round(derivedPass.comp * share),
    passYds:  Math.round(derivedPass.passYds * share),
    passTD:   Math.round(derivedPass.passTD * share),
  });

  const allPts = {};
  SLOTS.forEach(s => {
    if (s.pos === "QB") {
      const qbGames = stintsFor(s.key).reduce((n, st) => n + (st.games ?? 17), 0) || 1;
      allPts[s.key] = stintsFor(s.key).reduce((sum, st) => {
        const share = (st.games ?? 17) / qbGames;
        return sum + calcPPR(s.key, volume[st.id], rates[st.id] ?? {}, splitPass(share), scoring);
      }, 0);
    } else {
      allPts[s.key] = stintsFor(s.key).reduce((sum, st) =>
        sum + calcPPR(s.key, volume[st.id], rates[st.id] ?? {}, undefined, scoring), 0);
    }
  });
  const teamTotal = Object.values(allPts).reduce((s, v) => s + v, 0);

  // Stat-line realism guards (chunk 4). Advisory only — never blocks confirm,
  // exactly like groupEnvelopeFlags in the ALLOC step. Reads the SAME
  // projectTeamPlayers output the Finalize button and getSnapshot use, so the
  // warning a user sees is computed off the lines they are about to save. Depth
  // slots (RB3+/WR4+) are included by that call, which is what makes the
  // full-team envelopes the right reference here.
  // Memoised on exactly the inputs getSnapshot uses, so it recomputes when a rate
  // actually moves and not on the hover/expand re-renders this editor does
  // constantly. try/catch because an advisory warning must never be the thing
  // that breaks the editor.
  const statLineFlags = useMemo(() => {
    try {
      const { players } = projectTeamPlayers({ abbrev, totalPlays, passPct, allocs, rates, teamStarters, scoring });
      return computeStatLineFlags(players).filter(f => f.flag);
    } catch {
      return [];
    }
  }, [abbrev, totalPlays, passPct, allocs, rates, teamStarters, scoring]);

  const groups = {
    QB:  { label: "Quarterbacks",  slots: ["QB1"],                                    color: colors.pos.QB },
    RB:  { label: "Running Backs", slots: ["RB1", "RB2", "RB3+"],                    color: colors.pos.RB },
    REC: { label: "Receivers",     slots: ["WR1", "WR2", "WR3", "WR4+", "TE1", "TE2"], color: colors.pos.WR },
  };
  const cg = groups[posGroup];
  const groupCards = cg.slots.flatMap(slot => stintsFor(slot).map(st => ({ slot, st })));
  const expandedCard = groupCards.find(c => expanded[c.st.id]) || groupCards[0];
  const expandedSlot = expandedCard.slot;
  const expPlayer    = volume[expandedCard.st.id]?.player;
  const expPos       = expandedSlot.startsWith("QB") ? "QB" : expandedSlot.startsWith("RB") ? "RB" : "REC";
  const expHist      = buildPlayerHistForSlot(expPlayer, expPos, volume[expandedCard.st.id]?.sleeperId);
  const expIsTE      = expandedSlot.startsWith("TE");
  const expColor     = expIsTE ? colors.pos.TE : cg.color;

  const compKey = () => {
    const pfx = expandedSlot.startsWith("QB") ? "QB" : expandedSlot.startsWith("RB") ? "RB" : expandedSlot.startsWith("TE") ? "TE" : "WR";
    return `${pfx}-${activeMetric}`;
  };
  const compData = LEAGUE_COMPS[compKey()];
  const metricLabels = {
    compPct:"Comp%", ypa:"Yards/Attempt", tdPct:"TD Rate", intPct:"INT Rate", rushYPC:"Rush YPC",
    ypc:"Yards/Carry", rushTDPct:"Rush TD%", catchPct:"Catch Rate", ypr:"Yards/Reception", recTDPct:"Rec TD%",
  };

  const [hovNav, setHovNav] = useState(null);
  const navBtn = (id) => ({ padding: "10px 14px", background: hovNav === id ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.06)", border: `1px solid ${colors.border.default}`, borderRadius: 8, color: "#CBD5E0", fontFamily: fonts.display, fontSize: fontSize.sub, fontWeight: fontWeight.bold, cursor: "pointer", textDecoration: hovNav === id ? "underline" : "none", transition: "background 0.12s" });

  return (
    <div style={{ paddingBottom: 70 }}>
      <div style={{ fontSize: fontSize.body, fontWeight: fontWeight.bold, color: colors.brand.accent, letterSpacing: "2px", fontFamily: fonts.display, marginBottom: 2 }}>STEP 4 — PLAYER EFFICIENCY</div>
      <h2 style={{ fontFamily: fonts.display, fontSize: fontSize.title, fontWeight: fontWeight.heavy, margin: "0 0 4px", color: colors.text.primary }}>How efficient will each player be?</h2>
      <p style={{ fontSize: fontSize.caption, color: colors.text.faint, margin: "0 0 12px" }}>
        Defaults are never random: your own history blended with position means, backtested 2018-25.
      </p>
      {statLineFlags.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {statLineFlags.map(f => (
            <div key={f.check + (f.player || "")} style={{ fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#fbbf24", fontFamily: fonts.display, marginBottom: 2 }}>
              {f.check === "teamTargets" && `⚠ team receiving volume ${f.value} targets — outside the 2025 team range (${f.lo}-${f.hi})`}
              {f.check === "teamCatchRate" && `⚠ team catch rate ${f.value}% — outside 90% of 2025 teams (${f.lo}-${f.hi}%)`}
              {f.check === "playerCatchRate" && `⚠ ${f.player} catch rate ${f.value}% on ${f.targets} targets — outside 90% of 2025 ${f.pos}s (${f.lo}-${f.hi}%)`}
            </div>
          ))}
        </div>
      )}
      {(ratesResumed || editCount > 0 || clearedFlash != null) && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "-4px 0 12px" }}>
          {ratesResumed && <DeltaToggle on={showDeltas} onToggle={() => setShowDeltas(v => !v)} />}
          {ratesResumed && !ratesReset && (
            <TwoClickButton
              idleLabel="RESET RATES TO DEFAULTS"
              armedLabel="CONFIRM RESET"
              onConfirm={onResetRates}
              title="Drop this team's saved rate snapshot and return to today's fresh seeds (this session; next save persists them)"
            />
          )}
          {ratesResumed && showDeltas && <DeltaSummaryLine deltas={rateDeltas} />}
          {clearedFlash != null ? (
            <span style={{ fontSize: fontSize.micro, fontFamily: fonts.display, fontWeight: fontWeight.bold, letterSpacing: "1px", color: colors.status.success, marginLeft: "auto" }}>
              CLEARED {clearedFlash} PLAYER EDIT{clearedFlash === 1 ? "" : "S"}
            </span>
          ) : editCount > 0 && (
            <span style={{ marginLeft: "auto", flexShrink: 0 }}>
              <TwoClickButton
                idleLabel={`CLEAR PLAYER EDITS (${editCount})`}
                armedLabel="CONFIRM CLEAR"
                onConfirm={onClearEdits}
                title="Remove all saved per-player rate edits (localStorage) — seeds and saved-team rates are unaffected"
              />
            </span>
          )}
        </div>
      )}

      {/* Position tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
        {Object.entries(groups).map(([key, g]) => {
          const active = posGroup === key;
          const gPts = g.slots.reduce((s, sl) => s + (allPts[sl] || 0), 0);
          const gCards = g.slots.flatMap(sl => stintsFor(sl));
          const gLocked = gCards.filter(st => locked[st.id]).length;
          const gTotal = gCards.length;
          const gDone = gLocked === gTotal;
          return (
            <button key={key} onClick={() => { setPosGroup(key); const f = g.slots[0]; setExpanded({ [stintIdFor(f)]: true }); setActiveMetric(key === "QB" ? "intPct" : key === "RB" ? "ypc" : "catchPct"); }}
              style={{ flex: 1, padding: "8px 10px", borderRadius: 8, cursor: "pointer", textAlign: "left", background: active ? `${g.color}10` : "rgba(255,255,255,0.02)", border: `1.5px solid ${active ? `${g.color}35` : colors.border.subtle}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.heavy, color: active ? g.color : "#A0AEC0", fontFamily: fonts.display }}>{g.label}</span>
                {gDone ? (
                  <span style={{ fontSize: fontSize.micro, color: "#22c55e", fontWeight: fontWeight.black }}>✓ set</span>
                ) : (
                  <span style={{ fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: colors.text.faint, fontFamily: fonts.display }}>{gLocked}/{gTotal} set</span>
                )}
              </div>
              <div style={{ fontSize: fontSize.title, fontWeight: fontWeight.black, color: active ? g.color : "#A0AEC0", fontFamily: fonts.display }}>{gPts.toFixed(1)} <span style={{ fontSize: fontSize.micro }}>PPR</span></div>
            </button>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 400px", gap: 12, alignItems: "start" }}>
        {/* LEFT — player cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          {groupCards.map(({ slot, st }, cardIdx) => {
            const isTE = slot.startsWith("TE");
            const isLast = cardIdx === groupCards.length - 1;
            const isLastGroup = posGroup === "REC";
            const nextCard = groupCards[cardIdx + 1];
            // Lock In handler: lock stint, collapse, expand next
            const handleLockIn = () => {
              setLocked(p => ({ ...p, [st.id]: true }));
              if (!isLast) {
                const nextMetric = nextCard.slot.startsWith("QB") ? "intPct" : nextCard.slot.startsWith("RB") ? "ypc" : "catchPct";
                setExpanded({ [nextCard.st.id]: true });
                setActiveMetric(nextMetric);
                setTimeout(() => { const el = document.getElementById(`epc-${nextCard.st.id}`); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }, 60);
              } else {
                setExpanded({});
              }
            };
            // Next player link logic
            let nextLink = null;
            if (expanded[st.id]) {
              if (!isLast) {
                // Next player within same group
                const nextPlayer = volume[nextCard.st.id]?.player || nextCard.slot;
                const nextMetric = nextCard.slot.startsWith("QB") ? "intPct" : nextCard.slot.startsWith("RB") ? "ypc" : "catchPct";
                nextLink = (
                  <div style={{ textAlign: "right", padding: "4px 12px 2px" }}>
                    <span onClick={(e) => { e.stopPropagation(); setExpanded({ [nextCard.st.id]: true }); setActiveMetric(nextMetric); setTimeout(() => { const el = document.getElementById(`epc-${nextCard.st.id}`); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }, 60); }}
                      style={{ fontSize: fontSize.body, fontWeight: fontWeight.medium, color: colors.brand.accent, cursor: "pointer", fontFamily: fonts.display }}>
                      Next: {nextPlayer} ({nextCard.slot}) ↓
                    </span>
                  </div>
                );
              } else if (!isLastGroup) {
                // Last in group — continue to next group
                const nextGroupLabel = posGroup === "QB" ? "RBs" : "Receivers";
                nextLink = (
                  <div style={{ textAlign: "right", padding: "4px 12px 2px" }}>
                    <span onClick={(e) => { e.stopPropagation(); if (posGroup === "QB") { setPosGroup("RB"); setExpanded({ [stintIdFor("RB1")]: true }); setActiveMetric("ypc"); } else { setPosGroup("REC"); setExpanded({ [stintIdFor("WR1")]: true }); setActiveMetric("catchPct"); } }}
                      style={{ fontSize: fontSize.body, fontWeight: fontWeight.medium, color: colors.brand.accent, cursor: "pointer", fontFamily: fonts.display }}>
                      Continue → {nextGroupLabel}
                    </span>
                  </div>
                );
              }
              // Last player of last group (REC): no link shown
            }
            return (
              <div key={st.id} id={`epc-${st.id}`}>
                {slot === "TE1" && posGroup === "REC" && <div style={{ fontSize: fontSize.caption, fontWeight: fontWeight.bold, color: colors.pos.TE, letterSpacing: "1px", fontFamily: fonts.display, padding: "6px 0 3px", borderTop: `1px solid ${colors.border.subtle}`, marginTop: 3 }}>TIGHT ENDS</div>}
                <EPlayerCard slot={slot} vol={volume[st.id]} rates={rates[st.id] ?? rates[slot] ?? {}}
                  defaultRates={defaultRates[st.id] ?? defaultRates[slot] ?? {}}
                  seedTags={seedTagLine(seedRates?.[st.id])}
                  showDeltas={showDeltas}
                  onRateChange={(k, v) => updateRate(st, k, v)}
                  activeMetric={activeMetric} setActiveMetric={setActiveMetric}
                  isExpanded={!!expanded[st.id]}
                  onToggle={() => {
                    if (!expanded[st.id]) {
                      const p = slot.startsWith("QB") ? "intPct" : slot.startsWith("RB") ? "ypc" : "catchPct"; setActiveMetric(p);
                      // Unlock if re-expanding a locked player
                      if (locked[st.id]) setLocked(prev => { const next = { ...prev }; delete next[st.id]; return next; });
                    }
                    setExpanded(() => { const next = {}; next[st.id] = !expanded[st.id]; return next; });
                  }}
                  posColor={cg.color} isTE={isTE} abbrev={abbrev}
                  derivedPass={slot.startsWith("QB") ? splitPass((st.games ?? 17) / (stintsFor(slot).reduce((n, s2) => n + (s2.games ?? 17), 0) || 1)) : undefined}
                  isLocked={!!locked[st.id]}
                  onLockIn={expanded[st.id] ? handleLockIn : undefined}
                  showPace={showPace}
                  onDepthChange={onDepthChange} scoring={scoring} />
                {nextLink}
              </div>
            );
          })}
          {(() => {
            const groupDone = groupCards.every(c => locked[c.st.id]);
            const fwdStyle = groupDone
              ? { background: "linear-gradient(135deg, #FF6B00, #E55A00)", color: "#fff", border: "none", boxShadow: "0 4px 12px rgba(255,107,0,0.3)" }
              : { background: "rgba(255,255,255,0.06)", color: "#A0AEC0", border: `1px solid ${colors.border.default}`, boxShadow: "none" };
            return (
          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            {posGroup !== "QB" && (
              <button onClick={() => { const tgt = posGroup === "RB" ? "QB" : "RB"; setPosGroup(tgt); setExpanded({ [stintIdFor(tgt === "QB" ? "QB1" : "RB1")]: true }); setActiveMetric(tgt === "QB" ? "intPct" : "ypc"); }}
                onMouseEnter={() => setHovNav("back")} onMouseLeave={() => setHovNav(null)}
                style={navBtn("back")}>← {posGroup === "RB" ? "QBs" : "RBs"}</button>
            )}
            {posGroup !== "REC" ? (
              <button onClick={() => { if (posGroup === "QB") { setPosGroup("RB"); setExpanded({ [stintIdFor("RB1")]: true }); setActiveMetric("ypc"); } else { setPosGroup("REC"); setExpanded({ [stintIdFor("WR1")]: true }); setActiveMetric("catchPct"); } }}
                onMouseEnter={() => setHovNav("fwd")} onMouseLeave={() => setHovNav(null)}
                style={{ ...navBtn("fwd"), flex: 1, ...fwdStyle }}>
                {posGroup === "QB" ? "CONTINUE → RBs" : "CONTINUE → Receivers"}
              </button>
            ) : (
              <button onClick={() => {
                const { players, teamTotal: finalTeamTotal } = projectTeamPlayers({ abbrev, totalPlays, passPct, allocs, rates, teamStarters, scoring });
                onConfirm({ teamTotal: finalTeamTotal, players, rates });
              }}
                onMouseEnter={() => setHovNav("fin")} onMouseLeave={() => setHovNav(null)}
                style={{ ...navBtn("fin"), flex: 1, ...fwdStyle }}>
                FINALIZE → VIEW RESULTS
              </button>
            )}
          </div>
            );
          })()}
        </div>

        {/* RIGHT — sticky charts */}
        <div style={{ position: "sticky", top: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: fontSize.caption, color: showPace ? "#22c55e" : "#A0AEC0", fontFamily: fonts.display, fontWeight: fontWeight.bold }}>
              <span>17G Pace</span>
              <div onClick={() => setShowPace(p => !p)} style={{ width: 32, height: 16, borderRadius: 8, background: showPace ? "#22c55e" : "rgba(255,255,255,0.1)", border: `1px solid ${showPace ? "#22c55e" : "rgba(255,255,255,0.15)"}`, position: "relative", cursor: "pointer", transition: "all 0.2s" }}>
                <div style={{ width: 12, height: 12, borderRadius: 6, background: showPace ? "#fff" : "#718096", position: "absolute", top: 1, left: showPace ? 17 : 1, transition: "all 0.2s" }} />
              </div>
            </label>
          </div>
          {expHist && activeMetric && (() => {
            const ev = volume[expandedCard.st.id] || {};
            const er = rates[expandedCard.st.id] ?? rates[expandedSlot] ?? {};
            const expShare = expandedSlot.startsWith("QB")
              ? (expandedCard.st.games ?? 17) / (stintsFor(expandedSlot).reduce((n, s2) => n + (s2.games ?? 17), 0) || 1)
              : null;
            const expDp = expShare != null ? splitPass(expShare) : null;
            const rec = Math.round((ev.targets || 0) * (er.catchPct || 0) / 100);
            const proj26Row = {
              passAtt: ev.passAtt || 0, comp: expDp?.comp ?? 0, passTD: expDp?.passTD ?? 0,
              int: Math.round((ev.passAtt || 0) * (er.intPct || 0) / 100),
              rushAtt: ev.rushAtt || 0, rushTD: Math.round((ev.rushAtt || 0) * (er.rushTDPct || 0) / 100),
              targets: ev.targets || 0, rec, recTD: Math.round(rec * (er.recTDPct || 0) / 100),
            };
            const ctxFn = VOLUME_CONTEXT_KEYS[activeMetric];
            const proj26Ctx = ctxFn ? ctxFn(proj26Row) : null;
            return (
            <MetricHistoryChart
              title={`${shortName(expPlayer)} — ${metricLabels[activeMetric] || activeMetric}`}
              history={expHist} metricKey={activeMetric}
              projValue={(rates[expandedCard.st.id] ?? rates[expandedSlot])?.[activeMetric] ?? 0}
              color={expColor} teamAbbrev={abbrev}
              projContext={proj26Ctx} showPace={showPace} />
            );
          })()}
          {compData && (
            <LeagueCompChart
              title={`${metricLabels[activeMetric]} — League`}
              data={compData}
              projValue={(rates[expandedCard.st.id] ?? rates[expandedSlot])?.[activeMetric] ?? 0}
              color={expColor}
              expandedPlayerName={expPlayer} />
          )}
          {/* Team total mini-bar */}
          <div style={{ background: colors.bg.raised, borderRadius: 8, border: `1px solid ${colors.border.subtle}`, padding: 10 }}>
            <div style={{ fontSize: fontSize.micro, fontWeight: fontWeight.heavy, color: colors.text.primary, fontFamily: fonts.display, marginBottom: 6 }}>Team Projection</div>
            {SLOTS.map(s => {
              const pts = allPts[s.key] || 0;
              const pc = colors.pos[s.pos === "TE" ? "TE" : s.pos];
              const maxP = Math.max(...Object.values(allPts));
              return (
                <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                  <span style={{ width: 26, fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: pc, fontFamily: fonts.display }}>{s.key}</span>
                  <div style={{ flex: 1, height: 5, background: "rgba(255,255,255,0.04)", borderRadius: 3 }}>
                    <div style={{ height: "100%", width: `${(pts / maxP) * 100}%`, background: pc, borderRadius: 3, opacity: 0.5 }} />
                  </div>
                  <span style={{ width: 34, fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#E2E8F0", fontFamily: fonts.display, textAlign: "right" }}>{pts.toFixed(0)}</span>
                </div>
              );
            })}
            <div style={{ borderTop: `1px solid ${colors.border.subtle}`, marginTop: 4, paddingTop: 4, display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: fontSize.nano, fontWeight: fontWeight.heavy, color: colors.text.primary, fontFamily: fonts.display }}>TOTAL</span>
              <span style={{ fontSize: fontSize.body, fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display }}>{teamTotal.toFixed(1)}</span>
            </div>
          </div>
        </div>
      </div>
      <SanityBar teamTotal={teamTotal} allPts={allPts} scoring={scoring} />
    </div>
  );
});

// ── TeamEditor ────────────────────────────────────────────────────────────────

export default function TeamEditor({ abbrev, teamColor, onBack, onProjectTeam, startersOverride, onStepChange, progress, onOpenRankings, scoring = FULL_PPR_LEGACY }) {
  useRenderLoopGuard("TeamEditor");
  const meta          = TEAM_META[abbrev];
  const mergedHistory = useMemo(() => buildMergedHistory(abbrev), [abbrev]);
  const coach2025     = (COACHING_HISTORY[abbrev] || []).find(c => c.year === 2025);
  const coach2026     = (COACHING_HISTORY[abbrev] || []).find(c => c.year === 2026);
  const last          = mergedHistory[mergedHistory.length - 1];

  const hcColors    = useMemo(() => getCoachColorMap(mergedHistory, "hc"), [mergedHistory]);
  const ocColors    = useMemo(() => getCoachColorMap(mergedHistory, "oc"), [mergedHistory]);

  // Render trigger for depth chart override swaps — bumped after a successful
  // writeSlotOverride so teamStarters picks up the new merged ordering.
  // The VALUE is read (not just the setter) because teamStarters is memoised
  // below: a bare re-render no longer recomputes getStarters, so the tick has to
  // be a real dependency or depth swaps would never reach the wizard.
  const [depthTick, setDepthTick] = useState(0);
  const bumpDepthTick = useCallback(() => setDepthTick(t => t + 1), []);

  const hcName   = coach2026?.hc ?? coach2025?.hc ?? last?.hc ?? "—";
  const ocName   = coach2026?.oc ?? coach2025?.oc ?? last?.oc ?? "—";
  const ocCareer = useMemo(() => getCoachFullCareer(ocName), [ocName]);
  const hcCareer = useMemo(() => getHCCareerHistory(hcName), [hcName]);
  const ocCareerHistory = useMemo(() => getOCCareerHistory(ocName), [ocName]);
  // Convert career data to mergedHistory format for chart reuse
  const hcMerged = useMemo(() => buildCareerMergedHistory(hcCareer), [hcCareer]);
  const ocMerged = useMemo(() => buildCareerMergedHistory(ocCareerHistory), [ocCareerHistory]);
  const tc       = teamColor || meta.primaryColor;
  // 🔴 MEMOISED, AND THE MEMO IS LOAD-BEARING. Computed inline this was a new
  // object on every render, and EfficiencyEditor's rates-rebuild effect deps on
  // it: dragging a rate slider calls logChange -> setChangelog here -> re-render
  // -> new identity -> the effect refires and re-layers the SAVED rates (which
  // merge last, by design) over the drag, on the very next frame. On a confirmed
  // team that reads as a slider that cannot be moved. Note both branches are
  // unstable: hydrateOverrideStarters always builds a fresh object, and
  // getStarters returns a fresh merge whenever the team has depth overrides (it
  // returns the shared STARTERS_2025 row only in the no-override case — which is
  // why the bug looked team/player-specific rather than universal).
  // Do NOT "fix" this in the effect instead: the save-wins-last merge order is
  // load-bearing for the FN2 tab-away auto-commit, and a real teamStarters change
  // is still supposed to re-merge.
  const teamStarters = useMemo(
    () => (startersOverride ? hydrateOverrideStarters(startersOverride) : getStarters(abbrev)),
    // depthTick is not read inside the factory on purpose — it is the invalidation
    // signal for getStarters' localStorage-backed depth overrides, which the rule
    // cannot see. Dropping it would freeze starters across depth swaps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [startersOverride, abbrev, depthTick],
  );

  // ── New HC / OC detection ──
  const isNewHC = !!(coach2026?.hc && coach2025?.hc && coach2026.hc !== coach2025.hc);
  const isNewOC = !!(coach2026?.oc && coach2025?.oc && coach2026.oc !== coach2025.oc && !isNewHC);
  const hcPrior = useMemo(() => isNewHC ? getHCPriorTeamHistory(hcName, abbrev) : null, [isNewHC, hcName, abbrev]);
  const ocPrior = useMemo(() => isNewOC ? getOCPriorTeamHistory(ocName, abbrev) : null, [isNewOC, ocName, abbrev]);

  // ── localStorage restore ──
  const SAVE_VERSION = 11; // bump when saved schema changes (11 = Stage 2 WR/TE/RB efficiency-blend re-seed)
  const saveKey = `projlab-${abbrev}`;
  const [savedInit] = useState(() => {
    try {
      // safeReadProjlab quarantines (never deletes) a corrupt/too-new save and
      // returns null, so this team starts fresh instead of crashing on restore.
      const raw = safeReadProjlab(abbrev);
      if (!raw) return null;
      if ((raw.version || 0) < SAVE_VERSION) {
        // Seeding-only version bumps must NEVER destroy user-confirmed work.
        // New defaults apply only to teams with no saved summaries; existing
        // saves keep their numbers (re-stamped at current version on next save).
      }
      return raw;
    }
    catch { return null; }
  });

  // Wizard layers with no CONFIRMED WORK seed from the house projection instead of
  // buildInit*. Gated per layer on the absence of that layer's saved summary —
  // allocSummary for allocs, effSummary for rates — NOT on the absence of a save
  // file and not on a progress state. A save appears the moment the user touches
  // step 2, so gating on the file itself handed league-average seeds to anyone who
  // had merely opened the team. Where a layer's summary DOES exist the legacy path
  // takes over and the user's own numbers win, exactly as before. See
  // houseSeedFlags for why the two layers compose independently.
  const houseSeed = houseSeedFlags(savedInit, abbrev);

  // ── changelog ──
  const [changelog, setChangelog] = useState(() => savedInit?.changelog || []);
  const logChange = useCallback((playerOrStep, field, oldValue, newValue) => {
    if (oldValue === newValue) return;
    const round = v => typeof v === "number" ? Math.round(v * 10) / 10 : v;
    const now = Date.now();
    setChangelog(prev => {
      const last = prev.length > 0 ? prev[prev.length - 1] : null;
      // Coalesce rapid changes to the same field (slider drag) — update newValue instead of adding
      if (last && last.playerOrStep === playerOrStep && last.field === field && (now - last.ts) < 3000) {
        const updated = [...prev];
        updated[updated.length - 1] = { ...last, ts: now, newValue: round(newValue) };
        // If we dragged back to the original value, remove the entry entirely
        if (updated[updated.length - 1].oldValue === updated[updated.length - 1].newValue) updated.pop();
        return updated;
      }
      return [...prev, { ts: now, playerOrStep, field, oldValue: round(oldValue), newValue: round(newValue), note: null }];
    });
  }, []);

  // ── 1A state ──
  const [tab1A, setTab1A] = useState("team");
  const initVolume = useMemo(() => buildInitVolume(abbrev), [abbrev]);
  // Display-only seed provenance for the 1A/1B captions (E1 explain collector).
  const seedVolume = useMemo(() => { const ex = {}; buildInitVolume(abbrev, ex); return ex.volume; }, [abbrev]);
  const [totalPlays, setTotalPlaysRaw] = useState(savedInit?.totalPlays ?? initVolume.totalPlays);
  const setTotalPlays = useCallback((v) => {
    setTotalPlaysRaw(prev => { logChange("Step 1A", "Total Plays", prev, v); return v; });
  }, [logChange]);

  // ── Team tab reference-team dropdown (1A/1B) — view any team's history without touching the projection ──
  const [teamView, setTeamView] = useState(null);
  const teamViewMerged = useMemo(() => teamView ? buildMergedHistory(teamView) : mergedHistory, [teamView, mergedHistory]);
  const teamViewColor = teamView ? (TEAM_META[teamView]?.primaryColor ?? tc) : tc;
  const isOwnTeam = !teamView;

  // ── 1B state ──
  const [tab1B, setTab1B] = useState("team");
  const initPassPct = savedInit?.passPct ?? initVolume.passPct;
  const [passPct, setPassPctRaw] = useState(initPassPct);
  const setPassPct = useCallback((v) => {
    setPassPctRaw(prev => { logChange("Step 1B", "Pass %", prev, v); return v; });
  }, [logChange]);

  // ── step state ──
  const [currentStep, setCurrentStep] = useState(savedInit?.currentStep ?? "1a");
  const [allocSummary, setAllocSummary] = useState(savedInit?.allocSummary ?? null);
  const [effSummary,   setEffSummary]   = useState(savedInit?.effSummary ?? null);
  const [showPace, setShowPace]        = useState(true);

  // PLAYS/SPLIT/ALLOC are always reachable (plays/passPct always have values);
  // EFFICIENCY only once allocs are confirmed.
  const navMaxIdx = allocSummary ? 3 : 2;

  // FN2: tabbing away from ALLOC/EFFICIENCY auto-commits live edits (via each
  // editor's getSnapshot ref) instead of discarding them - only the editors'
  // own CONFIRM buttons used to commit.
  const allocRef = useRef(null);
  const effRef = useRef(null);
  const handleStepNav = (step) => {
    if (currentStep === "2") { const p = allocRef.current?.getSnapshot(); if (p) setAllocSummary(p); }
    if (currentStep === "3") { const p = effRef.current?.getSnapshot(); if (p) setEffSummary(p); }
    setCurrentStep(step);
  };

  // ── auto-save to localStorage ──
  const [saveFlash, setSaveFlash] = useState(false);
  const saveTimerRef = useRef(null);
  const isFirstSave = useRef(true);
  const completedRef = useRef(savedInit?.completedSteps || [false, false, false, false]);

  useEffect(() => {
    const STEPS = ["1a", "1b", "2", "3", "results", "done"];
    const idx = STEPS.indexOf(currentStep);
    const prev = completedRef.current;
    const completedSteps = [
      prev[0] || idx >= 1, prev[1] || idx >= 2,
      prev[2] || idx >= 3, prev[3] || idx >= 4,
    ];
    completedRef.current = completedSteps;

    const data = { schemaVersion: PROJLAB_SCHEMA_VERSION, version: SAVE_VERSION, totalPlays, passPct, allocSummary, effSummary, currentStep, completedSteps, changelog };
    try { localStorage.setItem(saveKey, JSON.stringify(data)); } catch {}

    if (isFirstSave.current) { isFirstSave.current = false; return; }
    setSaveFlash(true);
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => setSaveFlash(false), 1500);
    return () => clearTimeout(saveTimerRef.current);
  }, [totalPlays, passPct, allocSummary, effSummary, currentStep, saveKey, changelog]);

  useEffect(() => { onStepChange?.(currentStep); }, [currentStep, onStepChange]);

  // ── 1A derived ──
  const teamAvg1A = avgArr(mergedHistory.map(h => h.total));
  const lgAvg1A   = avgArr(LEAGUE_YEARS.filter(y => LEAGUE_STATS[y]).map(y => LEAGUE_STATS[y].avg));
  const diff2025  = totalPlays - (last?.total ?? 0);
  const diffAvg1A = totalPlays - teamAvg1A;
  const diffLg1A  = totalPlays - lgAvg1A;
  const narrative1A = useMemo(() => build1ANarrative(abbrev, mergedHistory, { isNewHC, isNewOC, hcName, ocName, hcPrior, ocPrior }), [abbrev, mergedHistory, isNewHC, isNewOC, hcName, ocName, hcPrior, ocPrior]);

  // ── 1B derived ──
  const passAtt   = Math.round(totalPlays * passPct / 100);
  const rushAtt   = totalPlays - passAtt;
  const lastPA    = last?.pa ?? 0;
  const lastRA    = last?.ra ?? 0;
  const avgPA     = avgArr(mergedHistory.map(h => h.pa));
  const avgRA     = avgArr(mergedHistory.map(h => h.ra));
  const lgPA      = avgArr(LEAGUE_YEARS.filter(y => LEAGUE_PASS_STATS[y]).map(y => LEAGUE_PASS_STATS[y].avg));
  const lgRA      = avgArr(LEAGUE_YEARS.filter(y => LEAGUE_RUSH_STATS[y]).map(y => LEAGUE_RUSH_STATS[y].avg));
  const narrative1B = useMemo(() => build1BNarrative(abbrev, mergedHistory, { isNewHC, isNewOC, hcName, ocName, hcPrior, ocPrior }), [abbrev, mergedHistory, isNewHC, isNewOC, hcName, ocName, hcPrior, ocPrior]);

  const ds = v => (v >= 0 ? "+" : "") + v;
  const dc = v => ({ color: v >= 0 ? "#4ade80" : "#f87171" });

  const wrapStyle = {
    minHeight: "100vh",
    background: `radial-gradient(ellipse at 50% 0%, #1a1f35 0%, ${colors.bg.deep} 50%, ${colors.bg.app} 100%)`,
    fontFamily: fonts.body,
    color: colors.text.primary,
    padding: "20px",
  };

  // ── STEP 2: allocation editor ─────────────────────────────────────────────

  if (currentStep === "2") {
    return (
      <div style={wrapStyle}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          <EditorHeader abbrev={abbrev} teamColor={tc} onBack={onBack} saveFlash={saveFlash} onOpenRankings={onOpenRankings} onViewResults={effSummary ? () => handleStepNav("results") : null} />
          <StepIndicator activeIdx={2} onStepClick={handleStepNav} maxIdx={navMaxIdx} />

          <LockedStrip label="TOTAL PLAYS" onClick={() => handleStepNav("1a")} hoverable>
            <span style={{ fontSize: fontSize.heading, fontWeight: fontWeight.black, color: colors.text.primary, fontFamily: fonts.display }}>{totalPlays}</span>
            <span style={{ fontSize: fontSize.small, fontWeight: fontWeight.medium, ...dc(diff2025) }}>({ds(diff2025)} vs '25)</span>
          </LockedStrip>
          <LockedStrip label="PASS / RUN" onClick={() => handleStepNav("1b")} hoverable>
            <span style={{ fontSize: fontSize.heading, fontWeight: fontWeight.black, color: colors.pos.QB, fontFamily: fonts.display }}>{passAtt}</span>
            <span style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>pass ({Math.round(passPct)}%)</span>
            <span style={{ color: "#A0AEC0" }}>|</span>
            <span style={{ fontSize: fontSize.heading, fontWeight: fontWeight.black, color: colors.pos.RB, fontFamily: fonts.display }}>{rushAtt}</span>
            <span style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>rush ({Math.round(100 - passPct)}%)</span>
          </LockedStrip>

          <div style={{ background: colors.bg.raised, borderRadius: 14, border: `1px solid ${colors.border.subtle}`, padding: 18 }}>
            <AllocationEditor
              ref={allocRef}
              abbrev={abbrev}
              teamColor={tc}
              passAtt={passAtt}
              rushAtt={rushAtt}
              hcName={hcName}
              ocName={ocName}
              teamStarters={teamStarters}
              initialAllocs={allocSummary?.allocs}
              logChange={logChange}
              showPace={showPace}
              setShowPace={setShowPace}
              onDepthChange={bumpDepthTick}
              houseSeed={houseSeed.allocs}
              onConfirm={({ allocs: confirmedAllocs, totalRush, totalTgt }) => {
                setAllocSummary({ allocs: confirmedAllocs, totalRush, totalTgt });
                setCurrentStep("3");
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── STEP 3: efficiency editor ────────────────────────────────────────────

  if (currentStep === "3") {
    return (
      <div style={wrapStyle}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          <EditorHeader abbrev={abbrev} teamColor={tc} onBack={onBack} saveFlash={saveFlash} onOpenRankings={onOpenRankings} onViewResults={effSummary ? () => handleStepNav("results") : null} />
          <StepIndicator activeIdx={3} onStepClick={handleStepNav} maxIdx={navMaxIdx} />
          <LockedStrip label="TOTAL PLAYS" onClick={() => handleStepNav("1a")} hoverable>
            <span style={{ fontSize: fontSize.heading, fontWeight: fontWeight.black, color: colors.text.primary, fontFamily: fonts.display }}>{totalPlays}</span>
            <span style={{ fontSize: fontSize.small, fontWeight: fontWeight.medium, ...dc(diff2025) }}>({ds(diff2025)} vs '25)</span>
          </LockedStrip>
          <LockedStrip label="PASS / RUN" onClick={() => handleStepNav("1b")} hoverable>
            <span style={{ fontSize: fontSize.heading, fontWeight: fontWeight.black, color: colors.pos.QB, fontFamily: fonts.display }}>{passAtt}</span>
            <span style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>pass ({Math.round(passPct)}%)</span>
            <span style={{ color: "#A0AEC0" }}>|</span>
            <span style={{ fontSize: fontSize.heading, fontWeight: fontWeight.black, color: colors.pos.RB, fontFamily: fonts.display }}>{rushAtt}</span>
            <span style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>rush ({Math.round(100 - passPct)}%)</span>
          </LockedStrip>
          <LockedStrip label="ALLOCATION" onClick={() => handleStepNav("2")} hoverable>
            <span style={{ fontSize: fontSize.body, color: colors.text.secondary, fontFamily: fonts.display }}>
              Rush: <strong style={{ color: "#60a5fa" }}>{Math.round(allocSummary?.totalRush ?? 0)}%</strong>
              {" | "}
              Tgt: <strong style={{ color: colors.brand.accent }}>{Math.round(allocSummary?.totalTgt ?? 0)}%</strong>
            </span>
          </LockedStrip>
          <div style={{ background: colors.bg.raised, borderRadius: 14, border: `1px solid ${colors.border.subtle}`, padding: 18 }}>
            <EfficiencyEditor
              ref={effRef}
              abbrev={abbrev}
              teamColor={tc}
              passAtt={passAtt}
              rushAtt={rushAtt}
              totalPlays={totalPlays}
              passPct={passPct}
              allocs={allocSummary?.allocs}
              teamStarters={teamStarters}
              initialRates={effSummary?.rates}
              logChange={logChange}
              showPace={showPace}
              setShowPace={setShowPace}
              onDepthChange={bumpDepthTick}
              scoring={scoring}
              houseSeed={houseSeed.rates}
              onConfirm={({ teamTotal, players, rates: confirmedRates }) => {
                setEffSummary({ teamTotal, players, rates: confirmedRates });
                setCurrentStep("results");
                // Completion event — fired once here at the FINALIZE transition
                // (never on re-view via "Final projection →" or on a resumed
                // save). Total/rank derived the same way the results page shows.
                const doneTotal = players.reduce((s, p) => s + (p.stats ? scoreFromStats(p.stats, p.pos, scoring, p.games ?? 17) : (p.pts || 0)), 0);
                captureProjlabTeamCompleted({
                  team: abbrev,
                  teamTotal: Math.round(doneTotal * 10) / 10,
                  projRank: TEAM_PPR_2025.filter(t => t.pts > doneTotal).length + 1,
                });
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── RESULTS: full projection output ─────────────────────────────────────

  if (currentStep === "results") {
    return (
      <ResultsView
        abbrev={abbrev}
        resumedFromSave={!!savedInit}
        teamStarters={teamStarters}
        teamColor={tc}
        totalPlays={totalPlays}
        passAtt={passAtt}
        rushAtt={rushAtt}
        passPct={passPct}
        players={effSummary?.players || []}
        teamTotal={effSummary?.teamTotal || 0}
        allocSummary={allocSummary}
        changelog={changelog}
        onChangelogUpdate={setChangelog}
        onBack={onBack}
        onEditStep={(step) => setCurrentStep(step)}
        onOpenRankings={onOpenRankings}
        onProjectTeam={onProjectTeam}
        progress={progress}
        scoring={scoring}
      />
    );
  }

  // ── DONE: all steps collapsed ────────────────────────────────────────────

  if (currentStep === "done") {
    // Derive-on-read: never trust the stored teamTotal (stale under a lens).
    const doneTeamTotal = Array.isArray(effSummary?.players)
      ? effSummary.players.reduce((s, p) => s + (p.stats ? scoreFromStats(p.stats, p.pos, scoring, p.games ?? 17) : (p.pts || 0)), 0)
      : (effSummary?.teamTotal ?? 0);
    return (
      <div style={wrapStyle}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <EditorHeader abbrev={abbrev} teamColor={tc} onBack={onBack} saveFlash={saveFlash} onOpenRankings={onOpenRankings} onViewResults={effSummary ? () => handleStepNav("results") : null} />
          <StepIndicator activeIdx={4} onStepClick={handleStepNav} maxIdx={navMaxIdx} />
          <LockedStrip label="TOTAL PLAYS" onClick={() => setCurrentStep("1a")} hoverable>
            <span style={{ fontSize: fontSize.heading, fontWeight: fontWeight.black, color: colors.text.primary, fontFamily: fonts.display }}>{totalPlays}</span>
            <span style={{ fontSize: fontSize.small, fontWeight: fontWeight.medium, ...dc(diff2025) }}>({ds(diff2025)} vs '25)</span>
          </LockedStrip>
          <LockedStrip label="PASS / RUN" onClick={() => setCurrentStep("1b")} hoverable>
            <span style={{ fontSize: fontSize.heading, fontWeight: fontWeight.black, color: colors.pos.QB, fontFamily: fonts.display }}>{passAtt}</span>
            <span style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>pass ({Math.round(passPct)}%)</span>
            <span style={{ color: "#A0AEC0" }}>|</span>
            <span style={{ fontSize: fontSize.heading, fontWeight: fontWeight.black, color: colors.pos.RB, fontFamily: fonts.display }}>{rushAtt}</span>
            <span style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>rush ({Math.round(100 - passPct)}%)</span>
          </LockedStrip>
          <LockedStrip label="ALLOCATION" onClick={() => setCurrentStep("2")} hoverable>
            <span style={{ fontSize: fontSize.body, color: colors.text.secondary, fontFamily: fonts.display }}>
              Rush: <strong style={{ color: "#60a5fa" }}>{Math.round(allocSummary?.totalRush ?? 0)}%</strong>
              {" | "}
              Tgt: <strong style={{ color: colors.brand.accent }}>{Math.round(allocSummary?.totalTgt ?? 0)}%</strong>
            </span>
          </LockedStrip>
          <LockedStrip label="EFFICIENCY" onClick={() => setCurrentStep("3")} hoverable>
            <span style={{ fontSize: fontSize.heading, fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display }}>{doneTeamTotal.toFixed(0)}</span>
            <span style={{ fontSize: fontSize.small, color: "#A0AEC0" }}>{pprLabel(scoring)} pts</span>
          </LockedStrip>
          <div style={{ background: colors.bg.raised, borderRadius: 14, border: `1px solid ${colors.border.subtle}`, padding: 24, textAlign: "center" }}>
            <div style={{ fontSize: fontSize.micro, fontWeight: fontWeight.bold, color: "#4ade80", letterSpacing: "2px", fontFamily: fonts.display, marginBottom: 6 }}>
              PROJECTION COMPLETE
            </div>
            <h2 style={{ fontFamily: fonts.display, fontSize: fontSize.heading, fontWeight: fontWeight.heavy, margin: "0 0 8px", color: colors.text.primary }}>
              {doneTeamTotal.toFixed(0)} {pprLabel(scoring)} points
            </h2>
            <p style={{ fontSize: fontSize.small, color: "#A0AEC0", margin: 0 }}>Fantasy scoring projection across all starters.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── STEP 1B: pass/run split editor ───────────────────────────────────────

  if (currentStep === "1b") {
    return (
      <div style={wrapStyle}>
        <div style={{ maxWidth: 1250, margin: "0 auto" }}>
          <EditorHeader abbrev={abbrev} teamColor={tc} onBack={onBack} saveFlash={saveFlash} onOpenRankings={onOpenRankings} onViewResults={effSummary ? () => handleStepNav("results") : null} />
          <StepIndicator activeIdx={1} onStepClick={handleStepNav} maxIdx={navMaxIdx} />

          {/* Collapsed 1A strip (read-only while editing 1B) */}
          <LockedStrip label="TOTAL PLAYS" hoverable={false}>
            <span style={{ fontSize: fontSize.title, fontWeight: fontWeight.black, color: colors.text.primary, fontFamily: fonts.display }}>{totalPlays}</span>
            <span style={{ fontSize: fontSize.small, fontWeight: fontWeight.medium, ...dc(diff2025) }}>({ds(diff2025)} vs '25)</span>
          </LockedStrip>

          {/* 1B editor card */}
          <div style={{ background: colors.bg.raised, borderRadius: 14, border: `1px solid ${colors.border.subtle}`, padding: 18 }}>
            {/* Prompt */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: fontSize.body, fontWeight: fontWeight.bold, color: colors.brand.accent, letterSpacing: "2px", fontFamily: fonts.display, marginBottom: 4 }}>
                STEP 1B — PASS / RUN SPLIT
              </div>
              <h2 style={{ fontFamily: fonts.display, fontSize: fontSize.title, fontWeight: fontWeight.heavy, margin: "0 0 4px", color: colors.text.primary }}>
                How will {meta.fullName}'s {totalPlays} plays split between pass and run?
              </h2>
              <p style={{ fontSize: fontSize.sub, color: "#A0AEC0", margin: 0 }}>
                Review the 5-year trend, league context, and coaching tendencies. Set your split below.
              </p>
            </div>

            {/* Tab pills */}
            <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
              {[
                { key: "team", label: "Team", color: "#22d3ee" },
                { key: "hc", label: "HC History", color: "#a78bfa" },
                { key: "oc", label: "OC History", color: "#f59e0b" },
              ].map(t => {
                const active = tab1B === t.key;
                if (t.key === "team") {
                  const otherTeams = Object.keys(TEAM_META).filter(k => k !== abbrev)
                    .sort((a, b) => TEAM_META[a].fullName.localeCompare(TEAM_META[b].fullName));
                  return (
                    <select key={t.key}
                      value={teamView ?? abbrev}
                      onMouseDown={() => setTab1B("team")}
                      onChange={e => { const v = e.target.value; setTab1B("team"); setTeamView(v === abbrev ? null : v); }}
                      style={{
                        padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${active ? t.color : colors.border.subtle}`,
                        background: active ? `${t.color}18` : "transparent", color: active ? t.color : "#A0AEC0",
                        fontSize: fontSize.small, fontWeight: fontWeight.bold, fontFamily: fonts.display, letterSpacing: "1px", cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}>
                      <option value={abbrev}>{meta.fullName} (your team)</option>
                      {otherTeams.map(k => <option key={k} value={k}>{TEAM_META[k].fullName}</option>)}
                    </select>
                  );
                }
                return (
                  <button key={t.key} onClick={() => setTab1B(t.key)} style={{
                    padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${active ? t.color : colors.border.subtle}`,
                    background: active ? `${t.color}18` : "transparent", color: active ? t.color : "#A0AEC0",
                    fontSize: fontSize.small, fontWeight: fontWeight.bold, fontFamily: fonts.display, letterSpacing: "1px", cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}>{t.label}</button>
                );
              })}
            </div>

            {/* Team tab */}
            {tab1B === "team" && (<>
              {/* Stacked pass/rush chart */}
              <SplitChart
                mergedHistory={teamViewMerged}
                projPassAtt={passAtt}
                projRushAtt={rushAtt}
                onSplitChange={setPassPct}
              />
              {isOwnTeam && seedVolume && (
                <div style={{ fontSize: fontSize.caption, color: colors.text.faint, marginTop: 6 }}>
                  seed: {seedVolume.passPct.coachChanged
                    ? `new HC/OC → ${Math.round(seedVolume.passPct.personalWeight * 100)}% own rate`
                    : `staff stable → ${Math.round(seedVolume.passPct.personalWeight * 100)}% own rate`} + league mean
                </div>
              )}

              {/* Historical table */}
              <div style={{ marginTop: 12, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fontSize.small }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${colors.border.default}` }}>
                      {["Year", "Pass Att", "Rush Att", "Pass %", "Rush %", "Head Coach", "Off. Coordinator"].map(h => (
                        <th key={h} style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {isOwnTeam && (
                    <tr style={{ background: "rgba(255,107,0,0.08)", borderLeft: `3px solid ${colors.brand.accent}`, borderBottom: `1px solid rgba(255,107,0,0.2)` }}>
                      <td style={{ padding: "6px 5px", fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display, textAlign: "center" }}>2026</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.QB, fontFamily: fonts.display }}>{passAtt}</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.RB, fontFamily: fonts.display }}>{rushAtt}</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.QB }}>{Math.round(passAtt / totalPlays * 100)}%</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.RB }}>{Math.round(rushAtt / totalPlays * 100)}%</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.caption }}>
                        {hcName !== "—" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ width: 6, height: 6, borderRadius: 2, background: hcColors[hcName] ?? colors.brand.accent, flexShrink: 0 }} /><span style={{ color: hcColors[hcName] ?? colors.brand.accent, fontWeight: fontWeight.medium }}>{hcName}</span></span> : "—"}
                      </td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.caption }}>
                        {ocName !== "—" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ width: 6, height: 6, borderRadius: 2, background: ocColors[ocName] ?? colors.brand.accent, flexShrink: 0 }} /><span style={{ color: ocColors[ocName] ?? colors.brand.accent, fontWeight: fontWeight.bold }}>{ocName}</span></span> : "—"}
                      </td>
                    </tr>
                    )}
                    {teamViewMerged.slice().reverse().map((h, i) => {
                      const pp = Math.round(h.pa / h.total * 100);
                      const rp = Math.round(h.ra / h.total * 100);
                      const hcColor = hcColors[h.hc] ?? "#A0AEC0";
                      const ocColor = ocColors[h.oc] ?? "#A0AEC0";
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${colors.border.subtle}` }}>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, fontFamily: fonts.display, color: colors.text.primary }}>{h.year}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.QB, fontFamily: fonts.display }}>{h.pa}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.RB, fontFamily: fonts.display }}>{h.ra}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.QB }}>{pp}%</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.RB }}>{rp}%</td>
                          <td style={{ padding: "6px 5px", textAlign: "center" }}>
                            {h.hc !== "—" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ width: 6, height: 6, borderRadius: 2, background: hcColor, flexShrink: 0 }} /><span style={{ color: hcColor, fontWeight: fontWeight.medium, fontSize: fontSize.caption }}>{h.hc}</span></span> : "—"}
                          </td>
                          <td style={{ padding: "6px 5px", textAlign: "center" }}>
                            {h.oc !== "—" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><span style={{ width: 6, height: 6, borderRadius: 2, background: ocColor, flexShrink: 0 }} /><span style={{ color: ocColor, fontWeight: fontWeight.bold, fontSize: fontSize.caption }}>{h.oc}</span></span> : "—"}
                          </td>
                        </tr>
                      );
                    })}
                    {(() => {
                      const aPA = avgArr(teamViewMerged.map(h => h.pa));
                      const aRA = avgArr(teamViewMerged.map(h => h.ra));
                      const aSum = aPA + aRA || 1;
                      return (
                        <tr style={{ background: "rgba(255,107,0,0.06)", borderTop: `2px solid ${colors.border.default}` }}>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display }}>AVG</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.QB, fontFamily: fonts.display }}>{aPA}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.RB, fontFamily: fonts.display }}>{aRA}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.QB }}>{Math.round(aPA / aSum * 100)}%</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.RB }}>{Math.round(aRA / aSum * 100)}%</td>
                          <td colSpan={2} />
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>

              {/* THE TREND narrative */}
              <div style={{ marginTop: 10, padding: "8px 12px", background: "rgba(255,255,255,0.02)", border: `1px solid ${colors.border.subtle}`, borderRadius: 8 }}>
                <div style={{ fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC", letterSpacing: "1px", marginBottom: 3, fontFamily: fonts.display }}>THE TREND</div>
                <div style={{ fontSize: fontSize.sub, color: "#CBD5E0", lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: narrative1B }} />
              </div>
            </>)}

            {/* HC History tab */}
            {tab1B === "hc" && (<>
              <SplitChart
                mergedHistory={hcMerged}
                projPassAtt={passAtt}
                projRushAtt={rushAtt}
                onSplitChange={setPassPct}
              />
              <div style={{ marginTop: 12, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fontSize.small }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${colors.border.default}` }}>
                      {["Year", "Team", "Pass Att", "Rush Att", "Pass %", "Rush %", "Head Coach", "Off. Coordinator"].map(h => (
                        <th key={h} style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ background: "rgba(255,107,0,0.08)", borderLeft: `3px solid ${colors.brand.accent}`, borderBottom: `1px solid rgba(255,107,0,0.2)` }}>
                      <td style={{ padding: "6px 5px", fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display, textAlign: "center" }}>2026</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.medium, color: "#a78bfa" }}>{abbrev}</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.QB, fontFamily: fonts.display }}>{passAtt}</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.RB, fontFamily: fonts.display }}>{rushAtt}</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.QB }}>{Math.round(passAtt / totalPlays * 100)}%</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.RB }}>{Math.round(rushAtt / totalPlays * 100)}%</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.caption }}>
                        {hcName !== "—" ? <span style={{ color: "#a78bfa", fontWeight: fontWeight.medium }}>{hcName}</span> : "—"}
                      </td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.caption }}>
                        {ocName !== "—" ? <span style={{ color: "#A0AEC0", fontWeight: fontWeight.medium }}>{ocName}</span> : "—"}
                      </td>
                    </tr>
                    {hcMerged.slice().reverse().map((h, i) => {
                      const pp = h.total > 0 ? Math.round(h.pa / h.total * 100) : 0;
                      const rp = 100 - pp;
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${colors.border.subtle}` }}>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, fontFamily: fonts.display, color: colors.text.primary }}>{h.year}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.medium, color: "#a78bfa" }}>{h.team}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.QB, fontFamily: fonts.display }}>{h.pa}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.RB, fontFamily: fonts.display }}>{h.ra}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.QB }}>{pp}%</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.RB }}>{rp}%</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.caption, color: "#A0AEC0" }}>{h.hc}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.caption, color: "#A0AEC0" }}>{h.oc}</td>
                        </tr>
                      );
                    })}
                    {hcMerged.length > 0 && (() => {
                      const aPA = avgArr(hcMerged.map(h => h.pa));
                      const aRA = avgArr(hcMerged.map(h => h.ra));
                      const aSum = aPA + aRA || 1;
                      return (
                        <tr style={{ background: "rgba(167,139,250,0.06)", borderTop: `2px solid ${colors.border.default}` }}>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.black, color: "#a78bfa", fontFamily: fonts.display }}>AVG</td>
                          <td style={{ padding: "6px 5px" }} />
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.QB, fontFamily: fonts.display }}>{aPA}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.RB, fontFamily: fonts.display }}>{aRA}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.QB }}>{Math.round(aPA / aSum * 100)}%</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.RB }}>{Math.round(aRA / aSum * 100)}%</td>
                          <td colSpan={2} />
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </>)}

            {/* OC History tab */}
            {tab1B === "oc" && (<>
              <SplitChart
                mergedHistory={ocMerged}
                projPassAtt={passAtt}
                projRushAtt={rushAtt}
                onSplitChange={setPassPct}
              />
              <div style={{ marginTop: 12, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fontSize.small }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${colors.border.default}` }}>
                      {["Year", "Team", "Pass Att", "Rush Att", "Pass %", "Rush %", "Head Coach", "Off. Coordinator"].map(h => (
                        <th key={h} style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ background: "rgba(255,107,0,0.08)", borderLeft: `3px solid ${colors.brand.accent}`, borderBottom: `1px solid rgba(255,107,0,0.2)` }}>
                      <td style={{ padding: "6px 5px", fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display, textAlign: "center" }}>2026</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.medium, color: "#f59e0b" }}>{abbrev}</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.QB, fontFamily: fonts.display }}>{passAtt}</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.RB, fontFamily: fonts.display }}>{rushAtt}</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.QB }}>{Math.round(passAtt / totalPlays * 100)}%</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.RB }}>{Math.round(rushAtt / totalPlays * 100)}%</td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.caption }}>
                        {hcName !== "—" ? <span style={{ color: "#A0AEC0", fontWeight: fontWeight.medium }}>{hcName}</span> : "—"}
                      </td>
                      <td style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.caption }}>
                        {ocName !== "—" ? <span style={{ color: "#f59e0b", fontWeight: fontWeight.medium }}>{ocName}</span> : "—"}
                      </td>
                    </tr>
                    {ocMerged.slice().reverse().map((h, i) => {
                      const pp = h.total > 0 ? Math.round(h.pa / h.total * 100) : 0;
                      const rp = 100 - pp;
                      return (
                        <tr key={i} style={{ borderBottom: `1px solid ${colors.border.subtle}` }}>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, fontFamily: fonts.display, color: colors.text.primary }}>{h.year}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.medium, color: "#f59e0b" }}>{h.team}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.QB, fontFamily: fonts.display }}>{h.pa}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.RB, fontFamily: fonts.display }}>{h.ra}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.QB }}>{pp}%</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.RB }}>{rp}%</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.caption, color: "#A0AEC0" }}>{h.hc}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontSize: fontSize.caption, color: "#A0AEC0" }}>{h.oc}</td>
                        </tr>
                      );
                    })}
                    {ocMerged.length > 0 && (() => {
                      const aPA = avgArr(ocMerged.map(h => h.pa));
                      const aRA = avgArr(ocMerged.map(h => h.ra));
                      const aSum = aPA + aRA || 1;
                      return (
                        <tr style={{ background: "rgba(245,158,11,0.06)", borderTop: `2px solid ${colors.border.default}` }}>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.black, color: "#f59e0b", fontFamily: fonts.display }}>AVG</td>
                          <td style={{ padding: "6px 5px" }} />
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.QB, fontFamily: fonts.display }}>{aPA}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.RB, fontFamily: fonts.display }}>{aRA}</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.QB }}>{Math.round(aPA / aSum * 100)}%</td>
                          <td style={{ padding: "6px 5px", textAlign: "center", color: colors.pos.RB }}>{Math.round(aRA / aSum * 100)}%</td>
                          <td colSpan={2} />
                        </tr>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </>)}

            {/* Input area */}
            <div style={{ marginTop: 14, padding: "14px 16px", background: "rgba(255,107,0,0.04)", border: "1px solid rgba(255,107,0,0.12)", borderRadius: 12 }}>
              <RatioSlider passPct={passPct} onChange={setPassPct} />

              {/* Linked number inputs */}
              <div style={{ display: "flex", gap: 12, marginTop: 10, marginBottom: 12 }}>
                <div style={{ flex: 1, padding: "10px 12px", background: `${colors.pos.QB}08`, border: `1px solid ${colors.pos.QB}20`, borderRadius: 10, textAlign: "center" }}>
                  <div style={{ fontSize: fontSize.caption, fontWeight: fontWeight.bold, color: colors.pos.QB, letterSpacing: "1px", marginBottom: 4 }}>PASS ATTEMPTS</div>
                  <input
                    type="number" value={passAtt}
                    onChange={e => {
                      const v = Math.max(0, Math.min(totalPlays, Number(e.target.value) || 0));
                      setPassPct(v / totalPlays * 100);
                    }}
                    style={{ width: 80, background: colors.bg.elevated, border: `1px solid ${colors.pos.QB}40`, borderRadius: 6, color: colors.pos.QB, fontSize: fontSize.title, fontWeight: fontWeight.black, textAlign: "center", padding: "6px", fontFamily: fonts.display, outline: "none" }}
                  />
                  <div style={{ fontSize: fontSize.small, color: "#A0AEC0", marginTop: 4 }}>
                    vs '25: <strong style={dc(passAtt - lastPA)}>{ds(passAtt - lastPA)}</strong>
                    &nbsp;|&nbsp;5yr avg: {avgPA}&nbsp;|&nbsp;lg: {lgPA}
                  </div>
                </div>
                <div style={{ flex: 1, padding: "10px 12px", background: `${colors.pos.RB}08`, border: `1px solid ${colors.pos.RB}20`, borderRadius: 10, textAlign: "center" }}>
                  <div style={{ fontSize: fontSize.caption, fontWeight: fontWeight.bold, color: colors.pos.RB, letterSpacing: "1px", marginBottom: 4 }}>RUSH ATTEMPTS</div>
                  <input
                    type="number" value={rushAtt}
                    onChange={e => {
                      const v = Math.max(0, Math.min(totalPlays, Number(e.target.value) || 0));
                      setPassPct((totalPlays - v) / totalPlays * 100);
                    }}
                    style={{ width: 80, background: colors.bg.elevated, border: `1px solid ${colors.pos.RB}40`, borderRadius: 6, color: colors.pos.RB, fontSize: fontSize.title, fontWeight: fontWeight.black, textAlign: "center", padding: "6px", fontFamily: fonts.display, outline: "none" }}
                  />
                  <div style={{ fontSize: fontSize.small, color: "#A0AEC0", marginTop: 4 }}>
                    vs '25: <strong style={dc(rushAtt - lastRA)}>{ds(rushAtt - lastRA)}</strong>
                    &nbsp;|&nbsp;5yr avg: {avgRA}&nbsp;|&nbsp;lg: {lgRA}
                  </div>
                </div>
              </div>

              {/* Sanity check */}
              <div style={{ textAlign: "center", fontSize: fontSize.small, color: "#A0AEC0", marginBottom: 10 }}>
                <span style={{ color: colors.pos.QB, fontWeight: fontWeight.bold }}>{passAtt}</span>
                {" + "}
                <span style={{ color: colors.pos.RB, fontWeight: fontWeight.bold }}>{rushAtt}</span>
                {" = "}
                <span style={{ color: tc, fontWeight: fontWeight.black }}>{totalPlays}</span>
                {" total plays ✓"}
              </div>

              <button
                onClick={() => setCurrentStep("2")}
                style={{ width: "100%", padding: "14px 0", background: "linear-gradient(135deg, #FF6B00, #E55A00)", border: "none", borderRadius: 10, color: "#fff", fontFamily: fonts.display, fontSize: fontSize.base, fontWeight: fontWeight.heavy, letterSpacing: "1.5px", cursor: "pointer", boxShadow: "0 4px 16px rgba(255,107,0,0.3)" }}
              >
                CONFIRM {passAtt} PASS / {rushAtt} RUSH → CONTINUE TO ALLOCATION
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── STEP 1A: total plays editor ───────────────────────────────────────────

  return (
    <div style={wrapStyle}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <EditorHeader abbrev={abbrev} teamColor={tc} onBack={onBack} saveFlash={saveFlash} onOpenRankings={onOpenRankings} onViewResults={effSummary ? () => handleStepNav("results") : null} />
        <StepIndicator activeIdx={0} onStepClick={handleStepNav} maxIdx={navMaxIdx} />

        <div style={{ background: colors.bg.raised, borderRadius: 14, border: `1px solid ${colors.border.subtle}`, padding: 18 }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: fontSize.body, fontWeight: fontWeight.bold, color: colors.brand.accent, letterSpacing: "2px", fontFamily: fonts.display, marginBottom: 4 }}>
              STEP 1A — TOTAL OFFENSIVE PLAYS
            </div>
            <h2 style={{ fontFamily: fonts.display, fontSize: fontSize.title, fontWeight: fontWeight.heavy, margin: "0 0 4px", color: colors.text.primary }}>
              How many total offensive plays will {meta.fullName} run in 2026?
            </h2>
            <p style={{ fontSize: fontSize.sub, color: "#A0AEC0", margin: 0 }}>
              Review the 5-year trend, league context, and coaching changes. Then set your projection below.
            </p>
          </div>

          {/* Tab pills */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {[
              { key: "team", label: "Team", color: "#22d3ee" },
              { key: "hc", label: "HC History", color: "#a78bfa" },
              { key: "oc", label: "OC History", color: "#f59e0b" },
            ].map(t => {
              const active = tab1A === t.key;
              if (t.key === "team") {
                const otherTeams = Object.keys(TEAM_META).filter(k => k !== abbrev)
                  .sort((a, b) => TEAM_META[a].fullName.localeCompare(TEAM_META[b].fullName));
                return (
                  <select key={t.key}
                    value={teamView ?? abbrev}
                    onMouseDown={() => setTab1A("team")}
                    onChange={e => { const v = e.target.value; setTab1A("team"); setTeamView(v === abbrev ? null : v); }}
                    style={{
                      padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${active ? t.color : colors.border.subtle}`,
                      background: active ? `${t.color}18` : "transparent", color: active ? t.color : "#A0AEC0",
                      fontSize: fontSize.small, fontWeight: fontWeight.bold, fontFamily: fonts.display, letterSpacing: "1px", cursor: "pointer",
                      transition: "all 0.15s ease",
                    }}>
                    <option value={abbrev}>{meta.fullName} (your team)</option>
                    {otherTeams.map(k => <option key={k} value={k}>{TEAM_META[k].fullName}</option>)}
                  </select>
                );
              }
              return (
                <button key={t.key} onClick={() => setTab1A(t.key)} style={{
                  padding: "6px 14px", borderRadius: 20, border: `1.5px solid ${active ? t.color : colors.border.subtle}`,
                  background: active ? `${t.color}18` : "transparent", color: active ? t.color : "#A0AEC0",
                  fontSize: fontSize.small, fontWeight: fontWeight.bold, fontFamily: fonts.display, letterSpacing: "1px", cursor: "pointer",
                  transition: "all 0.15s ease",
                }}>{t.label}</button>
              );
            })}
          </div>

          {/* Team tab */}
          {tab1A === "team" && (<>
            <TotalPlaysChart
              mergedHistory={teamViewMerged}
              projValue={totalPlays}
              teamColor={teamViewColor}
              onProjChange={setTotalPlays}
              abbrev={teamView ?? abbrev}
            />
            {isOwnTeam && seedVolume && (
              <div style={{ fontSize: fontSize.caption, color: colors.text.faint, marginTop: 6 }}>
                seed: 60% {abbrev} 3-yr pace ({Math.round(seedVolume.plays.ownPG * 17)} plays) + 40% league
              </div>
            )}

            {/* Historical table */}
            <div style={{ marginTop: 14, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fontSize.body }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${colors.border.default}` }}>
                    {["Year", "Pass Att", "Rush Att", "Total", "Pass %", "+/− Lg", "Head Coach", "Off. Coordinator"].map(h => (
                      <th key={h} style={{ padding: "7px 6px", textAlign: (h.includes("Coach") || h.includes("Coord")) ? "left" : "center", fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC", letterSpacing: "0.5px" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {isOwnTeam && (
                  <tr style={{ background: "rgba(255,107,0,0.08)", borderLeft: `3px solid ${colors.brand.accent}`, borderBottom: `1px solid rgba(255,107,0,0.2)` }}>
                    <td style={{ padding: "8px 6px", fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display, textAlign: "center" }}>2026</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", color: colors.pos.QB, fontWeight: fontWeight.bold }}>{passAtt}</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", color: colors.pos.RB, fontWeight: fontWeight.bold }}>{rushAtt}</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display, fontSize: fontSize.sub }}>{totalPlays}</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", color: "#E2E8F0" }}>{Math.round(passAtt / totalPlays * 100)}%</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: (totalPlays - lgAvg1A) >= 0 ? "#4ade80" : "#f87171" }}>{ds(totalPlays - lgAvg1A)}</td>
                    <td style={{ padding: "8px 6px", fontSize: fontSize.small }}>
                      {hcName !== "—" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: 2, background: hcColors[hcName] ?? colors.brand.accent, flexShrink: 0 }} /><span style={{ color: hcColors[hcName] ?? colors.brand.accent, fontWeight: fontWeight.medium }}>{hcName}</span></span> : "—"}
                    </td>
                    <td style={{ padding: "8px 6px", fontSize: fontSize.small }}>
                      {ocName !== "—" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: 2, background: ocColors[ocName] ?? colors.brand.accent, flexShrink: 0 }} /><span style={{ color: ocColors[ocName] ?? colors.brand.accent, fontWeight: fontWeight.bold }}>{ocName}</span></span> : "—"}
                    </td>
                  </tr>
                  )}
                  {teamViewMerged.slice().reverse().map((h, i) => {
                    const lg      = LEAGUE_STATS[h.year];
                    const diff    = lg ? h.total - lg.avg : null;
                    const hcColor = hcColors[h.hc] ?? "#A0AEC0";
                    const ocColor = ocColors[h.oc] ?? "#A0AEC0";
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${colors.border.subtle}` }}>
                        <td style={{ padding: "8px 6px", fontWeight: fontWeight.bold, color: colors.text.primary, fontFamily: fonts.display, textAlign: "center" }}>{h.year}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", color: colors.pos.QB, fontWeight: fontWeight.medium }}>{h.pa}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", color: colors.pos.RB, fontWeight: fontWeight.medium }}>{h.ra}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.heavy, color: tc, fontFamily: fonts.display, fontSize: fontSize.sub }}>{h.total}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", color: "#E2E8F0" }}>{Math.round(h.pa / h.total * 100)}%</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: diff != null ? (diff >= 0 ? "#4ade80" : "#f87171") : "#A0AEC0" }}>
                          {diff != null ? ds(diff) : "—"}
                        </td>
                        <td style={{ padding: "8px 6px", fontSize: fontSize.small }}>
                          {h.hc !== "—" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: 2, background: hcColor, flexShrink: 0 }} /><span style={{ color: hcColor, fontWeight: fontWeight.medium }}>{h.hc}</span></span> : "—"}
                        </td>
                        <td style={{ padding: "8px 6px", fontSize: fontSize.small }}>
                          {h.oc !== "—" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: 2, background: ocColor, flexShrink: 0 }} /><span style={{ color: ocColor, fontWeight: fontWeight.bold }}>{h.oc}</span></span> : "—"}
                        </td>
                      </tr>
                    );
                  })}
                  {(() => {
                    const avgPA = avgArr(teamViewMerged.map(h => h.pa));
                    const avgRA = avgArr(teamViewMerged.map(h => h.ra));
                    const avgTotal = avgArr(teamViewMerged.map(h => h.total));
                    return (
                      <tr style={{ background: "rgba(255,107,0,0.06)", borderTop: `2px solid ${colors.border.default}` }}>
                        <td style={{ padding: "8px 6px", fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display, textAlign: "center" }}>AVG</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.QB }}>{avgPA}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.RB }}>{avgRA}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.black, color: tc, fontFamily: fonts.display, fontSize: fontSize.sub }}>{avgTotal}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: "#E2E8F0" }}>
                          {avgTotal > 0 ? Math.round(avgPA / avgTotal * 100) : 0}%
                        </td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: avgTotal - lgAvg1A >= 0 ? "#4ade80" : "#f87171" }}>
                          {ds(avgTotal - lgAvg1A)}
                        </td>
                        <td colSpan={2} />
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </>)}

          {/* HC History tab */}
          {tab1A === "hc" && (<>
            <TotalPlaysChart
              mergedHistory={hcMerged} projValue={totalPlays}
              teamColor="#a78bfa" abbrev={hcName}
              onProjChange={setTotalPlays}
              title={`${hcName} — HC CAREER TOTAL PLAYS`}
            />

            {/* Historical table */}
            <div style={{ marginTop: 14, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fontSize.body }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${colors.border.default}` }}>
                    {["Year", "Team", "Pass Att", "Rush Att", "Total", "Pass %", "+/− Lg", "Head Coach", "Off. Coordinator"].map(h => (
                      <th key={h} style={{ padding: "7px 6px", textAlign: (h.includes("Coach") || h.includes("Coord")) ? "left" : "center", fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC", letterSpacing: "0.5px" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: "rgba(255,107,0,0.08)", borderLeft: `3px solid ${colors.brand.accent}`, borderBottom: `1px solid rgba(255,107,0,0.2)` }}>
                    <td style={{ padding: "8px 6px", fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display, textAlign: "center" }}>2026</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.medium, color: "#a78bfa" }}>{abbrev}</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", color: colors.pos.QB, fontWeight: fontWeight.bold }}>{passAtt}</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", color: colors.pos.RB, fontWeight: fontWeight.bold }}>{rushAtt}</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display, fontSize: fontSize.sub }}>{totalPlays}</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", color: "#E2E8F0" }}>{Math.round(passAtt / totalPlays * 100)}%</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: (totalPlays - lgAvg1A) >= 0 ? "#4ade80" : "#f87171" }}>{ds(totalPlays - lgAvg1A)}</td>
                    <td style={{ padding: "8px 6px", fontSize: fontSize.small }}>
                      {hcName !== "—" ? <span style={{ color: "#a78bfa", fontWeight: fontWeight.medium }}>{hcName}</span> : "—"}
                    </td>
                    <td style={{ padding: "8px 6px", fontSize: fontSize.small }}>
                      {ocName !== "—" ? <span style={{ color: "#A0AEC0", fontWeight: fontWeight.medium }}>{ocName}</span> : "—"}
                    </td>
                  </tr>
                  {hcMerged.slice().reverse().map((h, i) => {
                    const lg   = LEAGUE_STATS[h.year];
                    const diff = lg ? h.total - lg.avg : null;
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${colors.border.subtle}` }}>
                        <td style={{ padding: "8px 6px", fontWeight: fontWeight.bold, color: colors.text.primary, fontFamily: fonts.display, textAlign: "center" }}>{h.year}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.medium, color: "#a78bfa" }}>{h.team}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", color: colors.pos.QB, fontWeight: fontWeight.medium }}>{h.pa}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", color: colors.pos.RB, fontWeight: fontWeight.medium }}>{h.ra}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.heavy, color: "#a78bfa", fontFamily: fonts.display, fontSize: fontSize.sub }}>{h.total}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", color: "#E2E8F0" }}>{h.total > 0 ? Math.round(h.pa / h.total * 100) : 0}%</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: diff != null ? (diff >= 0 ? "#4ade80" : "#f87171") : "#A0AEC0" }}>
                          {diff != null ? ds(diff) : "—"}
                        </td>
                        <td style={{ padding: "8px 6px", fontSize: fontSize.small, color: "#A0AEC0" }}>{h.hc}</td>
                        <td style={{ padding: "8px 6px", fontSize: fontSize.small, color: "#A0AEC0" }}>{h.oc}</td>
                      </tr>
                    );
                  })}
                  {hcMerged.length > 0 && (() => {
                    const avgPA = avgArr(hcMerged.map(h => h.pa));
                    const avgRA = avgArr(hcMerged.map(h => h.ra));
                    const avgTotal = avgArr(hcMerged.map(h => h.total));
                    return (
                      <tr style={{ background: "rgba(167,139,250,0.06)", borderTop: `2px solid ${colors.border.default}` }}>
                        <td style={{ padding: "8px 6px", fontWeight: fontWeight.black, color: "#a78bfa", fontFamily: fonts.display, textAlign: "center" }}>AVG</td>
                        <td style={{ padding: "8px 6px" }} />
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.QB }}>{avgPA}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.RB }}>{avgRA}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.black, color: "#a78bfa", fontFamily: fonts.display, fontSize: fontSize.sub }}>{avgTotal}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: "#E2E8F0" }}>
                          {avgTotal > 0 ? Math.round(avgPA / avgTotal * 100) : 0}%
                        </td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: avgTotal - lgAvg1A >= 0 ? "#4ade80" : "#f87171" }}>
                          {ds(avgTotal - lgAvg1A)}
                        </td>
                        <td colSpan={2} />
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </>)}

          {/* OC History tab */}
          {tab1A === "oc" && (<>
            <TotalPlaysChart
              mergedHistory={ocMerged} projValue={totalPlays}
              teamColor="#f59e0b" abbrev={ocName}
              onProjChange={setTotalPlays}
              title={`${ocName} — OC CAREER TOTAL PLAYS`}
            />

            {/* Historical table */}
            <div style={{ marginTop: 14, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fontSize.body }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${colors.border.default}` }}>
                    {["Year", "Team", "Pass Att", "Rush Att", "Total", "Pass %", "+/− Lg", "Head Coach", "Off. Coordinator"].map(h => (
                      <th key={h} style={{ padding: "7px 6px", textAlign: (h.includes("Coach") || h.includes("Coord")) ? "left" : "center", fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC", letterSpacing: "0.5px" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ background: "rgba(255,107,0,0.08)", borderLeft: `3px solid ${colors.brand.accent}`, borderBottom: `1px solid rgba(255,107,0,0.2)` }}>
                    <td style={{ padding: "8px 6px", fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display, textAlign: "center" }}>2026</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.medium, color: "#f59e0b" }}>{abbrev}</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", color: colors.pos.QB, fontWeight: fontWeight.bold }}>{passAtt}</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", color: colors.pos.RB, fontWeight: fontWeight.bold }}>{rushAtt}</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.black, color: colors.brand.accent, fontFamily: fonts.display, fontSize: fontSize.sub }}>{totalPlays}</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", color: "#E2E8F0" }}>{Math.round(passAtt / totalPlays * 100)}%</td>
                    <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: (totalPlays - lgAvg1A) >= 0 ? "#4ade80" : "#f87171" }}>{ds(totalPlays - lgAvg1A)}</td>
                    <td style={{ padding: "8px 6px", fontSize: fontSize.small }}>
                      {hcName !== "—" ? <span style={{ color: "#A0AEC0", fontWeight: fontWeight.medium }}>{hcName}</span> : "—"}
                    </td>
                    <td style={{ padding: "8px 6px", fontSize: fontSize.small }}>
                      {ocName !== "—" ? <span style={{ color: "#f59e0b", fontWeight: fontWeight.medium }}>{ocName}</span> : "—"}
                    </td>
                  </tr>
                  {ocMerged.slice().reverse().map((h, i) => {
                    const lg   = LEAGUE_STATS[h.year];
                    const diff = lg ? h.total - lg.avg : null;
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${colors.border.subtle}` }}>
                        <td style={{ padding: "8px 6px", fontWeight: fontWeight.bold, color: colors.text.primary, fontFamily: fonts.display, textAlign: "center" }}>{h.year}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.medium, color: "#f59e0b" }}>{h.team}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", color: colors.pos.QB, fontWeight: fontWeight.medium }}>{h.pa}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", color: colors.pos.RB, fontWeight: fontWeight.medium }}>{h.ra}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.heavy, color: "#f59e0b", fontFamily: fonts.display, fontSize: fontSize.sub }}>{h.total}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", color: "#E2E8F0" }}>{h.total > 0 ? Math.round(h.pa / h.total * 100) : 0}%</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: diff != null ? (diff >= 0 ? "#4ade80" : "#f87171") : "#A0AEC0" }}>
                          {diff != null ? ds(diff) : "—"}
                        </td>
                        <td style={{ padding: "8px 6px", fontSize: fontSize.small, color: "#A0AEC0" }}>{h.hc}</td>
                        <td style={{ padding: "8px 6px", fontSize: fontSize.small, color: "#A0AEC0" }}>{h.oc}</td>
                      </tr>
                    );
                  })}
                  {ocMerged.length > 0 && (() => {
                    const avgPA = avgArr(ocMerged.map(h => h.pa));
                    const avgRA = avgArr(ocMerged.map(h => h.ra));
                    const avgTotal = avgArr(ocMerged.map(h => h.total));
                    return (
                      <tr style={{ background: "rgba(245,158,11,0.06)", borderTop: `2px solid ${colors.border.default}` }}>
                        <td style={{ padding: "8px 6px", fontWeight: fontWeight.black, color: "#f59e0b", fontFamily: fonts.display, textAlign: "center" }}>AVG</td>
                        <td style={{ padding: "8px 6px" }} />
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.QB }}>{avgPA}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: colors.pos.RB }}>{avgRA}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.black, color: "#f59e0b", fontFamily: fonts.display, fontSize: fontSize.sub }}>{avgTotal}</td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: "#E2E8F0" }}>
                          {avgTotal > 0 ? Math.round(avgPA / avgTotal * 100) : 0}%
                        </td>
                        <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: fontWeight.bold, color: avgTotal - lgAvg1A >= 0 ? "#4ade80" : "#f87171" }}>
                          {ds(avgTotal - lgAvg1A)}
                        </td>
                        <td colSpan={2} />
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </>)}

          <CoachingPanels
            abbrev={abbrev} teamColor={tc}
            hcName={hcName} ocName={ocName}
            hcColors={hcColors} ocColors={ocColors}
            coachData={coach2026 || coach2025} ocCareer={ocCareer}
            isNewHC={isNewHC} isNewOC={isNewOC}
          />

          {/* THE QUESTION narrative */}
          <div style={{ marginTop: 12, padding: "10px 14px", background: "rgba(255,255,255,0.02)", border: `1px solid ${colors.border.subtle}`, borderRadius: 10 }}>
            <div style={{ fontSize: fontSize.small, fontWeight: fontWeight.bold, color: "#F7FAFC", letterSpacing: "1px", marginBottom: 4, fontFamily: fonts.display }}>THE QUESTION</div>
            <div style={{ fontSize: fontSize.sub, color: "#CBD5E0", lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: narrative1A }} />
          </div>

          {/* Slider + CONFIRM */}
          <div style={{ marginTop: 16, padding: "14px 16px", background: "rgba(255,107,0,0.04)", border: "1px solid rgba(255,107,0,0.12)", borderRadius: 12 }}>
            <SliderInput
              label="Your 2026 Projection" value={totalPlays} onChange={setTotalPlays}
              min={500} max={1550} color={colors.brand.accent}
              helper={`5yr avg: ${teamAvg1A} | League avg: ${lgAvg1A}`}
            />
            <div style={{ display: "flex", justifyContent: "center", gap: 20, padding: "6px 0 12px", fontSize: fontSize.body }}>
              <span style={{ color: "#A0AEC0" }}>vs '25: <strong style={dc(diff2025)}>{ds(diff2025)}</strong></span>
              <span style={{ color: "#A0AEC0" }}>vs 5yr avg: <strong style={dc(diffAvg1A)}>{ds(diffAvg1A)}</strong></span>
              <span style={{ color: "#A0AEC0" }}>vs league: <strong style={dc(diffLg1A)}>{ds(diffLg1A)}</strong></span>
            </div>
            <button
              onClick={() => setCurrentStep("1b")}
              style={{ width: "100%", padding: "14px 0", background: "linear-gradient(135deg, #FF6B00, #E55A00)", border: "none", borderRadius: 10, color: "#fff", fontFamily: fonts.display, fontSize: fontSize.base, fontWeight: fontWeight.heavy, letterSpacing: "1.5px", cursor: "pointer", boxShadow: "0 4px 16px rgba(255,107,0,0.3)" }}
            >
              CONFIRM {totalPlays} TOTAL PLAYS → CONTINUE TO PASS/RUN SPLIT
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
